import { FULL_PANORAMA_VIEWPORT, type PanoramaViewportInsets } from './viewers/shared/panoramaViewport';
import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense, lazy } from 'react';
// 🚀 HMR WORKING! Auto-refresh should happen now
// Lazy load heavy 3D viewers to prevent blocking UI on page load
const WorkingE57PanoramaViewer = lazy(() => import(/* webpackChunkName: "panorama-viewer" */ './components/WorkingE57PanoramaViewer'));
const WebIfcViewer = lazy(() => import(/* webpackChunkName: "ifc-viewer" */ './components/WebIfcViewer'));
const GEOTIFFViewer = lazy(() => import(/* webpackChunkName: "geotiff-viewer" */ './components/GEOTIFFViewer'));
const AutodeskViewer = lazy(() => import(/* webpackChunkName: "autodesk-viewer" */ './components/AutodeskViewer'));
const DiagramsPage = lazy(() => import(/* webpackChunkName: "diagrams-page" */ './components/DiagramsPage'));
const ServiceCallsPage = lazy(() => import(/* webpackChunkName: "service-calls-page" */ './components/ServiceCallsPage'));
const DevicesPage = lazy(() => import(/* webpackChunkName: "devices-page" */ './components/DevicesPage'));
const DashboardPage = lazy(() => import(/* webpackChunkName: "dashboard-page" */ './components/DashboardPage'));
const IntegrationsPage = lazy(() => import(/* webpackChunkName: "integrations-page" */ './components/IntegrationsPage'));
const CoverageDashboard = lazy(() => import(/* webpackChunkName: "coverage-page" */ './components/CoverageDashboard'));
const CreateServiceCallModal = lazy(() => import(/* webpackChunkName: "service-calls-page" */ './components/serviceCalls/CreateServiceCallModal'));
const ResponsibilityPage = lazy(() => import(/* webpackChunkName: "responsibility-page" */ './components/ResponsibilityPage'));
const PoiTicketsModal = lazy(() => import(/* webpackChunkName: "service-calls-page" */ './components/serviceCalls/PoiTicketsModal'));
import type { AutodeskViewerHandle, PanoramaFloorNavigationState } from './components/AutodeskViewer';
import LayersPanel from './components/browser/LayersPanel';
import { ViewAsBanner, ViewAsMenuItem, ViewAsDialog } from './components/ViewAsBar';
import WhatsNewDialog from './components/WhatsNewDialog';
import { WHATS_NEW_RELEASE, WHATS_NEW_SEEN_PREF, shouldShowWhatsNew } from './constants/whatsNew';
import type { AssetSystemLayer } from './viewers/autodesk/assetMarkerGrouping';
import { DEFAULT_TICKET_QUEUE, type TicketQueueState, type TicketScope } from './components/serviceCalls/types';
import type { ServiceCallDraft } from './components/serviceCalls/CreateServiceCallModal';
import NotificationBell from './components/notifications/NotificationBell';
import type { WorkingE57PanoramaViewerHandle } from './components/WorkingE57PanoramaViewer';
import ViewerErrorBoundary from './components/ViewerErrorBoundary';
import NewVersionNotice from './components/NewVersionNotice';
import { useNewVersionCheck, useNewVersionReady } from './hooks/useNewVersion';
// AssetCubePanoramaViewer removed during simplification

// Loading fallback for lazy-loaded viewers
const ViewerLoadingFallback: React.FC = () => (
  <div style={{
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--dt-surface-2)',
    color: 'var(--dt-text-2)',
    gap: '16px'
  }}>
    <div style={{
      width: '44px',
      height: '44px',
      border: '3px solid var(--dt-border)',
      borderTopColor: 'var(--dt-accent)',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }} />
    <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: 'var(--dt-text-2)' }}>Loading Viewer…</p>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

import SitesPage from './components/SitesPage';
import AssetsSearchPage from './components/AssetsSearchPage';
import LocationsPage from './components/LocationsPage';
import SystemsPage from './components/SystemsPage';
import { useTheme } from './contexts/ThemeContext';
import { useAuth } from './contexts/AuthContext';
import { useRBAC } from './contexts/RBACContext';
import { useTranslation } from './contexts/TranslationContext';
import LoginPage from './components/LoginPage';
import SimpleSettingsPanel from './components/SimpleSettingsPanel';
import POILayerControls from './components/POILayerControls';
import POIHierarchyBrowser from './components/browser/POIHierarchyBrowser';
import { usePOIHierarchies } from './components/browser/usePOIHierarchies';
import {
  BROWSER_PANE_WIDTH,
  BROWSER_PANE_WIDTH_PX,
  BROWSER_PANE_HIDDEN_OFFSET,
  BROWSER_PANE_CONTROL_INSET,
  browserPaneSide,
} from './components/browser/paneLayout';
import { aggregatePOIRows } from './utils/poiRowAggregation';
// Rare/admin pages are lazy (perf item 2): they leave the eager pages chunk
// and load on first open — same pattern as the viewers above.
const UsersPage = lazy(() => import(/* webpackChunkName: "users-page" */ './components/UsersPage'));
const TestRunsPage = lazy(() => import(/* webpackChunkName: "test-runs-page" */ './components/TestRunsPage'));
const UsageDashboard = lazy(() => import(/* webpackChunkName: "usage-dashboard" */ './components/UsageDashboard'));
const DocumentationPage = lazy(() => import(/* webpackChunkName: "documentation-page" */ './components/DocumentationPage'));
import AcceptInvitationPage from './components/AcceptInvitationPage';
import coordinateSync, { ViewPosition, SyncEvent } from './services/CoordinateSync';
import { WALK_EYE_HEIGHT_METERS } from './services/spatial/EyeHeights';
import { Asset, assetService } from './services/AssetService';
import { useHeaderDropdowns } from './app/hooks/useHeaderDropdowns';
import { useAssetSearch } from './app/hooks/useAssetSearch';
import { useLocationHierarchy } from './app/hooks/useLocationHierarchy';
import { useSyncPipeline } from './app/hooks/useSyncPipeline';
import { usePanorama3DOverlay } from './app/hooks/usePanorama3DOverlay';
import { useElementThumbnails } from './app/hooks/useElementThumbnails';
import { fileService } from './services/FileService';
import { POIService } from './services/POIService';
import { apiService } from './services/ApiService';
import { accService, ACCLink } from './services/ACCService';
import { POI, POICategory } from './types';
import type { PanoramaNavigationRequest, PanoramaNavigationTarget, PanoramaViewerState } from './types/PanoramaSync';
import type { ViewerSelectedElement } from './types/poi-table';
import { useViewerBlocker } from './orchestration/useViewerBlocker';
import type { ViewerMode } from './orchestration/useViewerBlocker';
import { getPanoramaBlockStep } from './orchestration/panoramaBlockGate';
import { useViewerStack } from './orchestration/useViewerStack';
import { showErrorToast } from './components/shared/toast';
import { Bdi } from './components/shared/BidiIsolate';
import { useUserPreference } from './hooks/useUserPreference';
import { logger } from './utils/log';
import { trackEvent, markSpanStart, trackSpanEnd } from './services/usageMetrics';

interface User {
  name: string;
  email: string;
  avatar: string;
  organization: string;
}

interface MenuItemConfig {
  id: string;
  label: string;
  icon: string;
  disabled?: boolean;
  adminOnly?: boolean;
  globalAdminOnly?: boolean;
  onboardingOnly?: boolean;
  /** Collapsible group: renders a droplist header; children are the pages. */
  children?: MenuItemConfig[];
}

/**
 * Line-icon set for the sidebar menu (DT design language). Keyed by menu id;
 * additive — rows fall back to the emoji `icon` string when no SVG exists,
 * so no MenuItemConfig type change and no behavior change. Styled by
 * `.dt-menu-ic` (stroke = currentColor, inherits the row's active/idle color).
 */
const MENU_ICON: Record<string, React.ReactNode> = {
  dashboard: (<svg className="dt-menu-ic" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /></svg>),
  tickets: (<svg className="dt-menu-ic" viewBox="0 0 24 24"><path d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0l-8-8V3h9.4l8.6 8.6a2 2 0 0 1 0 2.8z" /><circle cx="7.5" cy="7.5" r="1.1" /></svg>),
  viewer: (<svg className="dt-menu-ic" viewBox="0 0 24 24"><path d="M21 7.5v9L12 21l-9-4.5v-9L12 3z" /><path d="M3 7.5l9 4.5 9-4.5" /><path d="M12 12v9" /></svg>),
  sites: (<svg className="dt-menu-ic" viewBox="0 0 24 24"><rect x="4" y="3" width="16" height="18" rx="1" /><path d="M9 21v-4h6v4" /><path d="M8 7h.01M12 7h.01M16 7h.01M8 11h.01M12 11h.01M16 11h.01" /></svg>),
  assets: (<svg className="dt-menu-ic" viewBox="0 0 24 24"><path d="M21 16V8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><path d="M3.3 7L12 12l8.7-5" /><path d="M12 22V12" /></svg>),
  locations: (<svg className="dt-menu-ic" viewBox="0 0 24 24"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" /><circle cx="12" cy="10" r="3" /></svg>),
  systems: (<svg className="dt-menu-ic" viewBox="0 0 24 24"><line x1="6" y1="3" x2="6" y2="15" /><circle cx="18" cy="6" r="3" /><circle cx="6" cy="18" r="3" /><path d="M18 9a9 9 0 0 1-9 9" /></svg>),
  diagrams: (<svg className="dt-menu-ic" viewBox="0 0 24 24"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><line x1="8.6" y1="10.7" x2="15.4" y2="6.3" /><line x1="8.6" y1="13.3" x2="15.4" y2="17.7" /></svg>),
  users: (<svg className="dt-menu-ic" viewBox="0 0 24 24"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.9" /><path d="M16 3.1a4 4 0 0 1 0 7.8" /></svg>),
  tests: (<svg className="dt-menu-ic" viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>),
  // Usage (main's observability dashboard) — a bar chart, not the people glyph the lookup falls back to.
  usage: (<svg className="dt-menu-ic" viewBox="0 0 24 24"><path d="M3 3v18h18" /><rect x="7" y="12" width="3" height="6" /><rect x="12" y="8" width="3" height="10" /><rect x="17" y="5" width="3" height="13" /></svg>),
  connections: (<svg className="dt-menu-ic" viewBox="0 0 24 24"><path d="M12 22v-5" /><path d="M9 8V2" /><path d="M15 8V2" /><path d="M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8z" /></svg>),
  devices: (<svg className="dt-menu-ic" viewBox="0 0 24 24"><rect x="5" y="5" width="14" height="14" rx="2" /><rect x="9" y="9" width="6" height="6" /><path d="M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2" /></svg>),
  integrations: (<svg className="dt-menu-ic" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>),
  settings: (<svg className="dt-menu-ic" viewBox="0 0 24 24"><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" /></svg>),
};

const normalizePanoramaSourceFileKey = (sourceFileId: number | string | null | undefined): string | null => {
  if (sourceFileId === null || sourceFileId === undefined) {
    return null;
  }

  const normalized = String(sourceFileId).trim();
  return normalized.length > 0 ? normalized : null;
};

const getLocalRvtFiles = (asset: Asset | null | undefined): any[] => {
  const files = asset?.files as any;
  if (!files) return [];
  if (Array.isArray(files.rvtFiles)) return files.rvtFiles;
  return files.rvt ? [files.rvt] : [];
};

const getReadyLocalRvtFiles = (asset: Asset | null | undefined): any[] => (
  getLocalRvtFiles(asset).filter((rvtFile) => {
    const metadata = rvtFile?.metadata || {};
    return metadata.rvt_translation_status === 'ready' && !!metadata.viewable_urn;
  })
);

// Room the 3D / 2D / Panorama switch takes at the top of the viewer (about 240 px wide, 16 px from
// the edge) plus a gap — the panorama top row never extends under it.
const VIEW_MODE_SWITCH_RESERVE_PX = 290;

const getPrimaryLocalRvtFile = (asset: Asset | null | undefined): any | null => {
  const readyFiles = getReadyLocalRvtFiles(asset);
  return readyFiles.find((rvtFile) => rvtFile?.isDefault) || readyFiles[0] || null;
};

interface AutodeskAdditionalRVTModel {
  urn: string;
  connectionId: string;
  fileName: string;
  defaultLevelName?: string | null;
  defaultLevelElevation?: number | null;
}

const getAdditionalLocalRvtModels = (
  asset: Asset | null | undefined,
  primaryUrn: string | null | undefined
): AutodeskAdditionalRVTModel[] => (
  getReadyLocalRvtFiles(asset)
    .filter((rvtFile) => {
      const urn = rvtFile?.metadata?.viewable_urn;
      return !!urn && urn !== primaryUrn;
    })
    .map((rvtFile) => {
      const metadata = rvtFile.metadata || {};
      const levels = Array.isArray(metadata.levels) ? metadata.levels : [];
      const selectedLevel = metadata.rvt_default_level
        ? levels.find((level: any) => level?.name === metadata.rvt_default_level)
        : null;

      return {
        urn: metadata.viewable_urn,
        connectionId: 'local-rvt',
        fileName: rvtFile.fileName,
        defaultLevelName: metadata.rvt_default_level || null,
        defaultLevelElevation: selectedLevel?.elevation ?? null
      };
    })
);

const dedupeAdditionalRvtModels = (
  models: AutodeskAdditionalRVTModel[],
  primaryUrn: string | null | undefined
): AutodeskAdditionalRVTModel[] => {
  const seen = new Set<string>();
  return models.filter((model) => {
    if (!model.urn || model.urn === primaryUrn || seen.has(model.urn)) {
      return false;
    }
    seen.add(model.urn);
    return true;
  });
};

// Simple image component - no fallbacks, either works or it doesn't!

/**
 * Plan-space (x, y) of a POI row — the frame BOTH panorama stations and the GeoTIFF map are
 * expressed in (z is height in all three). Used by the Browser pane's go-to actions.
 */
const readPOIPlanPosition = (poi: any): { x: number; y: number } | null => {
  const x = Number(poi?.x ?? poi?.coordinates?.x);
  const y = Number(poi?.y ?? poi?.coordinates?.y);
  return (Number.isFinite(x) && Number.isFinite(y)) ? { x, y } : null;
};

/**
 * Simplified Mabat3D Portal for initial development
 * Updated: Menu toggle repositioned, viewer full-width, user avatars reverted
 */
const App: React.FC = () => {
  const { colors } = useTheme();
  const { isAuthenticated, isLoading: authLoading, user: authUser, logout } = useAuth();
  // "A new version is ready - Reload" (utils/newVersion.ts): looked for when signed in (a sign-in
  // does not reload the page), when the tab comes back into view and every 10 minutes.
  const newVersionReady = useNewVersionReady();
  useNewVersionCheck(isAuthenticated);
  const { roles, isAdmin, canAdministerPeople, canCreateSites, administersSite, accessibleSiteCount, loading: rbacLoading, viewAs } = useRBAC();
  // Per-user server-persisted preference: asset icons on 2D view + minimap (default OFF).
  const [showAssetIcons2D] = useUserPreference<boolean>('showAssetIcons2D', false);
  const { t, currentLanguage, setLanguage, isRTL } = useTranslation();
  const isBrowserRegressionRunnerPage = new URLSearchParams(window.location.search).get('mabatBrowserRunner') === 'frontend';

  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS (React Rules of Hooks)
  const [leftMenuCollapsed, setLeftMenuCollapsed] = useState(false);
  // Service-call creation draft (Epic B) — set from any origin surface (manual page,
  // 3D ElementInfoPanel, diagram impact row); non-null renders the global modal.
  const [serviceCallDraft, setServiceCallDraft] = useState<ServiceCallDraft | null>(null);
  /** "Tickets for this asset" target — one asset, asked from a row menu or the element panel. */
  const [poiTicketsTarget, setPoiTicketsTarget] = useState<{ id: number; title: string } | null>(null);
  // "Show in system diagram" target (Phase 1/2 element actions): the asset whose diagram node
  // the P&ID page should select on arrival. Cleared once the page has consumed it.
  const [ticketRevision, setTicketRevision] = useState(0);
  const [ticketQueues, setTicketQueues] = useState<Record<string, TicketQueueState>>({});
  const [ticketSystemFocusId, setTicketSystemFocusId] = useState<number | null>(null);
  const [diagramTargetId, setDiagramTargetId] = useState<number | null>(null);
  const ticketNavigationSeq = useRef(0);
  const [diagramFocusPOIId, setDiagramFocusPOIId] = useState<string | null>(null);

  const [activeMenuItem, setActiveMenuItem] = useState(() => {
    if (isBrowserRegressionRunnerPage) {
      sessionStorage.setItem('mabat3d_activePage', 'tests');
      return 'tests';
    }

    // Try to load saved page from sessionStorage (persists only during tab session)
    const savedPage = sessionStorage.getItem('mabat3d_activePage');
    // 'service-calls' was renamed 'tickets' (2026-07-14) — normalize stale saves.
    const normalized = savedPage === 'service-calls' ? 'tickets' : savedPage;
    // The tab's saved page, else Sites. The default used to be 'assets', the asset table that left
    // the menu in August: every new tab and every sign-in opened a page off the menu with no row
    // marked (register S22, Idan 10 Oct 2026: "is my page not suppose to be site page?").
    return normalized || 'sites';
  });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [files3D, setFiles3D] = useState<File[]>([]);
  const [files2D, setFiles2D] = useState<File[]>([]);
  const [filesPanorama, setFilesPanorama] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [viewerError, setViewerError] = useState<string | null>(null);
  // Persistent viewer stack visibility flags — extracted verbatim into useViewerStack
  // (structural phase 1, slice C). Destructured under the original names so every
  // consumer below is untouched.
  const {
    show3DViewer,
    setShow3DViewer,
    showWorkingE57Viewer,
    setShowWorkingE57Viewer,
    showGEOTIFFViewer,
    setShowGEOTIFFViewer,
    showAutodeskViewer,
    setShowAutodeskViewer,
    hasRendered3DViewer,
    setHasRendered3DViewer,
    hasRenderedE57Viewer,
    setHasRenderedE57Viewer,
    hasRenderedGEOTIFFViewer,
    setHasRenderedGEOTIFFViewer,
    hasRenderedAutodeskViewer,
    setHasRenderedAutodeskViewer
  } = useViewerStack();
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  useEffect(() => { setTicketQueues({}); ++ticketNavigationSeq.current; }, [authUser?.id]);
  useEffect(() => {
    ++ticketNavigationSeq.current;
    setServiceCallDraft(null);
    setPoiTicketsTarget(null);
    setDiagramTargetId(null);
    setTicketSystemFocusId(null);
    setDiagramFocusPOIId(null);
  }, [selectedAsset?.id]);
  const updateTicketQueue = (state: TicketQueueState) => {
    if (selectedAsset) setTicketQueues(previous => ({ ...previous, [selectedAsset.id]: state }));
  };
  const openScopeTickets = (scope: TicketScope) => {
    if (!selectedAsset) return;
    updateTicketQueue({ ...DEFAULT_TICKET_QUEUE, scope });
    setActiveMenuItem('tickets');
  };
  const createScopeTicket = (scope: TicketScope) => setServiceCallDraft({ origin: 'manual', scopes: [scope] });
  const openTicket = async (siteId: string, callId?: number) => {
    const request = ++ticketNavigationSeq.current;
    if (selectedAsset?.id !== siteId) {
      const site = await assetService.getAsset(siteId);
      if (request !== ticketNavigationSeq.current) return;
      if (!site) { showErrorToast(t('dt_tickets_siteUnavailable')); return; }
      assetService.selectAsset(siteId);
      handleAssetSelected(site);
    }
    setTicketQueues(previous => ({ ...previous, [siteId]: {
      ...(previous[siteId] || DEFAULT_TICKET_QUEUE), selectedId: callId || null,
    } }));
    setActiveMenuItem('tickets');
  };

  const [previousAssetId, setPreviousAssetId] = useState<string | null>(null);
  const [autodeskViewerConfig, setAutodeskViewerConfig] = useState<{
    urn: string;
    connectionId: string;
    assetId: string;
    viewables: Array<{ guid: string; name: string; role: '3d' | '2d' }>;
    fileName?: string; // RVT file name for tracking loaded models (enables 3D button for RVT POIs)
    defaultLevelName?: string | null; // User-selected level for camera spawn position
    defaultLevelElevation?: number | null; // User-selected level elevation (more reliable)
    initialViewableGuid?: string | null; // Pre-selected 3D view/phase from Sites page
    accRegion?: 'US' | 'EMEA'; // ACC region for API endpoint configuration
    roomSourceViewableGuid?: string | null; // Viewable to load room geometry from
    additionalModels?: AutodeskAdditionalRVTModel[]; // Explicit secondary RVTs captured from a fresh asset read
  } | null>(null);


  const getLocalRvtViewerConfig = useCallback((localRvt: any, assetIdOverride?: string) => {
    const metadata = localRvt?.metadata || {};
    const levels = Array.isArray(metadata.levels) ? metadata.levels : [];
    const selectedLevel = metadata.rvt_default_level
      ? levels.find((l: any) => l?.name === metadata.rvt_default_level)
      : null;

    return {
      urn: metadata.viewable_urn,
      connectionId: 'local-rvt',
      assetId: assetIdOverride || selectedAsset?.id || '',
      viewables: (metadata.viewables || []).map((v: any) => ({
        guid: v.guid,
        name: v.name,
        role: v.role,
      })),
      fileName: localRvt?.fileName,
      defaultLevelName: metadata.rvt_default_level || null,
      defaultLevelElevation: selectedLevel?.elevation ?? null,
      initialViewableGuid: metadata.rvt_selected_viewable_guid || null,
    };
  }, [selectedAsset?.id]);

  const [minimapCollapsed, setMinimapCollapsed] = useState(false);
  const [loadedAccLinks, setSelectedAssetAccLinks] = useState<ACCLink[]>([]);
  // The Autodesk links of the SELECTED site only. Links are loaded per site and cleared on a switch,
  // but for one render after a switch (and when an earlier site's request answers late) the loaded
  // list still holds the previous site's links; a viewer built from one of them loads that site's
  // model under this site's id (a winery tab asked for ACC SODA STREAM's connection, 11 Oct 2026).
  const selectedAssetAccLinks = useMemo(
    () => (selectedAsset?.id ? loadedAccLinks.filter(link => link.asset_id === selectedAsset.id) : []),
    [loadedAccLinks, selectedAsset?.id]
  );

  // Element-picture generator (hidden headless viewer, Option D) — fed by
  // Set-Systems and the diagram backfill; consumers re-fetch signed thumbnail
  // URLs whenever thumbnailsVersion bumps. The config falls back to the site's
  // ready ACC link (loaded on SITE SELECTION) so generation works without the
  // 3D viewer ever being opened this session — the generator bootstraps the
  // LMV environment itself from urn/connectionId/region.
  const thumbnailViewerConfig = useMemo(() => {
    if (autodeskViewerConfig) return autodeskViewerConfig;
    const readyLink = selectedAssetAccLinks.find(link => accService.isViewerReady(link) && !!link.viewable_urn);
    if (readyLink?.viewable_urn) {
      return {
        urn: readyLink.viewable_urn,
        connectionId: readyLink.acc_connection_id,
        assetId: selectedAsset?.id || '',
        accRegion: (readyLink as any).acc_region || undefined,
      };
    }
    return null;
  }, [autodeskViewerConfig, selectedAssetAccLinks, selectedAsset?.id]);

  const { enqueueThumbnails, thumbnailsVersion } = useElementThumbnails({
    assetId: selectedAsset?.id,
    viewerConfig: thumbnailViewerConfig,
  });

  const autoTriggeredViewerRef = useRef<string | null>(null);

  // Derived state: Check if current asset has an RVT file (from ACC links or local uploads)
  // RVT files have built-in 2D minimap, so we don't need GEOTIFF viewer for them
  const hasRVTFile = useMemo(() => {
    const hasAccRVT = selectedAssetAccLinks.some(link => link.acc_file_name?.toLowerCase().endsWith('.rvt'));
    const hasLocalRVT = getLocalRvtFiles(selectedAsset).length > 0;
    const hasRvtKey = selectedAsset?.files ? Object.keys(selectedAsset.files).includes('rvt') : false;
    return hasAccRVT || hasLocalRVT || hasRvtKey;
  }, [selectedAssetAccLinks, selectedAsset]);
  const hasIFCFile = Boolean(selectedAsset?.files?.ifc?.length);

  const usesAutodeskPanoramaFlow = useMemo(() => (
    !!selectedAsset?.files?.e57 &&
    (hasRVTFile || showAutodeskViewer || !!autodeskViewerConfig)
  ), [autodeskViewerConfig, hasRVTFile, selectedAsset?.files?.e57, showAutodeskViewer]);

  // Viewer blocker state machine + view-mode state — extracted verbatim into useViewerBlocker
  // (structural phase 1, slices A+B). Destructured under the original names so every consumer
  // below is untouched. handleViewModeChange (the mode-switch handler) intentionally stays here.
  const {
    currentViewMode,
    setCurrentViewMode,
    currentViewModeRef,
    viewerBlockState,
    viewerModeReady,
    beginViewerBlock,
    finishViewerBlock,
    updateViewerBlockProgress,
    updateViewerBlockStage
  } = useViewerBlocker({
    activeMenuItem,
    hasIFCFile,
    hasRVTFile,
    selectedAsset,
    show3DViewer,
    showAutodeskViewer,
    showGEOTIFFViewer,
    showWorkingE57Viewer
  });

  // The Panorama loading screen waits for the photo AND, on a site whose panorama runs on an
  // Autodesk model, every model settled (Idan 2026-09-29 — orchestration/panoramaBlockGate.ts).
  // Each input records WHICH SITE it is true for, so a site switch can never be released by the
  // previous site's photo or models — a reset effect would run one commit too late for that.
  const [panoramaPhotoReadyFor, setPanoramaPhotoReadyFor] = useState<string | null>(null);
  const [panoramaModelsReadyFor, setPanoramaModelsReadyFor] = useState<string | null>(null);
  // Whose ACC links are loaded — until the current site's are, nobody knows if a model is coming.
  const [accLinksAssetId, setAccLinksAssetId] = useState<string | null>(null);
  const [autodeskModelsProgress, setAutodeskModelsProgress] = useState<number | null>(null);
  const selectedAssetIdRef = useRef<string | null>(null);
  selectedAssetIdRef.current = selectedAsset?.id ?? null;
  useEffect(() => {
    setAutodeskModelsProgress(null);
  }, [selectedAsset?.id]);

  // Compute additional RVT models to load (all viewer-ready RVT files except the primary one)
  const additionalRVTModels = useMemo(() => {
    if (!autodeskViewerConfig) return [];

    const accModels = selectedAssetAccLinks
      .filter(link =>
        // Must be viewer-ready
        accService.isViewerReady(link) &&
        // Must be an RVT file
        link.acc_file_name?.toLowerCase().endsWith('.rvt') &&
        // Exclude the primary model already being shown
        link.viewable_urn !== autodeskViewerConfig.urn
      )
      .map(link => ({
        urn: link.viewable_urn!,
        connectionId: link.acc_connection_id,
        fileName: link.acc_file_name,
        defaultLevelName: link.default_level || null,
        defaultLevelElevation: null // Will use default from primary model
      }));

    const localModels = getAdditionalLocalRvtModels(selectedAsset, autodeskViewerConfig.urn);

    return dedupeAdditionalRvtModels([...accModels, ...localModels], autodeskViewerConfig.urn);
  }, [selectedAssetAccLinks, autodeskViewerConfig, selectedAsset]);

  const effectiveAdditionalRVTModels = useMemo(() => {
    if (!autodeskViewerConfig) return [];
    return dedupeAdditionalRvtModels([
      ...(autodeskViewerConfig.additionalModels || []),
      ...additionalRVTModels
    ], autodeskViewerConfig.urn);
  }, [additionalRVTModels, autodeskViewerConfig]);

  // Reset GEOTIFF viewer state when RVT is detected
  // RVT files have their own built-in 2D minimap via Autodesk Minimap3DExtension
  useEffect(() => {
    if (hasRVTFile) {
      logger.debug('[App] RVT file detected - disabling GEOTIFF viewer');
      setHasRenderedGEOTIFFViewer(false);
      setShowGEOTIFFViewer(false);
    }
  }, [hasRVTFile, setHasRenderedGEOTIFFViewer, setShowGEOTIFFViewer]);

  // Shared layer pane state across all viewers
  const [isLayerPaneOpen, setIsLayerPaneOpen] = useState(false);

  // Shared layer control state (synced across all viewers)
  const [showPanoramaLayer, setShowPanoramaLayer] = useState(true);

  /**
   * Asset layers, OWNED HERE so both panes drive the same flags. The viewer renders its pane and
   * the panorama renders its own, but the selection is one state — the split between them is
   * exactly what made the panorama pane unable to turn off flags the viewer pane had switched on.
   *
   * The LIST is reported up by the viewer rather than derived here: the viewer groups from its
   * `allPois`, which is the freshly extracted set when one exists and falls back to these props
   * otherwise. Deriving it here would miss locally extracted POIs.
   */
  const [assetSystemLayers, setAssetSystemLayers] = useState<AssetSystemLayer[]>([]);
  const [enabledAssetSystems, setEnabledAssetSystems] = useState<Set<string>>(() => new Set());
  const [isAssetsLayerOpen, setIsAssetsLayerOpen] = useState(false);

  const toggleAssetSystem = useCallback((systemId: string) => {
    setEnabledAssetSystems((previous) => {
      const next = new Set(previous);
      if (next.has(systemId)) next.delete(systemId);
      else next.add(systemId);
      return next;
    });
  }, []);

  /** Master toggle: all on, or all off. Mixed state resolves to ON — the useful direction. */
  const toggleAllAssetSystems = useCallback(() => {
    setEnabledAssetSystems((previous) => {
      const everyOn = assetSystemLayers.length > 0
        && assetSystemLayers.every((layer) => previous.has(layer.id));
      return everyOn ? new Set<string>() : new Set(assetSystemLayers.map((layer) => layer.id));
    });
  }, [assetSystemLayers]);

  // A system that disappears (POIs re-extracted, systems reassigned) must not stay enabled
  // invisibly and switch back on if a system of the same name returns later.
  useEffect(() => {
    setEnabledAssetSystems((previous) => {
      if (previous.size === 0) return previous;
      const live = new Set(assetSystemLayers.map((layer) => layer.id));
      const pruned = new Set(Array.from(previous).filter((id) => live.has(id)));
      return pruned.size === previous.size ? previous : pruned;
    });
  }, [assetSystemLayers]);
  // A 3D tool takes clicks on the photo in Panorama: measuring, or placing / drawing a planning
  // object (reported by AutodeskViewer). The shield then hands it the hover and the clicks.
  const [isAutodeskPhotoClickToolActive, setIsAutodeskPhotoClickToolActive] = useState(false);

  const isPanoramaPage =
    activeMenuItem === 'viewer' &&
    currentViewMode === 'Panorama';

  const [autodeskSceneInsets, setAutodeskSceneInsets] = useState<PanoramaViewportInsets>(FULL_PANORAMA_VIEWPORT);
  // Panorama-page 3D-overlay state machine (F-2 hook; destructured under original names).
  // Owns flag/source/opacity + derived visibility + the flag<->visibility reconciler.
  // ON-activation side effects stay in handlePanorama3DOverlayToggle below.
  const {
    showPanorama3DOverlay,
    panorama3DOverlaySource,
    panorama3DOverlayOpacity,
    panorama3DOverlayOpacityPercent,
    handlePanorama3DOverlayOpacityChange,
    isAutodeskPanorama3DOverlayVisible,
    isIFCPanorama3DOverlayVisible,
    isPanorama3DOverlayVisible,
    isPanoramaMarkersOnly,
    openPanorama3DOverlay,
    closePanorama3DOverlay,
  } = usePanorama3DOverlay({
    isPanoramaPage,
    showAutodeskViewer,
    hasAutodeskConfig: !!autodeskViewerConfig,
    show3DViewer,
    hasIFCFile,
  });
  /**
   * THE PANORAMA ALWAYS SHOWS THE FLAGS. The marker layer lives in the Autodesk viewer (the
   * panorama's own nav discs are storey-filtered and come back empty on multi-source sites), so
   * that viewer is composited over the photo in Panorama mode whether or not the 3D overlay is
   * on — with the MODEL hidden, which is what `markersOnly` does.
   *
   * The 3D overlay button no longer decides whether markers exist; it decides whether the MODEL
   * is drawn on top of them, at the slider's opacity.
   */
  const isAutodeskPanoramaMarkerLayer =
    activeMenuItem === 'viewer'
    && currentViewMode === 'Panorama'
    && showAutodeskViewer
    && !!autodeskViewerConfig
    && !isAutodeskPanorama3DOverlayVisible;

  // The marker layer needs the SAME camera mirroring and input routing the overlay gets: the
  // Autodesk camera must follow the panorama so the flags are drawn from where you are actually
  // standing, and drags/wheel must reach the panorama. Without this the Autodesk canvas — which
  // is on top and now takes pointer events — swallowed the drag: the flags rotated and the photo
  // stayed put (2026-08-25).
  //
  // MEASURING keeps both (Idan 2026-10-01). It used to switch them off so Autodesk's measure tool got
  // the real mouse — but then a drag turned only the 3D (40° off the photo, measured) while the photo
  // stayed put. Now the photo still turns on a drag, the 3D follows it, and the shield hands the
  // measure tool what it needs: the hover (snapping) and the click, each with a move first.
  const isPanoramaOverlayCameraMirroringEnabled =
    isAutodeskPanorama3DOverlayVisible || isAutodeskPanoramaMarkerLayer;
  const isPanoramaOverlayInputRoutingEnabled =
    isIFCPanorama3DOverlayVisible ||
    isPanoramaOverlayCameraMirroringEnabled;

  // Panorama's top bar sits right after an open Browser pane. Its gap is measured from the PHOTO
  // AREA's edge — and with the overlay on, the photo area itself already moved over for the pane (the
  // 3D viewer's own Browser takes room; Panorama's own pane only floats over the photo). Adding the
  // full gap on top of that pushed the bar one pane-width too far (Idan 2026-10-02: "going to the
  // right twice"; measured x 958 instead of 636). The photo area's move is subtracted.
  const panoramaPhotoAreaPaneShift = (isAutodeskPanorama3DOverlayVisible || isAutodeskPanoramaMarkerLayer)
    ? (currentLanguage === 'he' ? autodeskSceneInsets.right : autodeskSceneInsets.left)
    : 0;
  const panoramaTopBarInset = isLayerPaneOpen
    ? `${Math.max(20, BROWSER_PANE_WIDTH_PX + 40 - panoramaPhotoAreaPaneShift)}px`
    : '20px';
  const isIFC3DViewerVisible =
    activeMenuItem === 'viewer' &&
    show3DViewer &&
    (currentViewMode === '3D' || isIFCPanorama3DOverlayVisible);

  /**
   * Events coming from viewer UI that is PORTALED OUT of the overlay (the Element Info panel).
   *
   * A React portal renders into document.body but its events still bubble through the REACT
   * tree — so a click inside the panel reaches this container's capture handlers, which run
   * before the panel's own onClick and stop the event dead. That is why the panel's close button
   * did nothing while the overlay was up (2026-08-25). The overlay routes pointer input to the
   * panorama; input that belongs to a panel on top of it must be left alone.
   */
  // Presses that belong to the viewers' own CONTROLS, not to the 3D picture over the photo: the
  // element info panel (portaled on top), the viewers' rails and panels, and any button / input /
  // menu. Only presses on the 3D picture itself are routed to the panorama (drag to turn, click a
  // marker). In markers mode the 3D area is not a stacking context (so its canvas can blend with the
  // photo), which puts its bottom-left buttons ABOVE the overlay shield — their presses reach these
  // handlers, which used to start a panorama drag, capture the pointer and swallow the click
  // (Search / Measure did nothing at "markers", 2026-10-01).
  const isEventFromViewerControls = useCallback((event: React.SyntheticEvent) => {
    const target = event.target as HTMLElement | null;
    return !!target?.closest?.('[data-element-info-panel="true"], [data-viewer-control="true"], .dt-viewer-workspace, .dt-viewer-browser, button, input, select, textarea, a, [role="dialog"], [role="menu"]');
  }, []);

  const stopPanoramaOverlayControlEvent = useCallback((event: React.SyntheticEvent) => {
    event.stopPropagation();
    (event.nativeEvent as any).stopImmediatePropagation?.();
  }, []);


  // 2D viewer UI state
  const [show2DNavigationMenu, setShow2DNavigationMenu] = useState(false);
  const [show2DMeasurementMenu, setShow2DMeasurementMenu] = useState(false);
  const [show2DHelpPanel, setShow2DHelpPanel] = useState(false);
  const [active2DMeasurementTool, setActive2DMeasurementTool] = useState<'distance' | 'area' | null>(null);

  // Mouse coordinates for 2D viewer
  const [mouseCoordinates, setMouseCoordinates] = useState<{
    x: number;
    y: number;
    imageX: number;
    imageY: number;
    hasGeoCoords: boolean;
    geoX?: number;
    geoY?: number;
  } | null>(null);

  const {
    languageDropdownOpen, setLanguageDropdownOpen, userDropdownOpen, setUserDropdownOpen,
    siteDropdownOpen, setSiteDropdownOpen,
  } = useHeaderDropdowns();
  // The header's site switcher: the list is fetched when it opens (not at boot).
  const [headerSites, setHeaderSites] = useState<Asset[] | null>(null);
  const [headerSiteFilter, setHeaderSiteFilter] = useState('');
  const [currentPanoramaState, setCurrentPanoramaState] = useState<PanoramaViewerState | null>(null);
  const [current3DCameraState, setCurrent3DCameraState] = useState<{
    position: { x: number; y: number; z: number; rotation?: number };
    adjustedPosition: { x: number; y: number; z: number }; // X=X, Y=-Z, Z=Y
    adjustedFixedPosition: { x: number; y: number; z: number }; // adjustedPosition with dynamic model bounds
  } | null>(null);
  const [pendingIfcPanoramaPoseSync, setPendingIfcPanoramaPoseSync] = useState<{
    key: number;
    state: PanoramaViewerState;
  } | null>(null);
  const pendingIfcPanoramaPoseSyncKeyRef = useRef(0);
  const [currentPanorama3DAnchor, setCurrentPanorama3DAnchor] = useState<{ x: number; y: number } | null>(null);
  const [modelBounds, setModelBounds] = useState<{ min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number } } | null>(null);
  const [activeSpaceBoundary, setActiveSpaceBoundary] = useState<Array<{x: number; y: number}> | null>(null); // Space floor boundary for 2D viewer visualization
  const [focusedPOI, setFocusedPOI] = useState<import('./types').POI | null>(null);
  const [currentPosition, setCurrentPosition] = useState<ViewPosition>({
    x: 0, y: WALK_EYE_HEIGHT_METERS, z: 0, heading: 0, pitch: 0, roll: 0
  });
  const [loadedIfcFiles, setLoadedIfcFiles] = useState<string[]>([]); // Track loaded IFC filenames from viewer
  const [loadingIfcFiles, setLoadingIfcFiles] = useState<string[]>([]); // Track IFC files currently being loaded
  const [requestedModelFileName, setRequestedModelFileName] = useState<string | null>(null); // Model requested to load via WebIfcViewer
  const [focusedRVTPOI, setFocusedRVTPOI] = useState<import('./types').POI | null>(null); // POI to focus in AutodeskViewer (RVT elements)
  const [highlightedPOIGroup, setHighlightedPOIGroup] = useState<{ pois: import('./types').POI[]; color?: string } | null>(null); // Group of POIs to highlight in 3D viewers
  const [viewerSelectedDbIds, setViewerSelectedDbIds] = useState<ViewerSelectedElement[]>([]); // Track selected elements from 3D viewer for filtering
  const [targetPanoramaSetup, setTargetPanoramaSetup] = useState<number | undefined>(undefined);
  const [panoramaNavigationRequest, setPanoramaNavigationRequest] = useState<PanoramaNavigationRequest | null>(null);
  const [panoramaViewerMountKey, setPanoramaViewerMountKey] = useState(0);
  const [closestAutodeskPanoramaTarget, setClosestAutodeskPanoramaTarget] = useState<PanoramaNavigationTarget | null>(null);
  // IFC flow equivalent of the Autodesk closest-panorama tracking: last raw
  // camera world position from WebIfcViewer + per-scan panorama locations
  const ifcCameraWorldPositionRef = useRef<{
    x: number;
    y: number;
    z: number;
    worldYaw?: number;
    pitch?: number;
    fov?: number;
  } | null>(null);
  // Per-scan panorama stations for the CURRENT site. Two consumers: the IFC flow's
  // closest-to-camera selection, and the Browser pane's "Jump to nearest station" (which is
  // flow-agnostic — hence the load below is no longer gated on the IFC flow).
  const [panoramaStationLocations, setPanoramaStationLocations] = useState<Array<{
    setupIndex: number;
    sourceFileId: number | string | null;
    x: number;
    y: number;
  }>>([]);
  const panoramaStationLocationsRef = useRef<typeof panoramaStationLocations>([]);
  const [closestIfcPanoramaTarget, setClosestIfcPanoramaTarget] = useState<{
    setupIndex: number;
    sourceFileId: number | string | null;
  } | null>(null);
  const closestIfcPanoramaTargetRef = useRef<typeof closestIfcPanoramaTarget>(null);
  const closestIfcPanoramaTargetSourceRef = useRef<'none' | 'raw-camera' | 'geotiff-minimap'>('none');
  const ifcClosestNoLocationsWarnedRef = useRef(false);
  const [pendingPanoramaPOI, setPendingPanoramaPOI] = useState<import('./types').POI | null>(null);
  const [visiblePanoramaIndices, setVisiblePanoramaIndices] = useState<Set<number> | null>(null);
  // Markers-only: scan indices that are behind geometry from where you are standing. Computed by
  // the Autodesk viewer (the RVT model lives only there) while it stays invisible, and folded
  // into the same visiblePanoramaIndices filter the storey filter already uses.
  const [visiblePanoramaKeys, setVisiblePanoramaKeys] = useState<Set<string> | null>(null);
  const [currentPanoramaStorey, setCurrentPanoramaStorey] = useState<string | null>(null);
  const [availableStoreys, setAvailableStoreys] = useState<Array<{name: string, elevation: number}>>([]);
  const [panoramaFloorNavigationState, setPanoramaFloorNavigationState] = useState<PanoramaFloorNavigationState>({
    currentFloorName: null,
    currentFloorIndex: null,
    floorCount: 0,
    canGoDown: false,
    canGoUp: false
  });
  const [modelGlobalOffset, setModelGlobalOffset] = useState<{x: number, y: number, z: number}>({x: 0, y: 0, z: 0});
  const [globalPOIService] = useState(() => new POIService());
  const autodeskViewerRef = useRef<AutodeskViewerHandle>(null); // Ref for polygon extraction


  const panoramaViewerRef = useRef<WorkingE57PanoramaViewerHandle>(null);
  const handlePanoramaFloorDown = useCallback(() => {
    autodeskViewerRef.current?.navigatePanoramaFloor(-1);
  }, []);
  const handlePanoramaFloorUp = useCallback(() => {
    autodeskViewerRef.current?.navigatePanoramaFloor(1);
  }, []);
  const routedPanoramaOverlayPointerRef = useRef<{ x: number; y: number; time: number } | null>(null);
  // When a press last went to a panorama marker (it moves you to that photo): a double-click there
  // is that move, never an element's details.
  const lastMarkerPressAtRef = useRef(0);
  const panoramaOverlayDragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    hasDragged: boolean;
  } | null>(null);
  const panoramaOverlayPassthroughPointerRef = useRef<{
    pointerId: number;
    button: number;
    startX: number;
    startY: number;
    hasMoved: boolean;
  } | null>(null);
  const panoramaNavigationRequestIdRef = useRef(0);
  const pendingPanoramaNavigationStateRef = useRef<{
    setupIndex: number;
    sourceFileId: string | null;
    requestId: number;
    expiresAt: number;
  } | null>(null);
  const pendingPanoramaPOIRef = useRef<import('./types').POI | null>(null);
  const pendingPanoramaPOITimerRef = useRef<number | null>(null);
  const [customRoomDrawingMode, setCustomRoomDrawingMode] = useState(false); // Custom room drawing mode
  const [customStoreyDrawingMode, setCustomStoreyDrawingMode] = useState(false); // Custom storey drawing mode
  const [editingCustomLocation, setEditingCustomLocation] = useState<any>(null); // Location being edited (null = create new)
  const [preSelectedBottomStoreyId, setPreSelectedBottomStoreyId] = useState<string | null>(null); // Pre-selected bottom storey for room drawing

  useEffect(() => {
    if (isPanoramaOverlayInputRoutingEnabled || !panoramaOverlayDragRef.current) {
      return;
    }

    panoramaOverlayDragRef.current = null;
    panoramaViewerRef.current?.endPanoramaOverlayDrag();
    panoramaOverlayPassthroughPointerRef.current = null;
  }, [isPanoramaOverlayInputRoutingEnabled]);

  const routePanoramaOverlayPointer = useCallback((clientX: number, clientY: number) => {
    if (!isPanoramaOverlayInputRoutingEnabled) {
      return false;
    }

    const handled = panoramaViewerRef.current?.handlePanoramaMarkerPointer(clientX, clientY) ?? false;
    if (handled) {
      lastMarkerPressAtRef.current = Date.now();
      routedPanoramaOverlayPointerRef.current = {
        x: clientX,
        y: clientY,
        time: Date.now()
      };
    }

    return handled;
  }, [isPanoramaOverlayInputRoutingEnabled]);

  const shouldSuppressRoutedPanoramaClick = useCallback((clientX: number, clientY: number) => {
    if (isAutodeskPhotoClickToolActive) {
      routedPanoramaOverlayPointerRef.current = null;
      return false;
    }

    const routedPointer = routedPanoramaOverlayPointerRef.current;
    if (!routedPointer) {
      return false;
    }

    const ageMs = Date.now() - routedPointer.time;
    const distance = Math.hypot(clientX - routedPointer.x, clientY - routedPointer.y);
    const shouldSuppress = ageMs < 800 && distance < 8;
    routedPanoramaOverlayPointerRef.current = null;
    return shouldSuppress;
  }, [isAutodeskPhotoClickToolActive]);

  const stopOverlayPointerEvent = useCallback((event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
    (event.nativeEvent as any).stopImmediatePropagation?.();
  }, []);

  const capturePanoramaOverlayPointer = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    try {
      if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.setPointerCapture(event.pointerId);
      }
    } catch {
      // Pointer capture can fail if the browser already released the pointer.
    }
  }, []);

  const releasePanoramaOverlayPointer = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    try {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    } catch {
      // Safe to ignore; the drag is still ended in React state below.
    }
  }, []);

  const beginPanoramaOverlayDrag = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!isPanoramaOverlayInputRoutingEnabled || event.button !== 0) {
      return false;
    }

    panoramaOverlayDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      hasDragged: false
    };
    capturePanoramaOverlayPointer(event);
    panoramaViewerRef.current?.beginPanoramaOverlayDrag(event.clientX, event.clientY);
    return true;
  }, [capturePanoramaOverlayPointer, isPanoramaOverlayInputRoutingEnabled]);

  const movePanoramaOverlayDrag = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const dragState = panoramaOverlayDragRef.current;
    if (!isPanoramaOverlayInputRoutingEnabled) {
      if (dragState?.pointerId === event.pointerId) {
        panoramaOverlayDragRef.current = null;
        panoramaViewerRef.current?.endPanoramaOverlayDrag();
        releasePanoramaOverlayPointer(event);
        stopOverlayPointerEvent(event);
      }
      return;
    }

    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    stopOverlayPointerEvent(event);

    const distance = Math.hypot(event.clientX - dragState.startX, event.clientY - dragState.startY);
    if (!dragState.hasDragged && distance < 3) {
      return;
    }

    dragState.hasDragged = true;
    panoramaViewerRef.current?.movePanoramaOverlayDrag(event.clientX, event.clientY);
  }, [isPanoramaOverlayInputRoutingEnabled, releasePanoramaOverlayPointer, stopOverlayPointerEvent]);

  const endPanoramaOverlayDrag = useCallback((event: React.PointerEvent<HTMLDivElement>, options?: { suppressClick?: boolean }) => {
    const dragState = panoramaOverlayDragRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    panoramaOverlayDragRef.current = null;
    panoramaViewerRef.current?.endPanoramaOverlayDrag();
    releasePanoramaOverlayPointer(event);

    if (options?.suppressClick !== false) {
      routedPanoramaOverlayPointerRef.current = {
        x: event.clientX,
        y: event.clientY,
        time: Date.now()
      };
    }
    stopOverlayPointerEvent(event);
  }, [releasePanoramaOverlayPointer, stopOverlayPointerEvent]);

  const handlePanoramaOverlayWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
    if (!isPanoramaOverlayInputRoutingEnabled) {
      return;
    }

    const handled = panoramaViewerRef.current?.handlePanoramaOverlayWheel(
      event.deltaY,
      event.deltaMode
    ) ?? false;

    if (handled) {
      stopOverlayPointerEvent(event);
    }
  }, [isPanoramaOverlayInputRoutingEnabled, stopOverlayPointerEvent]);

  /** The 3D-overlay element under a point of the shield (the shield itself looked through). */
  const find3DOverlayTargetUnderShield = useCallback((shield: HTMLElement, clientX: number, clientY: number): Element | null => {
    const previousPointerEvents = shield.style.pointerEvents;
    let targetElement: Element | null = null;
    const overlaySelector = [
      '[data-panorama-ifc-overlay="true"]',
      '[data-panorama-autodesk-overlay="true"]',
      '[data-autodesk-viewer-root="true"]'
    ].join(', ');

    shield.style.pointerEvents = 'none';
    try {
      const elements = typeof document.elementsFromPoint === 'function'
        ? document.elementsFromPoint(clientX, clientY)
        : [];

      targetElement = elements.find((element) => (
        element !== shield &&
        !shield.contains(element) &&
        !!element.closest(overlaySelector)
      )) ?? null;

      if (!targetElement) {
        const fallbackElement = document.elementFromPoint(clientX, clientY);
        if (
          fallbackElement &&
          fallbackElement !== shield &&
          !shield.contains(fallbackElement) &&
          fallbackElement.closest(overlaySelector)
        ) {
          targetElement = fallbackElement;
        }
      }
    } finally {
      shield.style.pointerEvents = previousPointerEvents;
    }
    return targetElement;
  }, []);

  // Measuring: Autodesk's measure tool snaps to what is under the mouse WHILE IT HOVERS, so the
  // shield hands it the hover too — one forwarded move per frame, the latest one.
  const measureHoverRef = useRef<{ shield: HTMLElement; clientX: number; clientY: number; screenX: number; screenY: number } | null>(null);
  const measureHoverFrameRef = useRef<number | null>(null);
  const forwardMeasureHover = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    measureHoverRef.current = {
      shield: event.currentTarget, clientX: event.clientX, clientY: event.clientY, screenX: event.screenX, screenY: event.screenY
    };
    if (measureHoverFrameRef.current !== null) return;
    measureHoverFrameRef.current = requestAnimationFrame(() => {
      measureHoverFrameRef.current = null;
      const hover = measureHoverRef.current;
      measureHoverRef.current = null;
      if (!hover) return;
      const target = find3DOverlayTargetUnderShield(hover.shield, hover.clientX, hover.clientY);
      target?.dispatchEvent(new MouseEvent('mousemove', {
        bubbles: true, cancelable: true, composed: true, view: window,
        clientX: hover.clientX, clientY: hover.clientY, screenX: hover.screenX, screenY: hover.screenY, button: 0, buttons: 0
      }));
    });
  }, [find3DOverlayTargetUnderShield]);
  useEffect(() => () => {
    if (measureHoverFrameRef.current !== null) cancelAnimationFrame(measureHoverFrameRef.current);
  }, []);

  // A press on a planning object belongs to the 3D layer (Idan 2026-10-02, option A): it drags the
  // object; a press anywhere else still turns the photo. The press, its moves and its release go to
  // the 3D canvas as mouse events — the object tool's drag listens on the viewer's container.
  const photoPressClaimRef = useRef<((clientX: number, clientY: number) => boolean) | null>(null);
  const registerPhotoPressClaim = useCallback((claim: ((clientX: number, clientY: number) => boolean) | null) => {
    photoPressClaimRef.current = claim;
  }, []);
  const photoPressRef = useRef<{ pointerId: number; target: Element; startX: number; startY: number; moved: boolean } | null>(null);
  const send3DMouse = useCallback((target: Element, type: string, event: React.PointerEvent<HTMLDivElement>, buttons: number) => {
    target.dispatchEvent(new MouseEvent(type, {
      bubbles: true, cancelable: true, composed: true, view: window,
      detail: type === 'mousemove' ? 0 : 1, button: 0, buttons,
      clientX: event.clientX, clientY: event.clientY, screenX: event.screenX, screenY: event.screenY,
      ctrlKey: event.ctrlKey, shiftKey: event.shiftKey, altKey: event.altKey, metaKey: event.metaKey,
    }));
  }, []);

  const handlePanoramaOverlayShieldPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button === 0 && photoPressClaimRef.current?.(event.clientX, event.clientY)) {
      const target = find3DOverlayTargetUnderShield(event.currentTarget, event.clientX, event.clientY);
      if (target) {
        photoPressRef.current = { pointerId: event.pointerId, target, startX: event.clientX, startY: event.clientY, moved: false };
        capturePanoramaOverlayPointer(event);
        send3DMouse(target, 'mousemove', event, 0);
        send3DMouse(target, 'mousedown', event, 1);
        stopOverlayPointerEvent(event);
        return;
      }
    }
    // While a tool takes the photo's clicks (measuring, placing), a click is the tool's — it never
    // jumps to the panorama under it.
    if (event.button === 0 && !isAutodeskPhotoClickToolActive && routePanoramaOverlayPointer(event.clientX, event.clientY)) {
      stopOverlayPointerEvent(event);
      return;
    }

    const didBeginDrag = beginPanoramaOverlayDrag(event);
    if (!didBeginDrag && event.button !== 0) {
      panoramaOverlayPassthroughPointerRef.current = {
        pointerId: event.pointerId,
        button: event.button,
        startX: event.clientX,
        startY: event.clientY,
        hasMoved: false
      };
      capturePanoramaOverlayPointer(event);
    }

    stopOverlayPointerEvent(event);
  }, [beginPanoramaOverlayDrag, capturePanoramaOverlayPointer, find3DOverlayTargetUnderShield, isAutodeskPhotoClickToolActive, routePanoramaOverlayPointer, send3DMouse, stopOverlayPointerEvent]);

  const handlePanoramaOverlayShieldPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const press = photoPressRef.current;
    if (press && press.pointerId === event.pointerId) {
      if (Math.hypot(event.clientX - press.startX, event.clientY - press.startY) >= 3) press.moved = true;
      send3DMouse(press.target, 'mousemove', event, 1);
      stopOverlayPointerEvent(event);
      return;
    }
    if (isAutodeskPhotoClickToolActive && event.buttons === 0 && !panoramaOverlayDragRef.current) {
      forwardMeasureHover(event);
    }
    const passthroughState = panoramaOverlayPassthroughPointerRef.current;
    if (passthroughState?.pointerId === event.pointerId) {
      const distance = Math.hypot(event.clientX - passthroughState.startX, event.clientY - passthroughState.startY);
      if (distance >= 3) {
        passthroughState.hasMoved = true;
      }
    }

    movePanoramaOverlayDrag(event);
    stopOverlayPointerEvent(event);
  }, [forwardMeasureHover, isAutodeskPhotoClickToolActive, movePanoramaOverlayDrag, send3DMouse, stopOverlayPointerEvent]);

  const replayPanoramaOverlayClickTo3DOverlay = useCallback((event: React.PointerEvent<HTMLDivElement>, button = event.button) => {
    const targetElement = find3DOverlayTargetUnderShield(event.currentTarget, event.clientX, event.clientY);
    if (!targetElement) {
      return;
    }

    routedPanoramaOverlayPointerRef.current = null;
    const replayButton = button === 2 ? 2 : button === 1 ? 1 : 0;
    const pressedButtons = replayButton === 2 ? 2 : replayButton === 1 ? 4 : 1;

    const mouseEventInit: MouseEventInit = {
      bubbles: true,
      cancelable: true,
      composed: true,
      view: window,
      detail: 1,
      screenX: event.screenX,
      screenY: event.screenY,
      clientX: event.clientX,
      clientY: event.clientY,
      ctrlKey: event.ctrlKey,
      shiftKey: event.shiftKey,
      altKey: event.altKey,
      metaKey: event.metaKey,
      button: replayButton
    };

    // A real click always arrives after the mouse moved there — and Autodesk's measure tool takes
    // the point from that move (without it, clicks on the photo placed nothing: measured 2026-10-01).
    targetElement.dispatchEvent(new MouseEvent('mousemove', {
      ...mouseEventInit,
      detail: 0,
      button: 0,
      buttons: 0
    }));
    targetElement.dispatchEvent(new MouseEvent('mousedown', {
      ...mouseEventInit,
      buttons: pressedButtons
    }));
    targetElement.dispatchEvent(new MouseEvent('mouseup', {
      ...mouseEventInit,
      buttons: 0
    }));

    if (replayButton === 2) {
      targetElement.dispatchEvent(new MouseEvent('contextmenu', {
        ...mouseEventInit,
        buttons: 0
      }));
      return;
    }

    targetElement.dispatchEvent(new MouseEvent(replayButton === 1 ? 'auxclick' : 'click', {
      ...mouseEventInit,
      buttons: 0
    }));
  }, [find3DOverlayTargetUnderShield]);

  const handlePanoramaOverlayShieldPointerEnd = useCallback((event: React.PointerEvent<HTMLDivElement>, options?: { replayClick?: boolean }) => {
    const press = photoPressRef.current;
    if (press && press.pointerId === event.pointerId) {
      photoPressRef.current = null;
      releasePanoramaOverlayPointer(event);
      send3DMouse(press.target, 'mouseup', event, 0);
      if (!press.moved && options?.replayClick !== false) send3DMouse(press.target, 'click', event, 0);
      stopOverlayPointerEvent(event);
      return;
    }
    const dragState = panoramaOverlayDragRef.current;
    const passthroughState = panoramaOverlayPassthroughPointerRef.current;
    const replayButton =
      passthroughState?.pointerId === event.pointerId
        ? passthroughState.button
        : event.button;
    const shouldReplayClick =
      options?.replayClick !== false &&
      (
        (
          !!dragState &&
          dragState.pointerId === event.pointerId &&
          !dragState.hasDragged &&
          replayButton === 0
        ) ||
        (
          !!passthroughState &&
          passthroughState.pointerId === event.pointerId &&
          !passthroughState.hasMoved &&
          (replayButton === 1 || replayButton === 2)
        )
      );

    if (dragState?.pointerId === event.pointerId) {
      endPanoramaOverlayDrag(event, { suppressClick: false });
    } else if (passthroughState?.pointerId === event.pointerId) {
      panoramaOverlayPassthroughPointerRef.current = null;
      releasePanoramaOverlayPointer(event);
    }

    if (shouldReplayClick) {
      replayPanoramaOverlayClickTo3DOverlay(event, replayButton);
    }

    stopOverlayPointerEvent(event);
  }, [endPanoramaOverlayDrag, releasePanoramaOverlayPointer, replayPanoramaOverlayClickTo3DOverlay, send3DMouse, stopOverlayPointerEvent]);

  const handlePanoramaOverlayShieldWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
    handlePanoramaOverlayWheel(event);
    stopOverlayPointerEvent(event);
  }, [handlePanoramaOverlayWheel, stopOverlayPointerEvent]);

  const handlePanoramaOverlayShieldClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    shouldSuppressRoutedPanoramaClick(event.clientX, event.clientY);
    stopOverlayPointerEvent(event);
  }, [shouldSuppressRoutedPanoramaClick, stopOverlayPointerEvent]);

  // A double-click on the photo opens the element under it, as it does in 3D (register P14: since
  // 26 May the shield dropped every double-click, so the element's details never opened with the
  // overlay on). Only where the overlay shows the model (Idan 10 Oct 2026: not at "markers only",
  // 0%), not while a tool takes the photo's clicks, and not on a panorama marker (that press moves
  // you to its photo).
  const handlePanoramaOverlayShieldDoubleClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    stopOverlayPointerEvent(event);
    const modelShown = isPanorama3DOverlayVisible && !isPanoramaMarkersOnly;
    if (!modelShown || isAutodeskPhotoClickToolActive || event.button !== 0) return;
    if (Date.now() - lastMarkerPressAtRef.current < 1000) return;
    const target = find3DOverlayTargetUnderShield(event.currentTarget, event.clientX, event.clientY);
    if (!target) return;
    target.dispatchEvent(new MouseEvent('dblclick', {
      bubbles: true,
      cancelable: true,
      composed: true,
      view: window,
      detail: 2,
      screenX: event.screenX,
      screenY: event.screenY,
      clientX: event.clientX,
      clientY: event.clientY,
      ctrlKey: event.ctrlKey,
      shiftKey: event.shiftKey,
      altKey: event.altKey,
      metaKey: event.metaKey,
      button: 0,
      buttons: 0
    }));
  }, [find3DOverlayTargetUnderShield, isAutodeskPhotoClickToolActive, isPanorama3DOverlayVisible, isPanoramaMarkersOnly, stopOverlayPointerEvent]);

  // Helper to get selected phase from link data (database-stored, shared across all users).
  // Memoized on the links it reads and declared next to its only caller, so the config
  // builder below can depend on it honestly without churning on every render.
  const getSelectedPhaseForLink = useCallback((linkId: string): string | null => {
    const link = selectedAssetAccLinks.find(l => l.id === linkId);
    return link?.selected_viewable_guid || null;
  }, [selectedAssetAccLinks]);

  const buildAutodeskViewerConfigFromLink = useCallback((link: ACCLink) => {
    let levels: any[] = [];

    try {
      const levelsJson = link.levels_json || [];
      const parsedLevels = typeof levelsJson === 'string' ? JSON.parse(levelsJson) : levelsJson;
      levels = Array.isArray(parsedLevels) ? parsedLevels : [];
    } catch {
      levels = [];
    }

    const selectedLevel = link.default_level
      ? levels.find((level: any) => level.name === link.default_level)
      : null;

    return {
      urn: link.viewable_urn!,
      connectionId: link.acc_connection_id,
      assetId: selectedAsset?.id || '',
      viewables: accService.getViewables(link),
      fileName: link.acc_file_name,
      defaultLevelName: link.default_level,
      defaultLevelElevation: selectedLevel?.elevation ?? null,
      initialViewableGuid: getSelectedPhaseForLink(link.id),
      accRegion: link.acc_region || undefined,
      roomSourceViewableGuid: link.room_source_viewable_guid || undefined
    };
    // selectedAssetAccLinks is reached through getSelectedPhaseForLink, which is memoized on it —
    // this rebuilds on exactly the same changes as before.
  }, [selectedAsset?.id, getSelectedPhaseForLink]);

  // Toggle = thin composition over the usePanorama3DOverlay state machine. OFF is keyed on
  // the STORED flag (stored intent — always able to clear, even mid-wedge). The ON path
  // resolves WHICH source can host the overlay and performs the viewer-activation side
  // effects (config building, viewer visibility) that App owns, then hands the lifecycle
  // to the hook via openPanorama3DOverlay(source).
  const handlePanorama3DOverlayToggle = useCallback(() => {
    if (showPanorama3DOverlay) {
      closePanorama3DOverlay();
      return;
    }

    if (autodeskViewerConfig) {
      setShowAutodeskViewer(true);
      setHasRenderedAutodeskViewer(true);
      openPanorama3DOverlay('autodesk');
      return;
    }

    const readyAutodeskLink = selectedAssetAccLinks.find(link =>
      accService.isViewerReady(link) && !!link.viewable_urn
    );

    if (readyAutodeskLink?.viewable_urn) {
      setAutodeskViewerConfig(buildAutodeskViewerConfigFromLink(readyAutodeskLink));
      setShowAutodeskViewer(true);
      setHasRenderedAutodeskViewer(true);
      setShow3DViewer(false);
      openPanorama3DOverlay('autodesk');
      return;
    }

    const localRvt = getPrimaryLocalRvtFile(selectedAsset);
    const localRvtMetadata = localRvt?.metadata;

    if (localRvtMetadata?.rvt_translation_status === 'ready' && localRvtMetadata?.viewable_urn) {
      setAutodeskViewerConfig(getLocalRvtViewerConfig(localRvt, selectedAsset?.id || ''));
      setShowAutodeskViewer(true);
      setHasRenderedAutodeskViewer(true);
      setShow3DViewer(false);
      openPanorama3DOverlay('autodesk');
      return;
    }

    if (hasIFCFile) {
      setShowAutodeskViewer(false);
      setShow3DViewer(true);
      setHasRendered3DViewer(true);
      openPanorama3DOverlay('ifc');
      return;
    }

    showErrorToast('No ready 3D model is available for this panorama yet.');
  }, [
    autodeskViewerConfig,
    buildAutodeskViewerConfigFromLink,
    closePanorama3DOverlay,
    getLocalRvtViewerConfig,
    hasIFCFile,
    openPanorama3DOverlay,
    showPanorama3DOverlay,
    selectedAsset,
    selectedAssetAccLinks,
    setHasRendered3DViewer,
    setHasRenderedAutodeskViewer,
    setShow3DViewer,
    setShowAutodeskViewer
  ]);


  useEffect(() => {
    setCurrentPanorama3DAnchor(null);
    setTargetPanoramaSetup(undefined);
    setPanoramaNavigationRequest(null);
    closePanorama3DOverlay();
    pendingPanoramaNavigationStateRef.current = null;
    setClosestAutodeskPanoramaTarget(null);
    setVisiblePanoramaIndices(null);
    setVisiblePanoramaKeys(null);
    setCurrentPanoramaStorey(null);
    setPanoramaViewerMountKey(0);
    (window as any).__mabat3dClosestPanoramaNavigationTarget = null;
  }, [selectedAsset?.id, closePanorama3DOverlay]);

  // POI and Location Search functionality — state, debounced /search fetch and the
  // input handler live in useAssetSearch (F-2); result-click JSX uses the setters.
  const {
    searchTerm, setSearchTerm,
    searchResults,
    showSearchResults, setShowSearchResults,
    searchLoading,
    searchError,
    handleSearchChange,
  } = useAssetSearch({ selectedAssetId: selectedAsset?.id });
  // All loaded POIs (raw API data for matching in viewers)
  const [loadedPOIs, setLoadedPOIs] = useState<any[]>([]);
  const [focusedLocationId, setFocusedLocationId] = useState<string | null>(null);
  const [currentPanoramaIndex, setCurrentPanoramaIndex] = useState(0);
  const [panoramaViewMode, setPanoramaViewMode] = useState<'flat' | 'cube'>('flat');

  // Minimap image sharing between 2D and 3D viewers
  const [minimapImageData, setMinimapImageData] = useState<string | null>(null);
  const [showMinimap, setShowMinimap] = useState(true);

  // Track if IFC viewer search panel is open (for minimap positioning)
  const [ifcSearchPanelOpen, setIfcSearchPanelOpen] = useState(false);
  const [ifcSearchPanelHeight, setIfcSearchPanelHeight] = useState('min(70%, 350px)');

  // Location hierarchy for breadcrumb display — fetch + tree processing live in
  // useLocationHierarchy (F-2); read-only here.
  const { locationHierarchy } = useLocationHierarchy({ selectedAssetId: selectedAsset?.id, isAuthenticated });

  // Track which location was navigated to (for breadcrumb)
  const [navigatedLocationId, setNavigatedLocationId] = useState<string | null>(null);

  const publicInviteToken = useMemo(() => {
    if (typeof window === 'undefined') return null;
    const match = window.location.pathname.match(/^\/invite\/([^/]+)\/?$/);
    return match ? decodeURIComponent(match[1]) : null;
  }, []);
  /**
   * Who sees the onboarding surfaces. project_onboarding_manager was retired by migration
   * 069 — it read every site in the product and could not upload a model to any of them.
   * Onboarding is now an assignment on a session, so the question is "do you have any?",
   * which GET /api/onboarding/projects already answers: the server scopes that list to
   * admins, session creators and assignees, and returns [] for everyone else.
   */
  const [hasOnboardingWork, setHasOnboardingWork] = useState(false);
  useEffect(() => {
    if (!isAuthenticated || isAdmin) return;
    let cancelled = false;
    apiService.whenAuthReady()
      .then(() => apiService.get('/onboarding/projects'))
      .then((sessions: any) => { if (!cancelled) setHasOnboardingWork(Array.isArray(sessions) && sessions.length > 0); })
      .catch(() => { if (!cancelled) setHasOnboardingWork(false); });
    return () => { cancelled = true; };
  }, [isAuthenticated, isAdmin]);
  const isOnboardingManager = isAdmin || hasOnboardingWork;
  const [viewAsOpen, setViewAsOpen] = useState(false);
  // WHAT'S NEW: opens by itself once per person per release (constants/whatsNew.ts decides
  // when), and on demand from the profile menu. Dismissing records the release in the person's
  // server-side preferences, so it does not come back on another browser.
  const [whatsNewSeen, setWhatsNewSeen, whatsNewPrefsReady] = useUserPreference<string | null>(WHATS_NEW_SEEN_PREF, null);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  // Never over an app the browser regression suite is driving: its runner page carries a URL
  // marker, and the TARGET window it drives is always named mabat3d_browser_regression… (a modal
  // there would sit on top of every element the suite tries to click).
  const isRegressionTargetWindow = typeof window !== 'undefined' && String(window.name || '').startsWith('mabat3d_browser_regression');
  const whatsNewDue = isAuthenticated && !isBrowserRegressionRunnerPage && !isRegressionTargetWindow && shouldShowWhatsNew({
    preferencesReady: whatsNewPrefsReady,
    seenReleaseId: whatsNewSeen,
    userCreatedAt: authUser?.createdAt,
    viewingAsSomeoneElse: !!viewAs
  });
  const closeWhatsNew = useCallback(() => {
    setWhatsNewOpen(false);
    if (whatsNewSeen !== WHATS_NEW_RELEASE.id && !viewAs) setWhatsNewSeen(WHATS_NEW_RELEASE.id);
  }, [whatsNewSeen, viewAs, setWhatsNewSeen]);
  /** Whether the open ticket dialog actually made a ticket — read when it closes. */
  const ticketCreatedRef = useRef(false);

  /** What to call this person HERE — on the site currently selected. */
  const badgeRole = useMemo(() => {
    if (isAdmin) return { cls: 'admin', labelKey: 'headerRoleAdmin' };
    const here = (r: { role_id: string; site_id?: string | null }) =>
      !r.site_id || r.site_id === selectedAsset?.id;
    if (roles.some((r) => r.role_id === 'site_admin' && here(r))) return { cls: 'site-admin', labelKey: 'headerRoleSiteAdmin' };
    if (roles.some((r) => r.role_id === 'data_creator' && here(r))) return { cls: 'user', labelKey: 'headerRoleDataCreator' };
    return { cls: 'user', labelKey: 'headerRoleUser' };
  }, [isAdmin, roles, selectedAsset?.id]);

  // Initialize with the selected asset from AssetService
  useEffect(() => {
    // Only load assets after user is authenticated
    if (!isAuthenticated) return;

    const initializeSelectedAsset = async () => {
      const serviceSelectedAsset = await assetService.getSelectedAsset();
      if (serviceSelectedAsset) {
        setSelectedAsset(serviceSelectedAsset);
        setPreviousAssetId(serviceSelectedAsset.id);  // Sync previousAssetId to prevent viewer reset on Sites navigation
        loadPOIsForAsset(serviceSelectedAsset);
      } else {
        // Only if no asset is selected in service, load all assets and default to first
        const allAssets = await assetService.getAssets();
        const defaultAsset = allAssets[0];
        if (defaultAsset) {
          assetService.selectAsset(defaultAsset.id);
          setSelectedAsset(defaultAsset);
          setPreviousAssetId(defaultAsset.id);  // Sync previousAssetId to prevent viewer reset on Sites navigation
          loadPOIsForAsset(defaultAsset);
        }
      }
    };
    initializeSelectedAsset();
    // Boot-time fetch: it must fire once when auth becomes ready, never again — loadPOIsForAsset
    // is declared below and cannot be named here without a TDZ crash at render time anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);


  // Reset panorama index when selected asset changes
  useEffect(() => {
    setCurrentPanoramaIndex(0);
  }, [selectedAsset?.id]);

  // Callback to refresh ACC links - can be called by child components after sync
  const refreshAccLinks = useCallback(() => {
    if (selectedAsset?.id) {
      logger.debug('[DEBUG] Refreshing ACC links for asset:', selectedAsset.id);
      accService.getLinks(selectedAsset.id)
        .then(links => {
          logger.debug('[DEBUG] Refreshed ACC links from backend:', links.map(l => ({
            id: l.id,
            fileName: l.acc_file_name,
            urn: l.viewable_urn,
            syncStatus: l.sync_status,
            conversionType: l.conversion_type,
            createdAt: l.created_at
          })));
          setSelectedAssetAccLinks(links);
        })
        .catch((err) => {
          console.error('[DEBUG] Failed to refresh ACC links:', err);
        });
    }
  }, [selectedAsset?.id]);

  // Load ACC links when selected asset changes (for viewer-ready detection)
  useEffect(() => {
    if (selectedAsset?.id) {
      logger.debug('[DEBUG] Fetching ACC links for asset:', selectedAsset.id);
      // Clear stale state immediately before fetching fresh data
      setSelectedAssetAccLinks([]);
      const linksAssetId = selectedAsset.id;
      accService.getLinks(linksAssetId)
        .then(links => {
          logger.debug('[DEBUG] Fetched ACC links from backend:', links.map(l => ({
            id: l.id,
            fileName: l.acc_file_name,
            urn: l.viewable_urn,
            syncStatus: l.sync_status,
            conversionType: l.conversion_type,
            createdAt: l.created_at
          })));
          setSelectedAssetAccLinks(links);
          setAccLinksAssetId(linksAssetId);
        })
        .catch((err) => {
          console.error('[DEBUG] Failed to fetch ACC links:', err);
          setSelectedAssetAccLinks([]);
          setAccLinksAssetId(linksAssetId);
        });
    } else {
      logger.debug('[DEBUG] No asset selected, clearing ACC links');
      setSelectedAssetAccLinks([]);
    }
  }, [selectedAsset?.id]);



  // REMOVED: Redundant useEffect that was calling getSelectedAsset() without auth guard
  // This was causing 401 errors on refresh because it ran BEFORE token was ready
  // The effect at line 155-176 already handles this with proper isAuthenticated guard

  // Auto-trigger 3D viewer when entering viewer tab if in 3D mode
  useEffect(() => {
    if (activeMenuItem === 'viewer' && currentViewMode === '3D' && selectedAsset?.files?.ifc?.length) {
      setShow3DViewer(true);
      setHasRendered3DViewer(true);
    }
  }, [activeMenuItem, currentViewMode, selectedAsset, setHasRendered3DViewer, setShow3DViewer]);

  // Poll for local RVT translation completion
  useEffect(() => {
    if (!selectedAsset?.id) return;
    const translatingLocalRvts = getLocalRvtFiles(selectedAsset)
      .filter((localRvt) => localRvt?.metadata?.rvt_translation_status === 'translating');
    if (translatingLocalRvts.length === 0) return;

    logger.debug('[App.tsx] Polling RVT translation status for files:', translatingLocalRvts.map(rvt => rvt.id));
    const intervals = translatingLocalRvts.map((localRvt) => {
      const interval = setInterval(async () => {
        try {
          const status = await apiService.get(`/assets/${selectedAsset.id}/files/${localRvt.id}/rvt-status`);
          if (status.status === 'ready' || status.status === 'failed') {
            clearInterval(interval);
            // Refresh asset data to pick up the updated metadata
            const updatedAsset = await apiService.get(`/assets/${selectedAsset.id}`);
            setSelectedAsset(updatedAsset);
            if (status.status === 'ready') {
              logger.debug('[App.tsx] RVT translation complete - viewer will auto-trigger');
            } else {
              console.error('[App.tsx] RVT translation failed:', status.error);
            }
          }
        } catch (err) {
          console.error('[App.tsx] RVT status poll error:', err);
        }
      }, 10000);
      return interval;
    }); // Poll every 10 seconds

    return () => intervals.forEach(clearInterval);
    // Narrowed on purpose: re-running tears down and restarts the 10s poll timers, so it keys
    // on the id/files the poll actually reads rather than on selectedAsset object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAsset?.id, selectedAsset?.files]);


  // Global POI loading function. Memoized on the POI service singleton alone (everything
  // else it touches is a setState setter or a module import), so it is stable for the life
  // of the component and callers can depend on it without churning.
  const loadPOIsForAsset = useCallback(async (asset: Asset) => {
    if (!asset?.id) return;

    try {
      logger.debug('Loading POIs globally for asset:', asset.id);

      // Import apiService dynamically to avoid circular imports
      const { apiService } = await import('./services/ApiService');

      // Fetch POIs from API — apiService.get returns the parsed body directly and
      // /pois responds with a BARE ARRAY (assetPois.js res.json(pois)). The old
      // `response.data` here was an axios-era leftover that made loadedPOIs
      // permanently [] — silently killing the viewer prop's direct consumers
      // (ElementInfoPanel associatedPOI, 3D-element→ticket poiId capture).
      const response = await apiService.get(`/assets/${asset.id}/pois`);
      const poisData = Array.isArray(response) ? response : (response?.pois ?? []);

      // Store raw POI data for viewer matching
      setLoadedPOIs(poisData);

      // Clear existing POIs and add new ones
      globalPOIService.clearAllPOIs();
      poisData.forEach((apiPoi: any) => {
        // Transform API POI data to POI service format
        const poi: POI = {
          id: apiPoi.poi_id || apiPoi.id.toString(),
          name: apiPoi.title || 'Unnamed Asset',
          category: apiPoi.ifc_category === 'MEP' ? POICategory.SYSTEM : POICategory.UTILITY,
          coordinates: {
            x: parseFloat(apiPoi.x) || 0,
            y: parseFloat(apiPoi.y) || 0,
            z: parseFloat(apiPoi.z) || 0
          },
          properties: {
            type: apiPoi.poi_type_name || 'Building Elements',
            subCategory: apiPoi.ifc_category || 'MEP',
            originalCategory: apiPoi.poi_group_id || 'structure',
            description: apiPoi.title,
            metadata: {
              ifcElementGuid: apiPoi.ifc_element_guid,
              ifcElementType: apiPoi.ifc_element_type,
              ifcCategory: apiPoi.ifc_category,
              source: 'ifc_conversion'
            }
          },
          visualization: {
            icon: apiPoi.icon || apiPoi.poi_type_icon || '🔧',
            color: '#007bff',
            size: 12,
            visibility: apiPoi.visibility_check !== false
          }
        };
        globalPOIService.addPOI(poi);
      });

      // Sync with layer manager so POI counts appear
      globalPOIService.syncWithLayerManager();

      logger.debug('Global POI loading complete, synced with layer manager');
    } catch (error) {
      console.warn('Failed to load POIs globally:', error);
    }
  }, [globalPOIService]);

  // Canonical "enter the viewer page" transition (Wave C-1). Every entry path must persist
  // the page for refresh-restore (the mabat3d_activePage read on boot) — three copies had
  // drifted and skipped it, so a refresh landed the user back on the previous page.
  // hideOtherViewers = POI-navigation semantics (tear down panorama/GEOTIFF).
  const enterViewerPage = useCallback((options?: {
    mode?: '3D' | 'Panorama' | '2D';
    hideOtherViewers?: boolean;
  }) => {
    if (options?.mode) {
      setCurrentViewMode(options.mode);
    }
    setActiveMenuItem('viewer');
    sessionStorage.setItem('mabat3d_activePage', 'viewer');
    if (options?.hideOtherViewers) {
      setShowWorkingE57Viewer(false);
      setShowGEOTIFFViewer(false);
    }
    trackEvent(
      options?.mode === 'Panorama' ? 'viewer.panorama.open'
        : options?.mode === '2D' ? 'viewer.2d.open'
        : 'viewer.3d.open'
    );
    markSpanStart('viewer-open');
  }, [setCurrentViewMode, setShowGEOTIFFViewer, setShowWorkingE57Viewer]);

  // Sync task queue (auto-assign on sync) — state, pending-task fetch, and the
  // apply/dismiss/reassignment pipeline live in useSyncPipeline (F-2); viewer
  // transition plumbing is injected. Placed after enterViewerPage (declaration order).
  const {
    pendingSyncTasks,
    syncPipelineRunning, setSyncPipelineRunning,
    syncPipelineStatus, setSyncPipelineStatus,
    handleDismissSyncTask,
    handleApplySyncTask,
    handleRunReassignment,
  } = useSyncPipeline({
    selectedAsset,
    isAuthenticated,
    enterViewerPage,
    setRequestedModelFileName,
    setShow3DViewer,
    setHasRendered3DViewer,
  });

  const enterPanoramaViewer = useCallback(() => {
    setShowWorkingE57Viewer(true);
    setHasRenderedE57Viewer(true);
    enterViewerPage({ mode: 'Panorama' });
  }, [enterViewerPage, setHasRenderedE57Viewer, setShowWorkingE57Viewer]);

  // Handle panorama navigation from 2D viewer, minimaps, and the top-right Panorama button.
  const handlePanoramaNavigation = useCallback((
    setupIndex: number,
    navigationTarget?: Omit<PanoramaNavigationTarget, 'setupIndex'>
  ) => {
    // Drop stale panorama-driven sync state before applying a new external navigation request.
    setCurrentPanoramaState(null);
    setCurrentPanorama3DAnchor(null);

    // Set the target setup for the panorama viewer
    setTargetPanoramaSetup(setupIndex);
    const legacyPanoramaData = selectedAsset?.files?.e57?.metadata?.panoramaData;
    if (Array.isArray(legacyPanoramaData)) {
      const legacyIndex = legacyPanoramaData.findIndex((panorama: any, index: number) => (
        Number(panorama?.scanIndex) === setupIndex ||
        Number(panorama?.panoramaIndex) === setupIndex ||
        Number(panorama?.setupIndex) === setupIndex ||
        index === setupIndex
      ));
      if (legacyIndex >= 0) {
        setCurrentPanoramaIndex(legacyIndex);
      }
    }
    const requestId = panoramaNavigationRequestIdRef.current + 1;
    panoramaNavigationRequestIdRef.current = requestId;
    pendingPanoramaNavigationStateRef.current = {
      setupIndex,
      sourceFileId: normalizePanoramaSourceFileKey(navigationTarget?.sourceFileId),
      requestId,
      expiresAt: Date.now() + 10_000
    };
    setPanoramaNavigationRequest({
      setupIndex,
      sourceFileId: navigationTarget?.sourceFileId,
      requestId,
      worldYaw: navigationTarget?.worldYaw,
      pitch: navigationTarget?.pitch,
      fov: navigationTarget?.fov
    });
    enterPanoramaViewer();
  }, [enterPanoramaViewer, selectedAsset?.files?.e57?.metadata?.panoramaData]);

  const handleClosestAutodeskPanoramaTargetChange = useCallback((target: PanoramaNavigationTarget | null) => {
    setClosestAutodeskPanoramaTarget(target);

    if (
      !target ||
      currentViewMode === 'Panorama' ||
      !selectedAsset?.files?.e57
    ) {
      return;
    }

    // Keep only the lightweight target state while the Autodesk viewer moves.
    // The panorama viewer itself is opened by explicit navigation/view-mode changes.
    setTargetPanoramaSetup(target.setupIndex);
  }, [currentViewMode, selectedAsset?.files?.e57]);

  const updateClosestIfcPanoramaTarget = useCallback((
    target: { setupIndex: number; sourceFileId: number | string | null } | null,
    source: 'raw-camera' | 'geotiff-minimap'
  ) => {
    if (!target) {
      if (closestIfcPanoramaTargetSourceRef.current === source) {
        closestIfcPanoramaTargetRef.current = null;
        closestIfcPanoramaTargetSourceRef.current = 'none';
        setClosestIfcPanoramaTarget(null);
      }
      return;
    }

    const previous = closestIfcPanoramaTargetRef.current;
    if (
      previous &&
      previous.setupIndex === target.setupIndex &&
      String(previous.sourceFileId ?? null) === String(target.sourceFileId ?? null) &&
      closestIfcPanoramaTargetSourceRef.current === source
    ) {
      return;
    }

    closestIfcPanoramaTargetRef.current = target;
    closestIfcPanoramaTargetSourceRef.current = source;
    setClosestIfcPanoramaTarget(target);
  }, []);

  const handleIfcCameraWorldPositionChange = useCallback((position: {
    x: number;
    y: number;
    z: number;
    worldYaw?: number;
    pitch?: number;
    fov?: number;
  }) => {
    ifcCameraWorldPositionRef.current = position;

    if (closestIfcPanoramaTargetSourceRef.current === 'geotiff-minimap') {
      return;
    }

    // Live closest-panorama tracking (mirrors handleClosestAutodeskPanoramaTargetChange):
    // highlight the nearest scan on the 3D minimap and pre-seed the panorama viewer.
    const locations = panoramaStationLocationsRef.current;
    if (locations.length === 0) {
      if (!ifcClosestNoLocationsWarnedRef.current) {
        ifcClosestNoLocationsWarnedRef.current = true;
        logger.debug('🧭 IFC closest-pano: camera reporting but panorama locations not loaded yet');
      }
      return;
    }
    ifcClosestNoLocationsWarnedRef.current = false;

    const panoramaSpaceCamera = { x: position.x, y: -position.z };
    let closest: typeof locations[number] | null = null;
    let closestDistanceSq = Infinity;
    for (const location of locations) {
      const dx = location.x - panoramaSpaceCamera.x;
      const dy = location.y - panoramaSpaceCamera.y;
      const distanceSq = dx * dx + dy * dy;
      if (distanceSq < closestDistanceSq) {
        closestDistanceSq = distanceSq;
        closest = location;
      }
    }
    if (!closest) return;

    const previous = closestIfcPanoramaTargetRef.current;
    if (
      previous &&
      previous.setupIndex === closest.setupIndex &&
      String(previous.sourceFileId ?? null) === String(closest.sourceFileId ?? null)
    ) {
      return;
    }

    // Note: deliberately NOT calling setTargetPanoramaSetup here — it mounts
    // the hidden panorama viewer and triggers its navigation effect, which
    // emits a position+rotation sync that would teleport the 3D camera.
    // Switch-time selection goes through handlePanoramaNavigation instead.
    const target = { setupIndex: closest.setupIndex, sourceFileId: closest.sourceFileId };
    updateClosestIfcPanoramaTarget(target, 'raw-camera');
    logger.debug('🧭 IFC closest panorama:', target, 'anchor:', {
      x: Number(position.x.toFixed(2)),
      z: Number(position.z.toFixed(2))
    });
  }, [updateClosestIfcPanoramaTarget]);

  const handleIfcMinimapClosestPanoramaTargetChange = useCallback((
    target: { setupIndex: number; sourceFileId?: number | string | null } | null
  ) => {
    updateClosestIfcPanoramaTarget(
      target
        ? { setupIndex: target.setupIndex, sourceFileId: target.sourceFileId ?? null }
        : null,
      'geotiff-minimap'
    );
  }, [updateClosestIfcPanoramaTarget]);

  // Load the site's per-scan panorama stations. Two consumers, two scopes:
  //  - the IFC flow uses them to open the scan closest to the 3D camera (Autodesk loads its
  //    own copy, so closest-tracking state below stays IFC-only and is cleared otherwise);
  //  - the Browser pane's "Jump to nearest station" needs them in EVERY flow, which is why the
  //    fetch itself is gated only on "this site has panoramas".
  useEffect(() => {
    let cancelled = false;

    if (!hasIFCFile || usesAutodeskPanoramaFlow) {
      closestIfcPanoramaTargetRef.current = null;
      closestIfcPanoramaTargetSourceRef.current = 'none';
      setClosestIfcPanoramaTarget(null);
    }

    if (!selectedAsset?.id || !selectedAsset?.files?.e57) {
      panoramaStationLocationsRef.current = [];
      setPanoramaStationLocations([]);
      return;
    }

    (async () => {
      try {
        const response = await apiService.get<any>(`/assets/${selectedAsset.id}/panorama-index`);
        if (cancelled) return;
        const rows = Array.isArray(response) ? response : response?.panoramas;
        if (!Array.isArray(rows)) return;
        const seen = new Set<string>();
        const locations: Array<{ setupIndex: number; sourceFileId: number | string | null; x: number; y: number }> = [];
        for (const row of rows) {
          const setupIndex = Number(row?.scanIndex);
          const x = Number(row?.position?.x);
          const y = Number(row?.position?.y);
          if (!Number.isFinite(setupIndex) || !Number.isFinite(x) || !Number.isFinite(y)) continue;
          const key = `${row?.sourceFileId ?? 'legacy'}:${setupIndex}`;
          if (seen.has(key)) continue;
          seen.add(key);
          locations.push({ setupIndex, sourceFileId: row?.sourceFileId ?? null, x, y });
        }
        panoramaStationLocationsRef.current = locations;
        setPanoramaStationLocations(locations);
        logger.debug('[App.tsx] Loaded panorama stations for this site:', locations.length);
      } catch (error) {
        console.warn('[App.tsx] Failed to load the panorama station index:', error);
        if (!cancelled) {
          panoramaStationLocationsRef.current = [];
          closestIfcPanoramaTargetRef.current = null;
          closestIfcPanoramaTargetSourceRef.current = 'none';
          setPanoramaStationLocations([]);
          setClosestIfcPanoramaTarget(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedAsset?.id, selectedAsset?.files?.e57, hasIFCFile, usesAutodeskPanoramaFlow]);

  const handleViewModeChange = useCallback(async (mode: '3D' | '2D' | 'Panorama') => {
    if (activeMenuItem === 'viewer' && mode !== currentViewMode && !viewerModeReady[mode]) {
      beginViewerBlock(mode);
    }

    if (mode === 'Panorama') {
      logger.debug('[ViewModeChange] Switching to Panorama. Conditions:', {
        currentViewMode,
        activeMenuItem,
        hasRVTFile,
        hasE57: !!selectedAsset?.files?.e57,
        showAutodeskViewer,
        hasRef: !!autodeskViewerRef.current
      });

      if (currentViewMode === 'Panorama') {
        return;
      }
    }

    if (
      mode === 'Panorama' &&
      activeMenuItem === 'viewer' &&
      selectedAsset?.files?.e57 &&
      usesAutodeskPanoramaFlow
    ) {
      const runtimePanoramaTarget = (window as any).__mabat3dClosestPanoramaNavigationTarget as PanoramaNavigationTarget | null | undefined;
      let panoramaNavigationTarget = closestAutodeskPanoramaTarget ?? runtimePanoramaTarget ?? null;

      try {
        logger.debug('[ViewModeChange] All conditions met, resolving marked panorama...', {
          cachedTarget: closestAutodeskPanoramaTarget,
          runtimeTarget: runtimePanoramaTarget,
          hasRef: !!autodeskViewerRef.current
        });
        const markedNavigationTarget = autodeskViewerRef.current?.getMarkedPanoramaNavigationTarget();
        panoramaNavigationTarget = markedNavigationTarget ?? panoramaNavigationTarget;

        if (!panoramaNavigationTarget) {
          const liveNavigationTarget = await autodeskViewerRef.current?.getClosestPanoramaNavigationTarget();
          panoramaNavigationTarget = liveNavigationTarget ?? panoramaNavigationTarget;
        }

        logger.debug('[ViewModeChange] Navigation target:', panoramaNavigationTarget);
      } catch (error) {
        console.warn('[App.tsx] Failed to resolve closest panorama before entering Panorama mode:', error);
      }

      if (panoramaNavigationTarget) {
        handlePanoramaNavigation(panoramaNavigationTarget.setupIndex, {
          sourceFileId: panoramaNavigationTarget.sourceFileId,
          worldYaw: panoramaNavigationTarget.worldYaw,
          pitch: panoramaNavigationTarget.pitch,
          fov: panoramaNavigationTarget.fov
        });
        return;
      }

      console.warn('[ViewModeChange] No closest panorama target available — opening Panorama without an Autodesk target');
      enterPanoramaViewer();
      return;
    }

    // IFC flow: open the panorama scan closest to the 3D camera. The GeoTIFF
    // minimap is preferred because it uses the same projected coordinates the
    // user sees; raw WebIFC camera coordinates remain only as a pre-minimap fallback.
    if (
      mode === 'Panorama' &&
      activeMenuItem === 'viewer' &&
      selectedAsset?.files?.e57 &&
      !usesAutodeskPanoramaFlow &&
      hasIFCFile &&
      closestIfcPanoramaTargetRef.current
    ) {
      const closest = closestIfcPanoramaTargetRef.current;
      const cameraPose = ifcCameraWorldPositionRef.current;
      logger.debug('[ViewModeChange] IFC closest panorama selected:', closest);
      handlePanoramaNavigation(closest.setupIndex, {
        sourceFileId: closest.sourceFileId,
        worldYaw: cameraPose?.worldYaw,
        pitch: cameraPose?.pitch,
        fov: cameraPose?.fov
      });
      return;
    }

    if (mode === 'Panorama') {
      enterPanoramaViewer();
      return;
    }

    if (showPanorama3DOverlay) { // mode === 'Panorama' already returned above
      closePanorama3DOverlay();
    }

    if (
      mode === '3D' &&
      currentViewMode === 'Panorama' &&
      activeMenuItem === 'viewer' &&
      hasIFCFile &&
      !usesAutodeskPanoramaFlow &&
      currentPanoramaState?.position &&
      currentPanoramaState?.rotation
    ) {
      const nextKey = pendingIfcPanoramaPoseSyncKeyRef.current + 1;
      pendingIfcPanoramaPoseSyncKeyRef.current = nextKey;
      setPendingIfcPanoramaPoseSync({
        key: nextKey,
        state: {
          ...currentPanoramaState,
          position: { ...currentPanoramaState.position },
          rotation: { ...currentPanoramaState.rotation },
          syncType: 'position+rotation'
        }
      });
    }

    setCurrentViewMode(mode);

    if (mode === '3D' && activeMenuItem === 'viewer') {
      setShow3DViewer(true);
      setHasRendered3DViewer(true);
    }
  }, [
    activeMenuItem,
    beginViewerBlock,
    closePanorama3DOverlay,
    closestAutodeskPanoramaTarget,
    currentViewMode,
    enterPanoramaViewer,
    handlePanoramaNavigation,
    hasIFCFile,
    hasRVTFile,
    currentPanoramaState,
    selectedAsset?.files?.e57,
    showAutodeskViewer,
    showPanorama3DOverlay,
    usesAutodeskPanoramaFlow,
    viewerModeReady,
    setCurrentViewMode,
    setHasRendered3DViewer,
    setShow3DViewer
  ]);

  const handleAutodeskInteractionReady = useCallback((state: { mode: ViewerMode; reason: string }) => {
    // Every model has settled (seen from 3D or from Panorama), or the viewer failed while in
    // Panorama — the second thing the Panorama loading screen waits for. The gate effect decides.
    if (state.mode === 'Panorama' || state.reason === 'autodesk-all-models-loaded') {
      setPanoramaModelsReadyFor(selectedAssetIdRef.current);
    }
    if (state.mode === 'Panorama') {
      return;
    }
    if (state.mode === currentViewModeRef.current && (state.mode === '3D' || state.mode === '2D')) {
      finishViewerBlock(state.mode);
      // P3 perf span: enterViewerPage marks 'viewer-open'; this is the moment
      // the viewer became actually usable. Fire-and-forget.
      trackSpanEnd('perf.viewer-interactive', 'viewer-open', { props: { mode: state.mode, reason: state.reason } });
    }
  }, [finishViewerBlock, currentViewModeRef]);

  // Canonical ready-ACC-link resolution for the navigation family (Wave C-1): exact
  // file-name match, and the ready-.rvt fallback used when the exact match misses.
  // The auto-trigger effect and panorama-overlay toggle deliberately accept links of ANY
  // file type — those selectors are different intents and stay separate.
  // Memoized on selectedAssetAccLinks — the only state all three read — so the navigation
  // callbacks below can list them as dependencies without being rebuilt on every render.
  const findReadyLinkByFileName = useCallback((fileName?: string | null): ACCLink | undefined =>
    fileName
      ? selectedAssetAccLinks.find(link =>
          accService.isViewerReady(link) && link.acc_file_name === fileName
        )
      : undefined, [selectedAssetAccLinks]);

  const findReadyRvtLink = useCallback((preferredFileName?: string | null): ACCLink | undefined =>
    findReadyLinkByFileName(preferredFileName) ||
    selectedAssetAccLinks.find(link =>
      accService.isViewerReady(link) && link.acc_file_name?.toLowerCase().endsWith('.rvt')
    ), [findReadyLinkByFileName, selectedAssetAccLinks]);

  const isRVTPoiForViewerNavigation = useCallback((poi: import('./types').POI): boolean => {
    const rawPOI = poi as any;
    let customData = rawPOI.custom_data;

    if (typeof customData === 'string') {
      try {
        customData = JSON.parse(customData);
      } catch {
        customData = undefined;
      }
    }

    const sourceFile =
      rawPOI.ifc_source_file ||
      rawPOI.source_file ||
      rawPOI.rvt_source_file ||
      rawPOI.properties?.location ||
      rawPOI.properties?.source_file ||
      customData?.originalFileName ||
      customData?.rvt?.originalFileName ||
      customData?.rvt?.sourceFile;

    const matchesReadyRvtLink = typeof sourceFile === 'string' && selectedAssetAccLinks.some(link =>
      accService.isViewerReady(link) && link.acc_file_name === sourceFile
    );

    return Boolean(
      matchesReadyRvtLink ||
      rawPOI.properties?.isCustomRoom ||
      rawPOI.rvt_element_db_id ||
      rawPOI.rvt_element_dbid ||
      customData?.rvt ||
      customData?.rvtElementDbId ||
      (typeof sourceFile === 'string' && sourceFile.toLowerCase().endsWith('.rvt'))
    );
  }, [selectedAssetAccLinks]);

  const continuePOINavigation = (navigationPOI: import('./types').POI) => {
    const normalizePOIForViewerNavigation = (poi: import('./types').POI): import('./types').POI => {
      const normalizedPOI = { ...(poi as any) } as any;
      let customData = normalizedPOI.custom_data;

      if (typeof customData === 'string') {
        try {
          customData = JSON.parse(customData);
        } catch {
          customData = undefined;
        }
      }

      if (!customData || typeof customData !== 'object') {
        customData = {};
      }

      const directRvtDbId =
        normalizedPOI.rvt_element_db_id ||
        normalizedPOI.rvt_element_dbid ||
        customData?.rvtElementDbId ||
        customData?.rvt?.rvtElementDbId;

      const directSourceFile =
        normalizedPOI.ifc_source_file ||
        normalizedPOI.source_file ||
        normalizedPOI.rvt_source_file ||
        normalizedPOI.properties?.location ||
        normalizedPOI.properties?.source_file ||
        customData?.originalFileName ||
        customData?.rvt?.originalFileName ||
        customData?.rvt?.sourceFile;

      const fallbackReadyRvtFile = findReadyRvtLink()?.acc_file_name;

      const hasRvtIndicators = Boolean(
        normalizedPOI.properties?.isCustomRoom ||
        customData?.rvt ||
        directRvtDbId ||
        (typeof directSourceFile === 'string' && directSourceFile.toLowerCase().endsWith('.rvt'))
      );

      if (!hasRvtIndicators) {
        return normalizedPOI;
      }

      const effectiveSourceFile = directSourceFile || fallbackReadyRvtFile;
      const effectiveDbId = directRvtDbId ? Number(directRvtDbId) : undefined;

      normalizedPOI.ifc_source_file = normalizedPOI.ifc_source_file || effectiveSourceFile;
      normalizedPOI.source_file = normalizedPOI.source_file || effectiveSourceFile;
      normalizedPOI.custom_data = {
        ...customData,
        rvt: {
          ...(customData?.rvt || {}),
          ...(effectiveDbId ? { rvtElementDbId: effectiveDbId } : {}),
          ...(effectiveSourceFile ? { originalFileName: effectiveSourceFile } : {})
        }
      };

      return normalizedPOI as import('./types').POI;
    };

    const normalizedNavigationPOI = normalizePOIForViewerNavigation(navigationPOI);

    // Track navigated location for breadcrumb
    setNavigatedLocationId((normalizedNavigationPOI as any).id || null);

    // Clear any existing focused POIs first
    setFocusedPOI(null);
    setFocusedRVTPOI(null);

    // Switch to 3D viewer page, tearing down panorama/GEOTIFF (canonical transition)
    enterViewerPage({ mode: '3D', hideOtherViewers: true });

    // Source file used below for exact ready-link matching (the normalizer has already
    // injected the effective source file when RVT indicators exist)
    const poiSourceFile =
      (normalizedNavigationPOI as any).ifc_source_file ||
      (normalizedNavigationPOI as any).source_file ||
      normalizedNavigationPOI.properties?.location;

    // Canonical RVT-vs-IFC classification (Wave C-1): the SAME classifier
    // handlePOINavigation uses for the preservePanoramaCamera decision. The weaker
    // inline rules that lived here could disagree with it on edge POIs and send them
    // to the wrong viewer.
    const isRVTPOI = isRVTPoiForViewerNavigation(normalizedNavigationPOI);

    if (isRVTPOI) {
      // RVT POI - show AutodeskViewer and focus on element.
      // Exact source-file match first, then any ready .rvt link (canonical resolver).
      const readyLink = findReadyRvtLink(poiSourceFile);

      if (readyLink) {
        logger.debug('[App.tsx] RVT POI navigation - showing AutodeskViewer for:', readyLink.acc_file_name);

        // Setup AutodeskViewer config if not already set — canonical builder (safe
        // levels_json parse + populated viewables; entry paths must not drift)
        if (!autodeskViewerConfig || autodeskViewerConfig.urn !== readyLink.viewable_urn) {
          setAutodeskViewerConfig(buildAutodeskViewerConfigFromLink(readyLink));
        }

        setShowAutodeskViewer(true);
        setHasRenderedAutodeskViewer(true);
        setShow3DViewer(false); // Hide IFC viewer

        // Set the RVT POI to focus on with a minimal delay for React state updates
        setTimeout(() => {
          setFocusedRVTPOI(normalizedNavigationPOI);
        }, 100);
      } else if (autodeskViewerConfig) {
        // No ACC link found but viewer is already configured - just focus on the POI
        logger.debug('[App.tsx] RVT POI navigation - using existing AutodeskViewer config');
        setShowAutodeskViewer(true);
        setHasRenderedAutodeskViewer(true);
        setShow3DViewer(false);

        setTimeout(() => {
          setFocusedRVTPOI(normalizedNavigationPOI);
        }, 100);
      } else {
        console.warn('[App.tsx] RVT POI navigation failed - no ready ACC link found');
      }
    } else {
      // IFC POI - show WebIfcViewer
      setShow3DViewer(true);

      // Set the POI to focus on with a minimal delay for React state updates
      setTimeout(() => {
        setFocusedPOI(normalizedNavigationPOI);
      }, 100);
    }
  };

  // Handle POI navigation from Systems page
  const handlePOINavigation = (
    poi: import('./types').POI,
    options?: { preservePanoramaCamera?: boolean }
  ) => {
    trackEvent('poi.focus', { assetId: selectedAsset?.id });
    const shouldPreservePanoramaCamera =
      !!options?.preservePanoramaCamera &&
      !isRVTPoiForViewerNavigation(poi);

    const navigationPOI = shouldPreservePanoramaCamera
      ? ({ ...(poi as any), preservePanoramaCamera: true } as import('./types').POI)
      : poi;

    // Force a fresh panorama->3D sync payload so the 3D viewer keeps the exact panorama camera pose.
    if (shouldPreservePanoramaCamera && currentPanoramaState?.position && currentPanoramaState?.rotation) {
      setCurrentPanoramaState(prev => {
        if (!prev?.position || !prev?.rotation) return prev;
        return {
          ...prev,
          position: { ...prev.position },
          rotation: { ...prev.rotation },
          syncType: 'position+rotation'
        };
      });

      const shouldWaitForPanoramaAnchor =
        currentViewMode === 'Panorama' &&
        Boolean(selectedAsset?.files?.geotiff) &&
        !currentPanorama3DAnchor;

      if (shouldWaitForPanoramaAnchor) {
        pendingPanoramaPOIRef.current = navigationPOI;
        setPendingPanoramaPOI(navigationPOI);

        if (pendingPanoramaPOITimerRef.current !== null) {
          window.clearTimeout(pendingPanoramaPOITimerRef.current);
        }

        pendingPanoramaPOITimerRef.current = window.setTimeout(() => {
          const queuedPOI = pendingPanoramaPOIRef.current;
          if (!queuedPOI) return;

          pendingPanoramaPOIRef.current = null;
          pendingPanoramaPOITimerRef.current = null;
          setPendingPanoramaPOI(null);
          continuePOINavigation(queuedPOI);
        }, 350);

        return;
      }
    }

    continuePOINavigation(navigationPOI);
  };

  useEffect(() => {
    if (!pendingPanoramaPOI || !currentPanorama3DAnchor) {
      return;
    }

    if (pendingPanoramaPOITimerRef.current !== null) {
      window.clearTimeout(pendingPanoramaPOITimerRef.current);
      pendingPanoramaPOITimerRef.current = null;
    }

    const queuedPOI = pendingPanoramaPOI;
    pendingPanoramaPOIRef.current = null;
    setPendingPanoramaPOI(null);
    continuePOINavigation(queuedPOI);
    // Drains a queued navigation the moment the anchor arrives — it must fire on that pair
    // changing, not whenever continuePOINavigation (re-created every render) changes identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPanoramaPOI, currentPanorama3DAnchor]);

  useEffect(() => {
    return () => {
      if (pendingPanoramaPOITimerRef.current !== null) {
        window.clearTimeout(pendingPanoramaPOITimerRef.current);
      }
    };
  }, []);

  // Handle POI group highlighting from Systems page
  const handleHighlightGroup = useCallback((pois: import('./types').POI[], color?: string) => {
    const hasRVTPois = pois.some(isRVTPoiForViewerNavigation);
    const hasIFCPois = pois.some(poi => !isRVTPoiForViewerNavigation(poi));

    // Clear any single-POI focus
    setFocusedPOI(null);
    setFocusedRVTPOI(null);

    // Switch to 3D viewer page, tearing down panorama/GEOTIFF (canonical transition)
    enterViewerPage({ mode: '3D', hideOtherViewers: true });

    if (hasRVTPois && !hasIFCPois) {
      const rvtSourceFiles = pois
        .map((poi: any) =>
          poi?.ifc_source_file ||
          poi?.source_file ||
          poi?.rvt_source_file ||
          poi?.properties?.location ||
          poi?.properties?.source_file
        )
        .filter((fileName: string | undefined): fileName is string => typeof fileName === 'string' && fileName.toLowerCase().endsWith('.rvt'));

      // Exact match against each group source file first, then the canonical ready-.rvt fallback
      const readyLink = rvtSourceFiles
        .map(fileName => findReadyLinkByFileName(fileName))
        .find((link): link is ACCLink => !!link) || findReadyRvtLink();

      const canShowAutodeskViewer = Boolean(readyLink || autodeskViewerConfig);

      if (readyLink && (!autodeskViewerConfig || autodeskViewerConfig.urn !== readyLink.viewable_urn)) {
        // Canonical builder — same config shape on every entry path (Wave C-1)
        setAutodeskViewerConfig(buildAutodeskViewerConfigFromLink(readyLink));
      }

      if (canShowAutodeskViewer) {
        setShowAutodeskViewer(true);
        setHasRenderedAutodeskViewer(true);
        setShow3DViewer(false);
      } else {
        setShow3DViewer(true);
      }
    } else {
      setShow3DViewer(true);
      if (!hasRVTPois) {
        setShowAutodeskViewer(false);
      }
    }

    // Set the POI group to highlight (both viewers will pick this up)
    setHighlightedPOIGroup({ pois, color });
    // selectedAsset.id / selectedAssetAccLinks are reached through the memoized helpers below,
    // so this rebuilds on exactly the same changes as before.
  }, [
    autodeskViewerConfig,
    buildAutodeskViewerConfigFromLink,
    enterViewerPage,
    findReadyLinkByFileName,
    findReadyRvtLink,
    isRVTPoiForViewerNavigation,
    setHasRenderedAutodeskViewer,
    setShow3DViewer,
    setShowAutodeskViewer
  ]);

  const handleClearHighlightedGroup = useCallback(() => {
    setHighlightedPOIGroup(null);
    // Functional update with a same-reference bail: this is called unconditionally on every
    // Escape/clear (even with nothing selected), and returning the SAME empty array lets React
    // skip the re-render entirely instead of churning a new [] identity through every consumer.
    setViewerSelectedDbIds(prev => (prev.length === 0 ? prev : []));
  }, []);

  // Stable identity matters: this is a dependency of AutodeskViewer's clear/selection callbacks,
  // which feed the highlightedPOIGroup effect's deps. As an inline arrow it changed every render,
  // making that effect re-run (and re-arm the bulk protection tool, clobbering single selections)
  // on every App render while a group was highlighted.
  const handleClearNavigatedLocation = useCallback(() => setNavigatedLocationId(null), []);

  // Same stable-identity requirement for every other App callback that sits in an AutodeskViewer
  // effect dependency array. onFocusedPOIHandled in particular drives the focusedPOI navigation
  // effect: as an inline arrow it re-ran that heavy effect on EVERY App render while a POI was
  // focused, and since the effect itself triggers App renders (onSelectionChange with a fresh
  // array), the pair formed a self-sustaining render/effect loop that starved the main thread
  // (2026-07-02 regression: test runs crawled with exact-60s swallowed-wait timeouts).
  const handleFocusedPOIHandled = useCallback(() => setFocusedRVTPOI(null), []);
  // Navigation hook for the browser regression runner ONLY.
  // Assets / Locations / Systems were retired from the menu (their abilities moved into the
  // viewer's Browser pane) but the pages still exist and the suite still covers them. Rather than
  // keep phantom menu entries for users, expose a direct navigate for the runner window so
  // openMainMenuItemInChild can fall back to it when a menu item is intentionally absent.
  useEffect(() => {
    if (!isBrowserRegressionRunnerPage) return;
    (window as any).__mabatNavigate = (id: string) => setActiveMenuItem(id);
    return () => { delete (window as any).__mabatNavigate; };
  }, [isBrowserRegressionRunnerPage]);

  const handleLayerPanelToggle = useCallback(() => setIsLayerPaneOpen(v => !v), []);
  const handlePOIsExtracted = useCallback((count: number) => {
    logger.debug('[App.tsx] POIs extracted:', count);
  }, []);

  /** "Show in system diagram" on an asset: open the P&ID page and select that asset's node. */
  const handleShowPOIInDiagram = useCallback((poi: POI) => {
    setDiagramTargetId(null);
    setDiagramFocusPOIId(String((poi as any).id));
    setActiveMenuItem('diagrams');
  }, []);

  // ── Unified Browser pane for the App-owned viewers (Panorama + 2D) ──────────
  // Same component, same trees as the RVT/IFC panes; only the capability callbacks differ.
  // The rows are built from the SAME POI rows the viewers already loaded — aggregated first,
  // because /pois returns one row per location and the tree must see `locations[]`.
  const browserPOIs = useMemo(() => aggregatePOIRows(loadedPOIs), [loadedPOIs]);
  const {
    locationsHierarchy: browserLocationsHierarchy,
    systemsHierarchy: browserSystemsHierarchy,
  } = usePOIHierarchies(browserPOIs);

  // The Autodesk viewer's "show the asset from a panorama" (Search in Panorama), registered by it.
  const panoramaAssetFinderRef = useRef<((poi: any) => boolean) | null>(null);
  const registerPanoramaAssetFinder = useCallback((show: ((poi: any) => boolean) | null) => {
    panoramaAssetFinderRef.current = show;
  }, []);

  /**
   * Panorama pane's go-to action. On RVT sites it does what Search in Panorama does (Idan
   * 2026-10-02: "like new search"): the nearest panorama that can SEE the asset (model line of
   * sight), facing it, outlined. Where the 3D viewer cannot (IFC sites, an asset without model
   * geometry) it opens the scan station nearest to it — the same nearest-station arithmetic (and
   * station list) the IFC closest-camera tracking uses.
   *
   * It navigates through handlePanoramaNavigation rather than the viewer handle's
   * navigateToPanoramaIndex: stations are identified by SETUP index + source file (a site can
   * have several E57s), and only this route resolves that pair to the viewer's own index — plus
   * it works when the panorama viewer is not mounted yet.
   */
  const handleBrowserJumpToNearestStation = useCallback((poi: any) => {
    if (panoramaAssetFinderRef.current?.(poi)) return;
    const stations = panoramaStationLocationsRef.current;
    if (stations.length === 0) {
      showErrorToast('No panorama stations are available for this site.');
      return;
    }
    const position = readPOIPlanPosition(poi);
    if (!position) {
      showErrorToast('This asset has no position to search from.');
      return;
    }

    let closest: typeof stations[number] | null = null;
    let closestDistanceSq = Infinity;
    for (const station of stations) {
      const dx = station.x - position.x;
      const dy = station.y - position.y;
      const distanceSq = dx * dx + dy * dy;
      if (distanceSq < closestDistanceSq) {
        closestDistanceSq = distanceSq;
        closest = station;
      }
    }
    if (!closest) return;

    handlePanoramaNavigation(closest.setupIndex, { sourceFileId: closest.sourceFileId });
  }, [handlePanoramaNavigation]);

  /**
   * 2D pane's go-to action. The GeoTIFF viewer has no imperative handle — it is prop/event
   * driven — so the request travels as state and the viewer centres on it with the very
   * projection its POI markers are drawn with. requestId makes a repeat click re-centre.
   */
  const [map2DLocateTarget, setMap2DLocateTarget] = useState<{ x: number; y: number; requestId: number } | null>(null);
  const map2DLocateRequestIdRef = useRef(0);
  const handleBrowserLocateOnMap = useCallback((poi: any) => {
    const position = readPOIPlanPosition(poi);
    if (!position) {
      showErrorToast('This asset has no position to locate on the map.');
      return;
    }
    map2DLocateRequestIdRef.current += 1;
    setMap2DLocateTarget({ ...position, requestId: map2DLocateRequestIdRef.current });
  }, []);

  /** Row ticket → the one global service-call modal, same draft shape as the 3D viewers. */
  const handleBrowserCreateTicket = useCallback((poi: any) => {
    const elementName = poi?.title || poi?.name || 'Asset';
    setServiceCallDraft({
      origin: '3d_element',
      title: `${elementName} — service call`,
      context: { poiId: String(poi?.id), elementName },
    });
  }, []);

  /**
   * "Tickets for this asset" from any element surface — the row ⋯ menu, the viewer's
   * double-click menu and the Element Info panel all land here. It asks
   * GET /assets/:id/service-calls?poiId= (service_call_pois, migration 043); before that
   * junction the question had no answer at all.
   */
  const handleViewPOITickets = useCallback((poi: any) => {
    const id = Number(poi?.id);
    if (!Number.isInteger(id) || id <= 0) return;
    setPoiTicketsTarget({ id, title: poi?.title || poi?.name || `#${id}` });
  }, []);

  /** Any in-pane mutation → reload the POIs both App panes render from. */
  const handleBrowserDataChanged = useCallback(() => {
    if (selectedAsset) void loadPOIsForAsset(selectedAsset);
  }, [selectedAsset, loadPOIsForAsset]);

  // Handle starting custom room drawing from LocationsPage
  const handleStartCustomRoomDrawing = useCallback((preSelectedStoreyId?: string) => {
    setPreSelectedBottomStoreyId(preSelectedStoreyId || null);
    enterViewerPage({ mode: '3D', hideOtherViewers: true });
    setShow3DViewer(true);
    // Delay setting drawing mode to allow viewer to be visible first
    setTimeout(() => setCustomRoomDrawingMode(true), 300);
  }, [enterViewerPage, setShow3DViewer]);

  // Handle custom room drawing completion
  const handleCustomRoomDrawingComplete = useCallback((saved: boolean) => {
    setCustomRoomDrawingMode(false);
    setEditingCustomLocation(null);
    setPreSelectedBottomStoreyId(null);
    if (saved) {
      logger.debug('[App.tsx] Custom room saved successfully');
    }
  }, []);

  // Handle starting custom storey drawing from LocationsPage
  const handleStartCustomStoreyDrawing = useCallback(() => {
    enterViewerPage({ mode: '3D', hideOtherViewers: true });
    setShow3DViewer(true);
    setTimeout(() => setCustomStoreyDrawingMode(true), 300);
  }, [enterViewerPage, setShow3DViewer]);

  // Handle custom storey drawing completion
  const handleCustomStoreyDrawingComplete = useCallback((saved: boolean) => {
    setCustomStoreyDrawingMode(false);
    setEditingCustomLocation(null);
    if (saved) {
      logger.debug('[App.tsx] Custom storey saved successfully');
    }
  }, []);

  // Handle editing a custom room (switch to viewer with existing data)
  const handleEditCustomRoom = useCallback((location: any) => {
    setEditingCustomLocation(location);
    enterViewerPage({ mode: '3D', hideOtherViewers: true });
    setShow3DViewer(true);
    setTimeout(() => setCustomRoomDrawingMode(true), 300);
  }, [enterViewerPage, setShow3DViewer]);

  // Handle editing a custom storey (switch to viewer with existing data)
  const handleEditCustomStorey = useCallback((location: any) => {
    setEditingCustomLocation(location);
    enterViewerPage({ mode: '3D', hideOtherViewers: true });
    setShow3DViewer(true);
    setTimeout(() => setCustomStoreyDrawingMode(true), 300);
  }, [enterViewerPage, setShow3DViewer]);

  // Memoized callback for panorama state changes
  const handlePanoramaStateChange = useCallback((state: PanoramaViewerState) => {
    const pendingNavigation = pendingPanoramaNavigationStateRef.current;
    if (pendingNavigation) {
      if (pendingNavigation.expiresAt <= Date.now()) {
        pendingPanoramaNavigationStateRef.current = null;
      } else {
        const stateSourceFileId = normalizePanoramaSourceFileKey(state.currentSourceFileId);
        const stateMatchesPendingNavigation =
          state.currentSetupIndex === pendingNavigation.setupIndex &&
          (
            pendingNavigation.sourceFileId === null ||
            stateSourceFileId === pendingNavigation.sourceFileId
          );

        if (!stateMatchesPendingNavigation) {
          return;
        }

        pendingPanoramaNavigationStateRef.current = null;
      }
    }

    setCurrentPanoramaState(state);
  }, []);

  // Memoized callback for loaded models change (prevents infinite loop)
  const handleLoadedModelsChange = useCallback((models: { name: string }[]) => {
    const filenames = models.map(m => m.name);
    setLoadedIfcFiles(filenames);
    // Remove from loading state any files that are now loaded
    setLoadingIfcFiles(prev => prev.filter(f => !filenames.includes(f)));
    if (filenames.length > 0 && currentViewModeRef.current === '3D' && !showAutodeskViewer) {
      finishViewerBlock('3D');
    }
  }, [finishViewerBlock, showAutodeskViewer, currentViewModeRef]);

  useEffect(() => {
    if (
      activeMenuItem === 'viewer' &&
      currentViewMode === '3D' &&
      hasIFCFile &&
      show3DViewer &&
      !showAutodeskViewer &&
      loadedIfcFiles.length > 0
    ) {
      finishViewerBlock('3D');
    }
  }, [
    activeMenuItem,
    currentViewMode,
    finishViewerBlock,
    hasIFCFile,
    loadedIfcFiles.length,
    show3DViewer,
    showAutodeskViewer
  ]);

  // Callback to load an IFC/RVT model by filename (for POIs whose models aren't loaded yet)
  // Does NOT navigate - just starts loading in the background and shows loading indicator
  const handleRequestLoadModel = useCallback((fileName: string) => {
    // Check if already loaded
    if (loadedIfcFiles.includes(fileName)) {
      logger.debug('Model already loaded:', fileName);
      return;
    }

    // Check if already loading
    if (loadingIfcFiles.includes(fileName)) {
      logger.debug('Model already loading:', fileName);
      return;
    }

    // Check if this is an RVT file from ACC links (exact match only — no fallback here:
    // loading a DIFFERENT model than the one requested would be wrong)
    const rvtLink = findReadyLinkByFileName(fileName);

    if (rvtLink) {
      // RVT file - load via AutodeskViewer
      logger.debug('[App.tsx] Loading RVT model via AutodeskViewer:', fileName);

      // Add to loading state immediately (for UI feedback)
      setLoadingIfcFiles(prev => [...prev, fileName]);

      // Setup AutodeskViewer config — canonical builder (Wave C-1)
      setAutodeskViewerConfig(buildAutodeskViewerConfigFromLink(rvtLink));

      // Ensure AutodeskViewer is enabled (but DON'T navigate to it)
      setShowAutodeskViewer(true);
      setHasRenderedAutodeskViewer(true);
      return;
    }

    // Check for local RVT file
    const localRvt = getReadyLocalRvtFiles(selectedAsset).find((rvtFile) =>
      rvtFile.fileName === fileName || rvtFile.fileName?.toLowerCase() === fileName.toLowerCase()
    ) || getPrimaryLocalRvtFile(selectedAsset);
    const rvtMeta = localRvt?.metadata;
    if (localRvt && rvtMeta?.rvt_translation_status === 'ready' && rvtMeta?.viewable_urn) {
      logger.debug('[App.tsx] Loading local RVT model via AutodeskViewer:', fileName);
      setLoadingIfcFiles(prev => [...prev, fileName]);

      setAutodeskViewerConfig({
        ...getLocalRvtViewerConfig(localRvt, selectedAsset?.id || ''),
        fileName: localRvt.fileName || fileName,
      });

      setShowAutodeskViewer(true);
      setHasRenderedAutodeskViewer(true);
      return;
    }

    // Otherwise, try IFC files
    if (!selectedAsset?.files?.ifc?.length) {
      console.warn('No IFC files available in asset and not an RVT file:', fileName);
      return;
    }

    // Find the IFC file in the asset
    const ifcFile = selectedAsset.files.ifc.find(f => (f.fileName === fileName || f.file_name === fileName));
    if (!ifcFile) {
      console.warn('IFC file not found in asset:', fileName);
      return;
    }

    // Add to loading state immediately (for UI feedback)
    setLoadingIfcFiles(prev => [...prev, fileName]);

    // Request the WebIfcViewer to load this model by filename
    setRequestedModelFileName(fileName);

    // Ensure 3D viewer is enabled (but DON'T navigate to it)
    setShow3DViewer(true);
    setHasRendered3DViewer(true);

    logger.debug('Requesting to load IFC model in background:', fileName);
    // selectedAssetAccLinks is reached through findReadyLinkByFileName, memoized on it.
  }, [
    selectedAsset,
    loadedIfcFiles,
    loadingIfcFiles,
    getLocalRvtViewerConfig,
    buildAutodeskViewerConfigFromLink,
    findReadyLinkByFileName,
    setHasRendered3DViewer,
    setHasRenderedAutodeskViewer,
    setShow3DViewer,
    setShowAutodeskViewer
  ]);

  // Callback when WebIfcViewer finishes loading the requested model
  const handleRequestedModelLoaded = useCallback((fileName: string) => {
    logger.debug('Model loaded callback received:', fileName);
    // Clear loading state for this file
    setLoadingIfcFiles(prev => prev.filter(f => f !== fileName));
    // Clear the request (so we don't re-trigger loading)
    setRequestedModelFileName(null);
  }, []);

  const user: User = {
    name: 'Mabat3D User',
    email: 'user@mabat3d.com',
    avatar: '👤',
    organization: 'Mabat3D'
  };

  // Translation functions come from TranslationContext (useTranslation hook)

  // Menu IA (2026-07-14 product pass): Dashboard is real (Epic C feed + calls +
  // IoT status); Tickets IS the service-call page (Epic B) in the old stub's
  // slot; setup surfaces (IoT Devices, Integrations) live in the Connections
  // droplist next to Settings — they are configure-once pages, not daily stops.
  const allMenuItems = useMemo<MenuItemConfig[]>(() => [
    { id: 'dashboard', label: t('dashboard'), icon: '📊' },
    { id: 'tickets', label: t('tickets'), icon: '🎫' },
    { id: 'viewer', label: t('viewer'), icon: '🏗️' },
    { id: 'sites', label: t('sites'), icon: '🏢' },
    // Assets / Locations / Systems are retired from the menu — every ability they carried now
    // lives in the viewer's Browser pane. The pages still render when reached programmatically
    // (e.g. the dashboard cards at ~2458 / ~3485), so those flows keep working.
    { id: 'diagrams', label: currentLanguage === 'he' ? 'תרשימים' : 'Diagrams', icon: '🔗' },
    // FLAT, not a group. This was a collapsible 'access' heading over Users & Groups /
    // Coverage / Responsibility, which is the right shape for three siblings. Coverage and
    // Responsibility were then withdrawn from the menu, leaving a droplist you had to expand
    // to reach the single item inside it — pure ceremony, and one more click to the page.
    // A group earns its heading when it has siblings to hold; with one child it is just a
    // button wearing a disguise.
    //
    // The id stays 'users', so every existing route, deep link and dashboard card resolves
    // exactly as before. If Coverage and Responsibility ever come back to the menu, the
    // grouping comes back with them.
    //
    // Their pages, routes and APIs remain intact and still resolve when navigated to
    // directly — only the menu entries are withdrawn, pending a decision on folding what
    // they show into the pages that already exist. Do not delete the pages.
    { id: 'users', label: currentLanguage === 'he' ? 'משתמשים וקבוצות' : 'Users & Groups', icon: '👥', adminOnly: true },
    { id: 'tests', label: currentLanguage === 'he' ? 'בדיקות' : 'Tests', icon: '✅', globalAdminOnly: true },
    { id: 'usage', label: currentLanguage === 'he' ? 'שימוש' : 'Usage', icon: '📊', globalAdminOnly: true },
    {
      id: 'connections',
      label: currentLanguage === 'he' ? 'חיבורים' : 'Connections',
      icon: '🔌',
      children: [
        { id: 'devices', label: currentLanguage === 'he' ? 'חיישנים' : 'IoT Devices', icon: '📡' },
        { id: 'integrations', label: currentLanguage === 'he' ? 'אינטגרציות' : 'Integrations', icon: '🌐' },
      ],
    },
    { id: 'settings', label: t('settings'), icon: '⚙️' }
  ], [currentLanguage, t]);

  const canShowMenuItem = useCallback((item: MenuItemConfig) => {
    if (item.globalAdminOnly && !isAdmin) {
      return false;
    }
    // `canAdministerPeople` is the server's own answer (adminSiteIds). It used to read
    // `isAdmin || isAccountAdmin || isSiteAdmin`, where isSiteAdmin was never populated —
    // so a site admin never saw this item, although every route behind it admitted them.
    if (item.adminOnly && !canAdministerPeople) {
      return false;
    }
    if (item.onboardingOnly && !isOnboardingManager) {
      return false;
    }
    return true;
  }, [canAdministerPeople, isAdmin, isOnboardingManager]);

  const isBlockedMenuPage = useCallback((item: MenuItemConfig) => {
    return Boolean(item.disabled) || !canShowMenuItem(item);
  }, [canShowMenuItem]);

  // Filter menu items based on RBAC roles (groups: filter children too; drop a
  // group whose children are all hidden).
  const menuItems = allMenuItems
    .filter(canShowMenuItem)
    .map(item => item.children ? { ...item, children: item.children.filter(canShowMenuItem) } : item)
    .filter(item => !item.children || item.children.length > 0);

  // Droplist open/closed state, persisted per user.
  //
  // COLLAPSED BY DEFAULT, with one nuance: a STORED preference always wins, and only when
  // there is none does the group auto-open because it holds the current page. Forcing it
  // open whenever a child is active would fight a user who deliberately collapsed it while
  // reading Coverage; defaulting it shut with no exception would land you on Coverage with
  // the sidebar showing no trace of where you are.
  const [openMenuGroups, setOpenMenuGroups] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem('mabat3d_menu_groups') || '{}'); } catch { return {}; }
  });
  const toggleMenuGroup = useCallback((groupId: string, isOpen: boolean) => {
    setOpenMenuGroups(prev => {
      const next = { ...prev, [groupId]: !isOpen };
      try { localStorage.setItem('mabat3d_menu_groups', JSON.stringify(next)); } catch { /* private mode */ }
      return next;
    });
  }, []);
  // Live sidebar count pills (Tickets active / IoT devices) for the selected site — matches the mockup.
  const [menuCounts, setMenuCounts] = useState<Record<string, number>>({});
  useEffect(() => {
    const aid = selectedAsset?.id;
    if (!aid) { setMenuCounts({}); return; }
    let cancelled = false;
    void apiService.get(`/assets/${aid}/service-calls?page=1&pageSize=1&status=active`).then((list: any) => {
      if (cancelled) return;
      setMenuCounts(m => ({ ...m, tickets: Number(list.total || 0) }));
    }).catch(() => {});
    void apiService.get(`/assets/${aid}/iot-devices`).then((list: any) => {
      if (cancelled) return;
      setMenuCounts(m => ({ ...m, devices: Array.isArray(list) ? list.length : 0 }));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [selectedAsset?.id, ticketRevision]);

  useEffect(() => {
    if (rbacLoading || activeMenuItem === 'documentation') {
      return;
    }
    if (isBrowserRegressionRunnerPage && activeMenuItem === 'tests') {
      return;
    }

    const activeItem = allMenuItems
      .flatMap(item => [item, ...(item.children || [])])
      .find(item => item.id === activeMenuItem);
    const shouldFallbackToSites = activeMenuItem === 'onboarding'
      || (activeItem ? isBlockedMenuPage(activeItem) : false);

    if (shouldFallbackToSites && activeMenuItem !== 'sites') {
      setActiveMenuItem('sites');
    }
  }, [activeMenuItem, allMenuItems, isBlockedMenuPage, isBrowserRegressionRunnerPage, rbacLoading]);

  // Build location breadcrumb path based on hierarchy
  // Shows: asset_name > file_name > Building (if only 1) > Storey (if only 1) > Space (if only 1)

  // Dynamic viewer modes based on available asset files
  const getAvailableViewModes = (): Array<'3D' | '2D' | 'Panorama'> => {
    if (!selectedAsset) return [];
    const modes: Array<'3D' | '2D' | 'Panorama'> = [];

    // Priority order: 3D → Panorama → 2D
    // Note: ifc is an array, so check length; e57/geotiff are single objects
    // Also check for viewer-ready ACC links (synced RVT files with viewable_urn)
    const hasViewerReadyAccLink = selectedAssetAccLinks.some(link => accService.isViewerReady(link));
    const hasLocalRVTReady = getReadyLocalRvtFiles(selectedAsset).length > 0;
    if ((selectedAsset.files?.ifc && selectedAsset.files.ifc.length > 0) || hasViewerReadyAccLink || hasLocalRVTReady) {
      modes.push('3D');
    }
    if (selectedAsset.files?.e57) modes.push('Panorama');
    // 2D mode is available if there's a geotiff OR if there's an RVT file (RVT has built-in
    // 2D minimap). hasRVTFile is the single source for this predicate — this function runs
    // during render, so the memo is always fresh here (the former hasRvtDirect duplicate
    // re-implemented the same three clauses).
    if (selectedAsset.files?.geotiff || hasRVTFile) modes.push('2D');

    return modes;
  };

  const getDefaultViewMode = (): '3D' | '2D' | 'Panorama' => {
    const availableModes = getAvailableViewModes();
    return availableModes.length > 0 ? availableModes[0] : '3D';
  };

  // Component for when no viewer files are available
  const NoViewerFilesComponent: React.FC<{ asset: Asset | null }> = ({ asset }) => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      padding: '40px',
      textAlign: 'center',
      background: 'var(--dt-surface-2)'
    }}>
      <div style={{ fontSize: '56px', marginBottom: '20px', opacity: 0.7 }}>📂</div>
      <h2 style={{
        color: 'var(--dt-text)',
        marginBottom: '16px',
        fontSize: '22px',
        fontWeight: 700
      }}>
        No Viewer Files Available
      </h2>
      {asset ? (
        <>
          <p style={{
            color: 'var(--dt-text-2)',
            marginBottom: '24px',
            fontSize: '15px',
            lineHeight: '1.5',
            maxWidth: '500px'
          }}>
            Asset "{asset.name}" doesn't have any files that can be viewed in the 3D/2D/Panorama viewers.
          </p>
          <div style={{
            background: 'var(--dt-surface)',
            border: '1px solid var(--dt-border)',
            borderRadius: '12px',
            boxShadow: 'var(--dt-sh-sm)',
            padding: '20px',
            marginBottom: '24px',
            maxWidth: '600px'
          }}>
            <h3 style={{
              color: 'var(--dt-text)',
              marginBottom: '12px',
              fontSize: '16px',
              fontWeight: 700
            }}>
              To enable viewers, upload these file types:
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '24px' }}>🏢</span>
                <span style={{ color: 'var(--dt-text-2)' }}>
                  <strong>3D Viewer:</strong> Upload IFC files for BIM models
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '24px' }}>🌐</span>
                <span style={{ color: 'var(--dt-text-2)' }}>
                  <strong>Panorama Viewer:</strong> Upload E57 files for 360° scans
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '24px' }}>📐</span>
                <span style={{ color: 'var(--dt-text-2)' }}>
                  <strong>2D Viewer:</strong> Upload GEOTIFF/TIFF files for maps
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={() => setActiveMenuItem('assets')}
            style={{
              padding: '11px 20px',
              background: 'var(--dt-accent)',
              color: 'var(--dt-accent-ink)',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: 600,
              fontFamily: 'inherit',
              transition: 'all 0.15s ease'
            }}
          >
            📁 Go to Assets Page to Upload Files
          </button>
        </>
      ) : (
        <p style={{ color: 'var(--dt-text-3)', fontSize: '15px' }}>
          No asset selected. Please select an asset from the Assets page.
        </p>
      )}
    </div>
  );

  // Auto-select appropriate view mode when asset changes
  useEffect(() => {
    if (selectedAsset) {
      const defaultMode = getDefaultViewMode();
      const availableModes = getAvailableViewModes();

      // Only change mode if current mode is not available for this asset
      if (!availableModes.includes(currentViewMode)) {
        setCurrentViewMode(defaultMode);
      }
    }
    // Keyed on the asset change alone by design: re-running on currentViewMode would fight the
    // user's own mode picks, and the two mode helpers are render-fresh (new identity every render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAsset?.id]);

  // Auto-close 3D viewer when switching away from 3D mode
  React.useEffect(() => {
    if (currentViewMode !== '3D' && show3DViewer && !isIFCPanorama3DOverlayVisible) {
      setShow3DViewer(false);
    }
  }, [currentViewMode, isIFCPanorama3DOverlayVisible, show3DViewer, setShow3DViewer]);

  // Initialize coordinate synchronization
  useEffect(() => {
    const viewerId = `app-${currentViewMode}`;

    const handleSyncEvent = (event: SyncEvent) => {
      // Update position from other viewers
      if (event.sourceViewer.id !== viewerId) {
        setCurrentPosition(event.position);
      }
    };

    coordinateSync.registerViewer(viewerId, handleSyncEvent);

    return () => {
      coordinateSync.unregisterViewer(viewerId);
    };
  }, [currentViewMode]);

  // Get current files for active view mode
  const getCurrentViewFiles = () => {
    // First check manually uploaded files
    let manualFiles: File[] = [];
    switch (currentViewMode) {
      case '3D':
        manualFiles = files3D;
        break;
      case '2D':
        manualFiles = files2D;
        break;
      case 'Panorama':
        manualFiles = filesPanorama;
        break;
      default:
        manualFiles = [];
    }

    // If there are manually uploaded files, return them
    if (manualFiles.length > 0) {
      return manualFiles;
    }

    // Otherwise, check if selected asset has files for the current view mode
    if (selectedAsset) {
      const assetFiles: File[] = [];

      if (currentViewMode === '3D' && selectedAsset.files.ifc?.length) {
        // IFC is an array - use default file or first file
        const ifcFile = selectedAsset.files.ifc.find(f => f.isDefault) || selectedAsset.files.ifc[0];
        // Create a File object from the asset's IFC file
        // Note: We create a placeholder file - actual data will be fetched via assetFilePath
        const assetFile = new File([], ifcFile.fileName, {
          type: 'application/octet-stream'
        });
        // Add the actual file size from metadata
        Object.defineProperty(assetFile, 'size', {
          value: ifcFile.fileSize || 0,
          writable: false
        });
        // Add the API URL for loading
        (assetFile as any).assetFilePath = `/api/assets/${selectedAsset.id}/files/ifc`;
        assetFiles.push(assetFile);
      }


      if (currentViewMode === '2D' && selectedAsset.files.dwg) {
        // Create a File object from the asset's DWG file
        const assetFile = new File([], selectedAsset.files.dwg.fileName, {
          type: 'application/octet-stream'
        });
        // Add the actual file size from metadata
        Object.defineProperty(assetFile, 'size', {
          value: selectedAsset.files.dwg.fileSize || 0,
          writable: false
        });
        // Add the API URL for loading
        (assetFile as any).assetFilePath = `/api/assets/${selectedAsset.id}/files/dwg`;
        assetFiles.push(assetFile);
      }

      if (currentViewMode === 'Panorama' && selectedAsset.files.e57) {
        // Create a File object from the asset's E57 file
        const assetFile = new File([], selectedAsset.files.e57.fileName, {
          type: 'application/octet-stream'
        });
        // Set the assetFilePath to match what the panorama viewer expects for processed files
        (assetFile as any).assetFilePath = selectedAsset.files.e57.filePath || 'panoramas_extracted';
        (assetFile as any).assetId = selectedAsset.id;
        assetFiles.push(assetFile);
      }

      return assetFiles;
    }

    return [];
  };

  // Handle asset selection
  const handleAssetSelected = (asset: Asset) => {
    trackEvent('site.open', { assetId: asset.id });

    // DEBUG: Log every call to understand the flow
    logger.debug('[App.tsx] handleAssetSelected called:', {
      assetId: asset.id,
      previousAssetId,
      activeMenuItem,
      showAutodeskViewer
    });

    // Check if this is actually a different asset
    const isNewAsset = asset.id !== previousAssetId;

    // Same asset refresh: keep viewer state intact, but accept changed metadata/custom_data
    // so site-level settings can reach already-mounted viewers.
    if (!isNewAsset) {
      const currentCustomData = JSON.stringify(selectedAsset?.custom_data ?? null);
      const nextCustomData = JSON.stringify(asset.custom_data ?? null);
      const currentFiles = JSON.stringify(selectedAsset?.files ?? null);
      const nextFiles = JSON.stringify(asset.files ?? null);
      const shouldRefreshSelectedAsset =
        currentCustomData !== nextCustomData ||
        currentFiles !== nextFiles ||
        selectedAsset?.permission_level !== asset.permission_level;

      if (shouldRefreshSelectedAsset) {
        logger.debug('[App.tsx] Same asset - refreshing selected asset data only');
        setSelectedAsset(asset);
      } else {
        logger.debug('[App.tsx] Same asset - no selected asset data changes');
      }
      return;
    }

    logger.debug('[App.tsx] NEW asset - processing...');

    setSelectedAsset(asset);
    setPreviousAssetId(asset.id);

    // Load POIs for the selected asset globally
    loadPOIsForAsset(asset);

    // Only reset if it's actually a different asset
    if (isNewAsset) {
      // Clear any manually uploaded files when selecting a new asset
      setFiles3D([]);
      setFiles2D([]);
      setFilesPanorama([]);

      // Reset viewer states to force re-render when switching assets
      setHasRendered3DViewer(false);
      setHasRenderedE57Viewer(false);
      setHasRenderedGEOTIFFViewer(false);
      setHasRenderedAutodeskViewer(false);
      setShow3DViewer(false);
      setShowWorkingE57Viewer(false);
      setShowGEOTIFFViewer(false);
      setShowAutodeskViewer(false);
      setAutodeskViewerConfig(null);
      setLoadedIfcFiles([]);
      setLoadingIfcFiles([]);
      setRequestedModelFileName(null);

      // Clear minimap image data when switching to new asset
      setMinimapImageData(null);
    } else {
      // Don't reset viewer states for same asset - preserve component state
    }

    // Pre-load ALL available viewers for the asset immediately (new asset)
    if (isNewAsset) {
      // Always preload available viewers regardless of current view mode
      // This ensures minimap has 2D data immediately when switching to 3D

      // Enable 3D viewer if IFC files are available
      if (asset.files.ifc?.length) {
        setShow3DViewer(true);
      }

      // Enable GEOTIFF viewer if GEOTIFF files are available (for minimap)
      // Skip for RVT assets - they have built-in Autodesk minimap
      if (asset.files.geotiff && !hasRVTFile) {
        setShowGEOTIFFViewer(true);
        setHasRenderedGEOTIFFViewer(true);
        logger.debug('🗺️ GEOTIFF viewer enabled for minimap during asset selection');
      }

      // Enable E57 viewer if E57 files are available
      if (asset.files.e57) {
        setShowWorkingE57Viewer(true);
        setHasRenderedE57Viewer(true);
      }
    } else {
      // Same asset - ensure all available viewers are loaded for minimap support
      if (asset.files.ifc?.length && !show3DViewer) {
        setShow3DViewer(true);
      }
      // Skip for RVT assets - they have built-in Autodesk minimap
      if (asset.files.geotiff && !showGEOTIFFViewer && !hasRVTFile) {
        setShowGEOTIFFViewer(true);
        setHasRenderedGEOTIFFViewer(true);
        logger.debug('🗺️ GEOTIFF viewer re-enabled for minimap during same asset selection');
      }
      if (asset.files.e57 && !showWorkingE57Viewer) {
        setShowWorkingE57Viewer(true);
        setHasRenderedE57Viewer(true);
      }
    }

      /* COMMENTED OUT - DWG logic replaced with GEOTIFF
      } else if (asset.files.dwg && currentViewMode === '2D') {
        // For DWG files in 2D mode, show the DWG viewer
        setShowDWGViewer(true);
        setHasRenderedDWGViewer(true);
        setShow3DViewer(false);
        setShowWorkingE57Viewer(false);
      */
  };

  const handleOpenOnboardingSiteViewer = useCallback(async (siteId: string) => {
    try {
      setViewerError(null);

      if (!selectedAsset || selectedAsset.id !== siteId) {
        const asset = await assetService.getAsset(siteId);
        if (!asset) {
          throw new Error('Failed to load the selected site');
        }

        assetService.selectAsset(siteId);
        handleAssetSelected(asset);
      }

      enterViewerPage({ mode: '3D' });
    } catch (error: any) {
      setViewerError(error.message || 'Failed to open viewer');
    }
    // handleAssetSelected is a render-fresh function; depending on it would rebuild this
    // callback every render for no freshness gain — selectedAsset already keys what it reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAsset, enterViewerPage]);

  // Handle opening Autodesk Viewer for RVT files
  const handleOpenAutodeskViewer = useCallback(async (config: {
    urn: string;
    connectionId: string;
    assetId?: string;
    viewables: Array<{ guid: string; name: string; role: '3d' | '2d' }>;
    fileName?: string;
    defaultLevelName?: string | null;
    defaultLevelElevation?: number | null;
    initialViewableGuid?: string | null;
    accRegion?: 'US' | 'EMEA';
    roomSourceViewableGuid?: string | null;
    additionalModels?: AutodeskAdditionalRVTModel[];
  }) => {
    logger.debug('[App.tsx] Opening Autodesk Viewer with config:', config);
    const resolvedAssetId = config.assetId || selectedAsset?.id || '';
    let freshAdditionalModels = config.additionalModels || [];

    if (resolvedAssetId) {
      try {
        const freshAsset = await assetService.getAsset(resolvedAssetId);
        if (freshAsset) {
          setSelectedAsset(freshAsset);
          freshAdditionalModels = dedupeAdditionalRvtModels([
            ...freshAdditionalModels,
            ...getAdditionalLocalRvtModels(freshAsset, config.urn)
          ], config.urn);
          logger.debug('[App.tsx] Fresh local RVT secondary models for viewer:', freshAdditionalModels.map(model => model.fileName));
        }
      } catch (error) {
        console.warn('[App.tsx] Could not refresh asset before opening Autodesk Viewer:', error);
      }
    }

    setAutodeskViewerConfig({
      ...config,
      assetId: resolvedAssetId,
      fileName: config.fileName,
      defaultLevelName: config.defaultLevelName,
      defaultLevelElevation: config.defaultLevelElevation,
      initialViewableGuid: config.initialViewableGuid,
      accRegion: config.accRegion,
      roomSourceViewableGuid: config.roomSourceViewableGuid,
      additionalModels: freshAdditionalModels
    });
    setShowAutodeskViewer(true);
    setHasRenderedAutodeskViewer(true);
    // Hide other 3D viewers when showing Autodesk viewer
    setShow3DViewer(false);
    // Navigate to viewer page (canonical transition — now also persists the page for
    // refresh-restore, which this path previously skipped)
    enterViewerPage({ mode: '3D' });
  }, [
    selectedAsset?.id,
    enterViewerPage,
    setHasRenderedAutodeskViewer,
    setShow3DViewer,
    setShowAutodeskViewer
  ]);

  // In Panorama mode, RVT sites should still bootstrap AutodeskViewer so its built-in minimap is available.
  // Keep Panorama active and leave the full RVT canvas hidden; only the fixed minimap should surface.
  useEffect(() => {
    if (activeMenuItem !== 'viewer' || currentViewMode !== 'Panorama' || !selectedAsset?.files?.e57 || !hasRVTFile) {
      return;
    }

    const readyRVTLink = findReadyRvtLink();
    const readyLocalRvt = getPrimaryLocalRvtFile(selectedAsset);

    if (!readyRVTLink?.viewable_urn && !readyLocalRvt?.metadata?.viewable_urn) {
      return;
    }

    if (readyRVTLink?.viewable_urn) {
      setAutodeskViewerConfig(prev => {
        if (
          prev?.urn === readyRVTLink.viewable_urn &&
          prev?.connectionId === readyRVTLink.acc_connection_id &&
          prev?.assetId === (selectedAsset?.id || '')
        ) {
          return prev;
        }

        // Canonical builder — same config shape on every entry path (Wave C-1)
        return buildAutodeskViewerConfigFromLink(readyRVTLink);
      });
    } else if (readyLocalRvt?.metadata?.viewable_urn) {
      const nextConfig = getLocalRvtViewerConfig(readyLocalRvt, selectedAsset?.id || '');
      setAutodeskViewerConfig(prev => {
        if (
          prev?.urn === nextConfig.urn &&
          prev?.connectionId === nextConfig.connectionId &&
          prev?.assetId === nextConfig.assetId
        ) {
          return prev;
        }
        return nextConfig;
      });
    }

    setShowAutodeskViewer(true);
    setHasRenderedAutodeskViewer(true);
  }, [
    activeMenuItem,
    currentViewMode,
    selectedAsset,
    selectedAssetAccLinks,
    getLocalRvtViewerConfig,
    buildAutodeskViewerConfigFromLink,
    findReadyRvtLink,
    hasRVTFile,
    setHasRenderedAutodeskViewer,
    setShowAutodeskViewer
  ]);

  // Will an Autodesk model load behind this site's panorama? The effect above's own condition,
  // read synchronously — so the gate below cannot decide "no model" in the same commit that
  // effect is about to start one — plus a model that is already there (e.g. arrived from 3D).
  const panoramaExpects3DModel = Boolean(
    (showAutodeskViewer && autodeskViewerConfig) ||
    (selectedAsset?.files?.e57 && hasRVTFile && (
      findReadyRvtLink()?.viewable_urn || getPrimaryLocalRvtFile(selectedAsset)?.metadata?.viewable_urn
    ))
  );

  // THE Panorama gate: Panorama counts as ready only when getPanoramaBlockStep says so. That is
  // decided in 3D too — the photo preloads there — so entering Panorama with everything already in
  // shows no screen at all, as before. While the model is the only thing left, the screen says so
  // and shows how many models are in.
  useEffect(() => {
    const assetId = selectedAsset?.id;
    const step = getPanoramaBlockStep({
      photoReady: !!assetId && panoramaPhotoReadyFor === assetId,
      siteLinksKnown: !!assetId && accLinksAssetId === assetId,
      expects3DModel: panoramaExpects3DModel,
      modelsReady: !!assetId && panoramaModelsReadyFor === assetId
    });
    if (step === 'release') {
      finishViewerBlock('Panorama');
    } else if (step === 'model' && viewerBlockState?.mode === 'Panorama') {
      updateViewerBlockStage(
        'Panorama',
        currentLanguage === 'he' ? 'טוען מודל תלת-ממדי...' : 'Loading 3D model...',
        autodeskModelsProgress
      );
    }
  }, [
    accLinksAssetId,
    autodeskModelsProgress,
    currentLanguage,
    finishViewerBlock,
    panoramaExpects3DModel,
    panoramaModelsReadyFor,
    panoramaPhotoReadyFor,
    selectedAsset?.id,
    updateViewerBlockStage,
    viewerBlockState?.mode
  ]);

  // Auto-open Autodesk viewer when navigating to viewer menu with a ready ACC link
  useEffect(() => {
    // Only trigger if on viewer page without Autodesk viewer already showing
    // and there's no IFC files (otherwise IFC viewer takes priority)
    const hasIfc = selectedAsset?.files?.ifc && selectedAsset.files.ifc.length > 0;
    if (activeMenuItem === 'viewer' && currentViewMode !== 'Panorama' && !showAutodeskViewer && !hasIfc) {
      logger.debug('[DEBUG] Link selection - All available links:', selectedAssetAccLinks.map(l => ({
        id: l.id,
        fileName: l.acc_file_name,
        urn: l.viewable_urn,
        syncStatus: l.sync_status,
        conversionType: l.conversion_type,
        createdAt: l.created_at
      })));
      
      const readyLink = selectedAssetAccLinks.find(link => accService.isViewerReady(link));
      logger.debug('[DEBUG] Link selection - Selected readyLink:', readyLink ? {
        id: readyLink.id,
        fileName: readyLink.acc_file_name,
        urn: readyLink.viewable_urn,
        syncStatus: readyLink.sync_status,
        conversionType: readyLink.conversion_type,
        createdAt: readyLink.created_at
      } : 'None found');
      
      logger.debug('[DEBUG] Link selection - autoTriggeredViewerRef.current:', autoTriggeredViewerRef.current);
      
      if (readyLink && readyLink.viewable_urn) {
        // Only trigger if we haven't already triggered for this URN
        if (autoTriggeredViewerRef.current !== readyLink.viewable_urn) {
          logger.debug('[DEBUG] Link selection - URN mismatch, triggering viewer with new URN');
          autoTriggeredViewerRef.current = readyLink.viewable_urn;
          logger.debug('[App.tsx] Auto-triggering Autodesk Viewer for ACC link:', readyLink.acc_file_name);

          if (currentViewMode !== '3D') {
            setCurrentViewMode('3D');
          }

          // Canonical builder — same config shape on every entry path (Wave C-1)
          setAutodeskViewerConfig(buildAutodeskViewerConfigFromLink(readyLink));
          setShowAutodeskViewer(true);
          setHasRenderedAutodeskViewer(true);
          setShow3DViewer(false);
        } else {
          logger.debug('[DEBUG] Link selection - URN matches autoTriggeredViewerRef, skipping viewer trigger');
        }
      } else {
        // Check for local RVT file with completed translation
        const localRvt = getPrimaryLocalRvtFile(selectedAsset);
        const rvtMeta = localRvt?.metadata;
        if (rvtMeta?.rvt_translation_status === 'ready' && rvtMeta?.viewable_urn) {
          if (autoTriggeredViewerRef.current !== rvtMeta.viewable_urn) {
            logger.debug('[App.tsx] Auto-triggering Autodesk Viewer for local RVT:', localRvt.fileName);
            autoTriggeredViewerRef.current = rvtMeta.viewable_urn;

            if (currentViewMode !== '3D') {
              setCurrentViewMode('3D');
            }

            setAutodeskViewerConfig(getLocalRvtViewerConfig(localRvt, selectedAsset?.id || ''));
            setShowAutodeskViewer(true);
            setHasRenderedAutodeskViewer(true);
            setShow3DViewer(false);
          }
        }
      }
    }
  }, [
    activeMenuItem,
    currentViewMode,
    selectedAssetAccLinks,
    showAutodeskViewer,
    selectedAsset,
    getLocalRvtViewerConfig,
    buildAutodeskViewerConfigFromLink,
    setCurrentViewMode,
    setHasRenderedAutodeskViewer,
    setShow3DViewer,
    setShowAutodeskViewer
  ]);

  // Reset auto-triggered ref when asset changes
  useEffect(() => {
    autoTriggeredViewerRef.current = null;
  }, [selectedAsset?.id]);

  // Clear auto-triggeredViewerRef when ACC link URN changes
  // This ensures viewer reloads with new URN after re-sync
  useEffect(() => {
    if (selectedAssetAccLinks.length > 0) {
      const syncedLink = selectedAssetAccLinks.find(link => link.id === autoTriggeredViewerRef.current?.split('/')[1]?.split(':')[0]);
      if (syncedLink && syncedLink.viewable_urn !== autoTriggeredViewerRef.current) {
        logger.debug('[DEBUG] ACC link URN changed, clearing autoTriggeredViewerRef to force viewer reload');
        logger.debug('[DEBUG]   Old URN:', autoTriggeredViewerRef.current);
        logger.debug('[DEBUG]   New URN:', syncedLink.viewable_urn);
        autoTriggeredViewerRef.current = null;
      }
    }
  }, [selectedAssetAccLinks]);

  // Handle asset updates (after file uploads)
  const handleAssetUpdated = async (assetId: string) => {

    // Refresh the selected asset if it matches the updated asset
    if (selectedAsset && selectedAsset.id === assetId) {
      try {
        const updatedAsset = await assetService.getAsset(assetId);
        if (updatedAsset) {
          setSelectedAsset(updatedAsset);

          // Manually trigger viewer logic since handleAssetSelected might not be called
          handleAssetSelected(updatedAsset);

        }
      } catch (error) {
        console.error('❌ Failed to refresh selected asset:', error);
      }
    }
  };


  // Auto-trigger viewers when switching to viewer page with a selected asset
  useEffect(() => {
    if (activeMenuItem === 'viewer' && selectedAsset) {
      // Trigger viewer display logic when asset is selected

      if (selectedAsset.files.ifc?.length && currentViewMode === '3D') {
        // Auto-show IFC viewer for IFC files
        setShow3DViewer(true);
        setHasRendered3DViewer(true);
        // Don't hide E57 viewer - preserve state for view switching
      } else if (selectedAsset.files.geotiff && currentViewMode === '2D' && !hasRVTFile) {
        // Auto-show GEOTIFF viewer for GEOTIFF files (skip for RVT - uses Autodesk minimap)
        setShowGEOTIFFViewer(true);
        setHasRenderedGEOTIFFViewer(true);
        setShow3DViewer(false);
        // Don't hide E57 viewer - preserve state for view switching

        /* COMMENTED OUT - DWG logic replaced with GEOTIFF
        setShowDWGViewer(true);
        setHasRenderedDWGViewer(true);
        */
      } else if (selectedAsset.files.e57 && currentViewMode === 'Panorama') {
        // Auto-show E57 viewer for E57 files
        setShowWorkingE57Viewer(true);
        setHasRenderedE57Viewer(true);
        if (!isIFCPanorama3DOverlayVisible) {
          setShow3DViewer(false);
        }

        // Also enable GEOTIFF viewer if available for panorama mode minimap (skip for RVT)
        if (selectedAsset.files.geotiff && !hasRVTFile) {
          setShowGEOTIFFViewer(true);
          setHasRenderedGEOTIFFViewer(true);
        }
      } else {
        // Hide viewers when files are not available
        if (currentViewMode === '3D' && !selectedAsset.files.ifc?.length) {
          setShow3DViewer(false);
        } else if (currentViewMode === '2D' && !selectedAsset.files.geotiff) {
          setShowGEOTIFFViewer(false);

          /* COMMENTED OUT - DWG logic replaced with GEOTIFF
          setShowDWGViewer(false);
          */
        } else if (currentViewMode === 'Panorama' && !selectedAsset.files.e57) {
          setShowWorkingE57Viewer(false);
        }
      }
    }
  }, [
    activeMenuItem,
    selectedAsset,
    currentViewMode,
    hasRVTFile,
    isIFCPanorama3DOverlayVisible,
    setHasRendered3DViewer,
    setHasRenderedE57Viewer,
    setHasRenderedGEOTIFFViewer,
    setShow3DViewer,
    setShowGEOTIFFViewer,
    setShowWorkingE57Viewer
  ]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setIsLoading(true);
      setViewerError(null);
      setShow3DViewer(false);
      setLoadingProgress(0);

      const fileArray = Array.from(files);

      // Store files in the appropriate view mode state
      switch (currentViewMode) {
        case '3D':
          setFiles3D(fileArray);
          break;
        case '2D':
          setFiles2D(fileArray);
          break;
        case 'Panorama':
          setFilesPanorama(fileArray);
          break;
      }

      // Files are loaded for viewing only.
      // To upload files to assets, use the Assets page upload functionality.

      // Show user how to save files to assets if they want to
      if (!selectedAsset) {
      }

      // Check file types from asset (not fileArray which is for uploads)
      // Note: ifc is an array, so check length
      const hasIFC = Boolean(selectedAsset?.files?.ifc?.length);
      const hasPointCloud = Boolean(selectedAsset?.files?.e57);
      const hasGEOTIFF = Boolean(selectedAsset?.files?.geotiff);
      // const hasDWG = Boolean(selectedAsset?.files?.dwg); // Commented out - using GEOTIFF instead

      // Set loading state and show appropriate viewer immediately
      setIsLoading(false);

      if (hasIFC && currentViewMode === '3D') {
        setShow3DViewer(true);
        setHasRendered3DViewer(true);
      } else if (hasPointCloud && currentViewMode === 'Panorama') {
        setShowWorkingE57Viewer(true);
        setHasRenderedE57Viewer(true);

        // Also enable GEOTIFF viewer if available for panorama mode minimap (skip for RVT)
        if (hasGEOTIFF && !hasRVTFile) {
          setShowGEOTIFFViewer(true);
          setHasRenderedGEOTIFFViewer(true);
        }
      } else if (hasGEOTIFF && currentViewMode === '2D' && !hasRVTFile) {
        // Skip for RVT - uses Autodesk minimap
        setShowGEOTIFFViewer(true);
        setHasRenderedGEOTIFFViewer(true);
      } else {
        // No specific viewer triggered

        /* COMMENTED OUT - DWG logic replaced with GEOTIFF
        setShowDWGViewer(true);
        setHasRenderedDWGViewer(true);
        */
      }
    }
  };

  // Ensure hasRenderedGEOTIFFViewer is set when in 3D mode with GEOTIFF assets
  // This catches cases where flag wasn't set during asset selection
  // Skip for RVT assets - they have built-in minimap
  useEffect(() => {
    if (
      currentViewMode === '3D' &&
      selectedAsset?.files?.geotiff &&
      !hasRenderedGEOTIFFViewer &&
      !hasRVTFile
    ) {
      setHasRenderedGEOTIFFViewer(true);
    }
  }, [currentViewMode, selectedAsset, hasRenderedGEOTIFFViewer, hasRVTFile, setHasRenderedGEOTIFFViewer]);

  // Header icons: the app's stroke style (see .dt-menu-ic) — no emoji, no text glyphs.
  const headerIcon = (d: React.ReactNode, size = 18) => (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" className="dt-header-ic">{d}</svg>
  );
  const HEADER_IC = {
    chevronDown: <path d="m6 9 6 6 6-6" />,
    check: <path d="M20 6 9 17l-5-5" />,
    building: <><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M9 7h1M14 7h1M9 11h1M14 11h1M9 15h1M14 15h1M10 21v-3h4v3" /></>,
    storey: <><path d="m12 3 9 4.5-9 4.5-9-4.5z" /><path d="m3 12 9 4.5 9-4.5M3 16.5 12 21l9-4.5" /></>,
    room: <><path d="M3 21h18M5 21V3h14v18" /><path d="M15 12h.01" /></>,
    site: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" /></>,
    pin: <><path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" /><circle cx="12" cy="9.5" r="2.5" /></>,
    asset: <><path d="m21 16-9 5-9-5V8l9-5 9 5z" /><path d="m3.3 7 8.7 5 8.7-5M12 22V12" /></>,
    star: <path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1 6.2L12 17.3 6.5 20.2l1-6.2L3 9.6l6.2-.9z" />,
    logout: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5M21 12H9" /></>,
    search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>,
    sites: <><path d="M3 21h18M5 21V7l7-4 7 4v14" /><path d="M9 21v-6h6v6" /></>,
  };
  const locationIconFor = (type: string) => {
    const kind = (type || '').toLowerCase();
    if (kind.includes('storey') || kind.includes('floor') || kind.includes('level')) return HEADER_IC.storey;
    if (kind.includes('building')) return HEADER_IC.building;
    if (kind.includes('space') || kind.includes('room')) return HEADER_IC.room;
    if (kind.includes('site')) return HEADER_IC.site;
    return HEADER_IC.pin;
  };

  const getLanguageLabel = (lang: 'en' | 'he') => {
    return lang === 'en' ? 'EN' : 'HE'; // Language label helper function
  };

  // Debug current state before render
  const currentFiles = getCurrentViewFiles();
  const hasIfcFile = currentFiles.find(f => f.name.toLowerCase().endsWith('.ifc'));

  const webIfcPanoramaSyncState =
    isPanoramaPage
      ? currentPanoramaState
      : (currentViewMode === '3D' ? pendingIfcPanoramaPoseSync?.state ?? null : null);
  const webIfcPanoramaSyncKey =
    !isPanoramaPage && currentViewMode === '3D'
      ? pendingIfcPanoramaPoseSync?.key ?? null
      : null;
  const handleIfcTargetPoseApplied = useCallback((key: number) => {
    setPendingIfcPanoramaPoseSync(current => current?.key === key ? null : current);
  }, []);

  // Memoize targetPosition to prevent unnecessary re-renders
  const targetPosition = useMemo(() => {
    if (!webIfcPanoramaSyncState?.position) return null;
    if (currentPanorama3DAnchor && modelBounds) {
      return {
        x: currentPanorama3DAnchor.x + modelBounds.min.x,
        y: webIfcPanoramaSyncState.position.z,
        z: modelBounds.max.y + modelBounds.min.z - currentPanorama3DAnchor.y
      };
    }

    const { x, y, z } = webIfcPanoramaSyncState.position;
    return {
      x: x,
      y: z,  // Z becomes Y in IFC
      z: y   // Y becomes Z in IFC
    };
  }, [webIfcPanoramaSyncState?.position, currentPanorama3DAnchor, modelBounds]);

  // Memoize targetRotation to prevent unnecessary re-renders
  const targetRotation = useMemo(() => {
    if (!webIfcPanoramaSyncState?.rotation) return null;
    const { yaw, pitch } = webIfcPanoramaSyncState.rotation;
    return {
      yaw: yaw,
      pitch: pitch,
      // World-corrected heading (setup yaw + view yaw) — required by the
      // panorama 3D overlay, where each scan has its own scanner orientation
      worldYaw: Number.isFinite(webIfcPanoramaSyncState.worldYaw)
        ? webIfcPanoramaSyncState.worldYaw
        : yaw
    };
  }, [webIfcPanoramaSyncState?.rotation, webIfcPanoramaSyncState?.worldYaw]);

  // EARLY RETURNS - must be AFTER all hooks (React Rules of Hooks)
  if (publicInviteToken) {
    return <AcceptInvitationPage token={publicInviteToken} />;
  }

  // Show loading spinner while checking authentication
  if (authLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: colors.background
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: `4px solid ${colors.border}`,
          borderTopColor: colors.primary,
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite'
        }}></div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Show loading while RBAC data is being fetched
  if (rbacLoading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: colors.background,
        color: colors.textPrimary
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: `3px solid ${colors.border}`,
          borderTopColor: colors.primary,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <p style={{ marginTop: '16px', color: colors.textSecondary }}>Loading permissions...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Show the no-access page only when the SERVER agrees there is nothing to show.
  //
  // This used to be `roles.length === 0` — a count of user_site_roles rows. That is one of
  // SIX access paths, so a user whose access comes from a group grant, an asset group, an
  // onboarding assignment, or from having created the site has no role rows and was told
  // "you haven't been assigned to any sites" while the server was granting them sites.
  // That is exactly what happened to a user holding three sites through a group with role
  // 'account_admin': backend access, front door bolted.
  //
  // accessibleSiteCount comes from /rbac/my-sites — the same predicate the 59 gated routes
  // use. null means the answer has not arrived (or the call failed), and null must NEVER
  // block: locking someone out because a request failed is the worse error of the two.
  const hasNoAccessibleSites = accessibleSiteCount !== null && accessibleSiteCount === 0;
  // A site creator with no site yet is let through: the Sites page is where they make one.
  if (!isBrowserRegressionRunnerPage && !isAdmin && !canCreateSites && roles.length === 0 && hasNoAccessibleSites) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: colors.background,
        color: colors.textPrimary,
        padding: '40px',
        textAlign: 'center'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: 'rgba(239, 68, 68, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '24px'
        }}>
          <span style={{ fontSize: '32px' }}>&#128274;</span>
        </div>
        <h1 style={{ margin: '0 0 16px 0', fontSize: '28px', fontWeight: '600' }}>
          No Site Access
        </h1>
        <p style={{ margin: '0 0 24px 0', fontSize: '16px', color: colors.textSecondary, maxWidth: '500px' }}>
          Your account has been created but you haven't been assigned to any sites yet.
          Please contact your administrator to request access to a site.
        </p>
        <div style={{
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: '8px',
          padding: '20px',
          maxWidth: '400px'
        }}>
          <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: colors.textSecondary }}>
            Logged in as:
          </p>
          <p style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '500' }}>
            {authUser?.email}
          </p>
          <button
            onClick={() => logout()}
            className="btn"
            style={{
              padding: '10px 24px',
              fontSize: '14px',
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              color: colors.textPrimary,
              cursor: 'pointer',
              borderRadius: '6px'
            }}
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  // ── Header helpers ─────────────────────────────────────────────────────────────────
  const closeHeaderMenus = () => {
    setLanguageDropdownOpen(false); setUserDropdownOpen(false);
    setSiteDropdownOpen(false); setShowSearchResults(false);
  };
  const loadHeaderSites = async () => {
    setHeaderSiteFilter('');
    try {
      const list = await assetService.getAssets();
      setHeaderSites([...(list || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    } catch {
      setHeaderSites([]);
    }
  };
  // THE SITE SWITCHER: the same switch the ticket links do — load, select, hand to the app —
  // and stay on the page you are on.
  const switchSite = async (siteId: string) => {
    if (siteId === selectedAsset?.id) return;
    const site = await assetService.getAsset(siteId);
    if (!site) { showErrorToast(t('dt_tickets_siteUnavailable')); return; }
    assetService.selectAsset(siteId);
    handleAssetSelected(site);
  };
  const shownHeaderSites = (headerSites || []).filter(site =>
    (site.name || '').toLocaleLowerCase().includes(headerSiteFilter.trim().toLocaleLowerCase()));
  const headerInitials = (authUser?.name || authUser?.email || 'U').split(/[\s@._-]+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      direction: currentLanguage === 'he' ? 'rtl' : 'ltr'
    }}>
      {newVersionReady && <NewVersionNotice layout="bar" />}
      {/* Above everything, including the header: while a simulation is on, the first thing
          you should see is that it is on. Renders nothing when it is not. */}
      <ViewAsBanner />
      {/* Owned HERE, not by the user menu: the dropdown closes on any outside click, and a
          picker living inside it would close with it. */}
      <ViewAsDialog
        open={viewAsOpen}
        onClose={() => setViewAsOpen(false)}
        siteId={selectedAsset?.id}
        siteName={selectedAsset?.name}
      />
      {(whatsNewOpen || whatsNewDue) && <WhatsNewDialog onClose={closeWhatsNew} />}

      {/* Header */}
      <header className="dt-app-header" style={{ marginInlineStart: leftMenuCollapsed ? '60px' : '268px' }}
        onKeyDown={event => {
          if (event.key === 'Escape') {
            const trigger = event.currentTarget.querySelector<HTMLButtonElement>('[aria-expanded="true"]');
            trigger?.focus();
            closeHeaderMenus();
          }
        }}>
        <div className="dt-header-identity">
          {/* THE SITE, AS A SWITCHER. The page's own title already names the page, so the header
              names only the site — and lets you change it from anywhere. */}
          <div className="dt-header-site">
            <button type="button" className="dt-header-site-trigger" aria-haspopup="true" aria-expanded={siteDropdownOpen}
              aria-controls="header-site-menu" title={t('headerSwitchSite')}
              onClick={event => {
                event.stopPropagation();
                const next = !siteDropdownOpen;
                closeHeaderMenus();
                setSiteDropdownOpen(next);
                if (next) void loadHeaderSites();
              }}>
              <span className="dt-header-site-tile">{headerIcon(HEADER_IC.building)}</span>
              <strong><Bdi>{selectedAsset?.name || t('noAssetSelected')}</Bdi></strong>
              {headerIcon(HEADER_IC.chevronDown, 16)}
            </button>
            {siteDropdownOpen && <div id="header-site-menu" className="dt-pop dt-header-menu dt-header-site-menu" onClick={event => event.stopPropagation()}>
              {(headerSites?.length || 0) > 6 && <label className="dt-header-menu-filter">
                {headerIcon(HEADER_IC.search, 16)}
                <input autoFocus value={headerSiteFilter} onChange={event => setHeaderSiteFilter(event.target.value)}
                  placeholder={t('headerFindSite')} aria-label={t('headerFindSite')} />
              </label>}
              <div className="dt-grpttl">{t('headerSites')}</div>
              {headerSites === null
                ? <div className="dt-header-menu-state">{t('dt_loading')}</div>
                : shownHeaderSites.length === 0
                  ? <div className="dt-header-menu-state">{t('headerNoSitesFound')}</div>
                  : shownHeaderSites.map(site => (
                    <button key={site.id} type="button" className="dt-header-menu-action"
                      aria-current={site.id === selectedAsset?.id ? 'true' : undefined}
                      onClick={() => { setSiteDropdownOpen(false); void switchSite(site.id); }}>
                      {headerIcon(HEADER_IC.building)}
                      <span className="grow"><Bdi>{site.name}</Bdi></span>
                      {site.id === selectedAsset?.id && headerIcon(HEADER_IC.check, 16)}
                    </button>
                  ))}
              <div className="dt-header-menu-sep" />
              <button type="button" className="dt-header-menu-action" onClick={() => {
                setSiteDropdownOpen(false);
                setActiveMenuItem('sites');
                sessionStorage.setItem('mabat3d_activePage', 'sites');
              }}>
                {headerIcon(HEADER_IC.sites)}<span className="grow">{t('headerManageSites')}</span>
              </button>
            </div>}
          </div>
        </div>

        <div className="dt-header-utilities">
          <div className="dt-header-search" onClick={event => event.stopPropagation()}
            onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setShowSearchResults(false); }}>
            <label className="dt-header-search-field">
              {headerIcon(HEADER_IC.search, 16)}
              <input type="search" disabled={!selectedAsset} aria-label={t('headerSearch')}
                placeholder={t(selectedAsset ? 'headerSearch' : 'headerSelectSite')}
                value={searchTerm} onChange={handleSearchChange} aria-expanded={showSearchResults} aria-controls="header-search-results"
                onFocus={() => {
                  setLanguageDropdownOpen(false); setUserDropdownOpen(false); setSiteDropdownOpen(false);
                  if (searchTerm.trim().length >= 2) setShowSearchResults(true);
                }} onKeyDown={event => {
                  if (event.key === 'ArrowDown') { event.preventDefault(); document.querySelector<HTMLButtonElement>('#header-search-results button')?.focus(); }
                }}/>
              {selectedAsset && <span className="dt-tag" title={selectedAsset.name}>{t('headerThisSite')}</span>}
            </label>

            {/* Search results: one card, section titles, stroke icons — the app's popover look. */}
            {showSearchResults && (
              <div id="header-search-results" className="dt-header-search-results" aria-label={t('headerSearch')} aria-busy={searchLoading}
                onKeyDown={event => {
                  if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
                  const buttons = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('button'));
                  const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
                  event.preventDefault(); buttons[(index + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length]?.focus();
                }}>
                <div className="dt-header-search-scope">{t('headerThisSite')}: <Bdi>{selectedAsset?.name}</Bdi></div>
                {searchLoading ? (
                  <div className="dt-header-menu-state">{t('headerSearching')}</div>
                ) : (searchError || searchResults.locations.length === 0 && searchResults.pois.length === 0) ? (
                  <div className="dt-header-menu-state">{t(searchError ? 'headerSearchError' : 'headerNoResults')}</div>
                ) : (
                  <>
                    {searchResults.locations.length > 0 && (
                      <>
                        <div className="dt-grpttl dt-header-search-group">{t('locations')} · {searchResults.locations.length}</div>
                        {searchResults.locations.map((location: any) => {
                          const locationPOIs = searchResults.pois.filter((poi: any) => poi.location_id === location.id);
                          return (
                            <div key={location.id}>
                              <button type="button" className="dt-header-search-result" onClick={() => {
                                setShowSearchResults(false);
                                setSearchTerm('');
                                setFocusedLocationId(location.id);
                                setActiveMenuItem('locations');
                              }}>
                                <span className="dt-header-search-ic">{headerIcon(locationIconFor(location.type))}</span>
                                <span className="dt-header-search-text">
                                  <span className="t"><Bdi>{location.name}</Bdi></span>
                                  <span className="m">{[location.type, location.asset_name].filter(Boolean).join(' · ')}</span>
                                </span>
                              </button>
                              {locationPOIs.map((poi: any) => (
                                <button type="button" className="dt-header-search-result nested" key={poi.id} onClick={() => {
                                  setShowSearchResults(false);
                                  setSearchTerm('');
                                  // Pass raw poi object - handlePOINavigation handles both IFC and RVT
                                  handlePOINavigation(poi as any);
                                }}>
                                  <span className="dt-header-search-ic">{headerIcon(HEADER_IC.asset)}</span>
                                  <span className="dt-header-search-text">
                                    <span className="t"><Bdi>{poi.title || t('headerUntitledAsset')}</Bdi></span>
                                    <span className="m">{poi.subcategory || poi.poi_type_name || t('headerAssetFallback')}</span>
                                  </span>
                                </button>
                              ))}
                            </div>
                          );
                        })}
                      </>
                    )}

                    {/* Standalone POIs (not under a location that was found) */}
                    {(() => {
                      const locationIds = new Set(searchResults.locations.map((l: any) => l.id));
                      const standalonePOIs = searchResults.pois.filter(
                        (poi: any) => !poi.location_id || !locationIds.has(poi.location_id)
                      );
                      if (standalonePOIs.length === 0) return null;
                      return (
                        <>
                          <div className="dt-grpttl dt-header-search-group">{t('assets')} · {standalonePOIs.length}</div>
                          {standalonePOIs.map((poi: any) => (
                            <button type="button" className="dt-header-search-result" key={poi.id} onClick={() => {
                              setShowSearchResults(false);
                              setSearchTerm('');
                              // Pass raw poi object - handlePOINavigation handles both IFC and RVT
                              handlePOINavigation(poi as any);
                            }}>
                              <span className="dt-header-search-ic">{headerIcon(HEADER_IC.asset)}</span>
                              <span className="dt-header-search-text">
                                <span className="t"><Bdi>{poi.title || t('headerUntitledAsset')}</Bdi></span>
                                <span className="m">{[poi.subcategory || poi.poi_type_name || t('headerAssetFallback'), poi.asset_name, poi.location_name].filter(Boolean).join(' · ')}</span>
                              </span>
                            </button>
                          ))}
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Language — the same button shape as the bell */}
          <div className="dt-header-menu-anchor">
            <button type="button" className="dt-hbtn dt-hbtn--text" aria-label={t('headerLanguage')} aria-expanded={languageDropdownOpen}
              onClick={(e) => {
                e.stopPropagation();
                const next = !languageDropdownOpen;
                closeHeaderMenus();
                setLanguageDropdownOpen(next);
              }}>
              <span>{currentLanguage.toUpperCase()}</span>{headerIcon(HEADER_IC.chevronDown, 14)}
            </button>
            {languageDropdownOpen && (
              <div className="dt-pop dt-header-menu dt-header-menu--end dt-header-lang-menu" onClick={(e) => e.stopPropagation()}>
                {(['en', 'he'] as const).map(lang => (
                  <button key={lang} type="button" className="dt-header-menu-action"
                    aria-current={currentLanguage === lang ? 'true' : undefined}
                    onClick={() => { setLanguage(lang); setLanguageDropdownOpen(false); }}>
                    <span className="grow" lang={lang}>{lang === 'en' ? 'English' : 'עברית'}</span>
                    {currentLanguage === lang && headerIcon(HEADER_IC.check, 16)}
                  </button>
                ))}
              </div>
            )}
          </div>

          <NotificationBell dismiss={languageDropdownOpen || userDropdownOpen || siteDropdownOpen || showSearchResults}
            onOpen={() => closeHeaderMenus()} onOpenServiceCalls={(siteId, callId) => void openTicket(siteId, callId)} />

          {/* Account */}
          <div className="dt-header-menu-anchor">
            <button type="button" className="dt-header-account" aria-label={t('headerAccount')} aria-expanded={userDropdownOpen}
              onClick={(e) => {
                e.stopPropagation();
                const next = !userDropdownOpen;
                closeHeaderMenus();
                setUserDropdownOpen(next);
              }}
              title={authUser?.name || 'User'}>
              <span className="dt-header-avatar">{headerInitials}</span>
            </button>

            {userDropdownOpen && (
              <div className="dt-pop dt-header-menu dt-header-menu--end dt-header-account-menu" onClick={(e) => e.stopPropagation()}>
                <div className="dt-header-account-card">
                  <span className="dt-header-avatar lg">{headerInitials}</span>
                  <div className="who">
                    <strong>{authUser?.name || 'User'}</strong>
                    {authUser?.email && <span>{authUser.email}</span>}
                    {authUser?.organization && <span>{authUser.organization}</span>}
                    {/* THE ROLE ON THE SITE YOU ARE LOOKING AT, not the best one you hold
                        anywhere. It read the whole role list, so someone who is a site admin of
                        one site was badged "Site Admin" while working in a site where they can
                        only read. A platform admin is global, so that one needs no site. */}
                    <span className={`dt-rbadge dt-header-role dt-header-role--${badgeRole.cls}`}>{t(badgeRole.labelKey)}</span>
                  </div>
                </div>
                <div className="dt-header-menu-sep" />

                {/* WHAT'S NEW — reopen the release notes any time (they open by themselves once). */}
                <button type="button" className="dt-header-menu-action" onClick={() => { setUserDropdownOpen(false); setWhatsNewOpen(true); }}>
                  {headerIcon(HEADER_IC.star)}<span className="grow">{t('whatsNew_menu')}</span>
                </button>

                {/* SHOW AS — admins only; the component renders nothing for anyone else. */}
                <ViewAsMenuItem onOpen={() => { setUserDropdownOpen(false); setViewAsOpen(true); }} />

                <div className="dt-header-menu-sep" />
                <button type="button" className="dt-header-menu-action dngr" onClick={() => { setUserDropdownOpen(false); logout(); }}>
                  {headerIcon(HEADER_IC.logout)}<span className="grow">{t('headerLogout')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div style={{ position: 'relative', display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden', direction: currentLanguage === 'he' ? 'rtl' : 'ltr', paddingInlineStart: leftMenuCollapsed ? '60px' : '268px', transition: 'padding 0.3s ease' }}>
        {/* Left Menu - becomes Right Menu in Hebrew */}
        <nav
          className="main-nav-menu"
          style={{
          width: leftMenuCollapsed ? '60px' : '268px',
          background: 'var(--dt-surface-2)',
          color: 'var(--dt-text)',
          transition: 'width 0.3s ease',
          overflowY: 'auto',
          borderRight: currentLanguage === 'he' ? 'none' : '1px solid var(--dt-border)',
          borderLeft: currentLanguage === 'he' ? '1px solid var(--dt-border)' : 'none',
          position: 'fixed',
          top: 0,
          insetInlineStart: 0,
          height: '100vh',
          order: 1,
          zIndex: 'var(--dt-z-header)', // the sidebar is page chrome, like the header
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Menu Toggle Button - aligned with menu items when collapsed */}
          <button
            onClick={() => setLeftMenuCollapsed(!leftMenuCollapsed)}
            style={{
              position: 'absolute',
              // Position above menu items when collapsed: container padding (20px) - button height (~6px) = ~14px
              top: leftMenuCollapsed ? '14px' : '26px',
              // When collapsed: center button to align with menu item icons
              // Menu icons are centered at 30px (20px padding + 10px icon center)
              // Button width is ~22px (6px + 8px padding + content), so left = 30px - 11px = 19px
              left: currentLanguage === 'he' ?
                (leftMenuCollapsed ? 'auto' : '15px') :
                (leftMenuCollapsed ? '19px' : 'auto'),
              right: currentLanguage === 'he' ?
                (leftMenuCollapsed ? '19px' : 'auto') :
                (leftMenuCollapsed ? 'auto' : '15px'),
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              color: colors.textSecondary,
              fontSize: '14px',
              cursor: 'pointer',
              padding: '6px 8px',
              borderRadius: '4px',
              transition: 'all 0.2s ease',
              zIndex: 1000,
              boxShadow: `0 2px 4px ${colors.shadowLight}`
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.surfaceHover;
              e.currentTarget.style.borderColor = colors.primary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.surface;
              e.currentTarget.style.borderColor = colors.border;
            }}
          >
            {(leftMenuCollapsed ? currentLanguage !== 'he' : currentLanguage === 'he')
              ? (<svg className="dt-clp-ic" viewBox="0 0 24 24"><path d="M13 17l5-5-5-5" /><path d="M6 17l5-5-5-5" /></svg>)
              : (<svg className="dt-clp-ic" viewBox="0 0 24 24"><path d="M11 17l-5-5 5-5" /><path d="M18 17l-5-5 5-5" /></svg>)}
          </button>
          <div className="dt-brand">
            {!leftMenuCollapsed && (
              <>
                <img
                  src="https://mabat3d.com/_next/static/media/logo.ed3d26f6.svg"
                  alt="Mabat3D"
                  style={{ height: '28px', width: 'auto', filter: colors.textPrimary === '#F8F9FA' ? 'brightness(0) invert(1)' : 'none' }}
                />
                <span className="dtlabel">{currentLanguage === 'he' ? 'תאום דיגיטלי' : 'Digital Twin'}</span>
              </>
            )}
          </div>

          <div style={{
            // Add top padding when collapsed to make room for toggle button
            paddingTop: leftMenuCollapsed ? '40px' : '0'
          }}>
            {menuItems.map(item => {
              const renderRow = (row: MenuItemConfig, isChild: boolean) => (
                <div
                  key={row.id}
                  onClick={() => {
                    if (!row.disabled) {
                      setActiveMenuItem(row.id);
                      // Save the active page to sessionStorage (persists only during tab session)
                      sessionStorage.setItem('mabat3d_activePage', row.id);
                      // Auto-collapse on mobile when selecting a menu item
                      if (window.innerWidth <= 768) {
                        setLeftMenuCollapsed(true);
                      }
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '11px 15px',
                    margin: leftMenuCollapsed ? '2px 8px' : (row.id === 'settings' ? '14px 8px 2px' : '3px 8px'),
                    borderRadius: '9px',
                    ...(isChild && !leftMenuCollapsed ? { paddingInlineStart: '32px' } : {}),
                    cursor: row.disabled ? 'not-allowed' : 'pointer',
                    background: activeMenuItem === row.id ? 'var(--dt-accent-tint)' : 'transparent',
                    borderInlineStart: activeMenuItem === row.id ? '3px solid var(--dt-accent)' : '3px solid transparent',
                    opacity: row.disabled ? 0.5 : 1,
                    transition: 'all 0.15s ease',
                    color: activeMenuItem === row.id ? 'var(--dt-accent-strong)' : 'var(--dt-text-2)',
                    fontWeight: activeMenuItem === row.id ? 600 : 500
                  }}
                  onMouseEnter={(e) => {
                    if (!row.disabled && activeMenuItem !== row.id) {
                      e.currentTarget.style.background = 'var(--dt-surface-2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeMenuItem === row.id) {
                      e.currentTarget.style.background = 'var(--dt-accent-tint)';
                    } else {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <span style={{
                    fontSize: '20px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: currentLanguage === 'he' ?
                      '0' :
                      (leftMenuCollapsed ? '0' : '13px'),
                    marginLeft: currentLanguage === 'he' ?
                      (leftMenuCollapsed ? '0' : '13px') :
                      '0',
                    width: '23px',
                    textAlign: 'center'
                  }}>
                    {MENU_ICON[row.id] ?? row.icon}
                  </span>
                  {!leftMenuCollapsed && (
                    <>
                      <span style={{ fontSize: '1rem', flex: 1 }}>{row.label}</span>
                      {menuCounts[row.id] != null && (
                        <span className="dt-cnt">{menuCounts[row.id]}</span>
                      )}
                      {row.disabled && <span style={{
                        fontSize: '10px',
                        color: colors.warning,
                        marginLeft: currentLanguage === 'he' ? '0' : '8px',
                        marginRight: currentLanguage === 'he' ? '8px' : '0'
                      }}>{currentLanguage === 'he' ? 'בקרוב' : 'SOON'}</span>}
                    </>
                  )}
                </div>
              );

              if (!item.children) return renderRow(item, false);

              // A stored preference wins; with none, the group opens because it holds the
              // current page. See openMenuGroups above for why it is that way round.
              const holdsActivePage = item.children.some(child => child.id === activeMenuItem);
              const isOpen = openMenuGroups[item.id] ?? holdsActivePage;

              // Group heading, drawn as a NAV ROW rather than a caption: same icon slot, same
              // type size, same padding as the items around it. It was an uppercase 11px
              // caption (.dt-navgrp) and that one difference was enough to make a clean
              // sidebar read like a form — a grey label wedged between icon rows.
              //
              // It is NOT interactive: no click, no hover, no active state. The children are
              // the destinations; the heading only names them, and giving it a pressed state
              // would promise a page that does not exist.
              //
              // Children render with isChild=true so they indent under it. With the heading
              // now looking like a row, the indent is what carries the hierarchy.
              return (
                <div key={item.id}>
                  {!leftMenuCollapsed && (
                    <div
                      role="button"
                      tabIndex={0}
                      aria-expanded={isOpen}
                      onClick={() => toggleMenuGroup(item.id, isOpen)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleMenuGroup(item.id, isOpen); }
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dt-surface-2)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '11px 15px',
                        margin: '3px 8px',
                        borderRadius: '9px',
                        // Matches the row's reserved active-indicator strip so the heading's
                        // text lines up with every other label rather than sitting 3px off.
                        borderInlineStart: '3px solid transparent',
                        color: 'var(--dt-text-2)',
                        fontWeight: 500,
                        cursor: 'pointer',
                        background: 'transparent',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {/* Copied from renderRow's icon span verbatim — 23px wide, 13px gutter,
                          mirrored for Hebrew. Any drift here and the heading's icon sits off
                          the column every row below it lines up on. */}
                      <span style={{
                        fontSize: '20px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: currentLanguage === 'he' ? '0' : '13px',
                        marginLeft: currentLanguage === 'he' ? '13px' : '0',
                        width: '23px',
                        textAlign: 'center'
                      }}>
                        {MENU_ICON[item.id] ?? MENU_ICON.users ?? item.icon}
                      </span>
                      <span style={{ fontSize: '1rem', flex: 1 }}>{item.label}</span>
                      {/* Small, muted, at the end — the affordance, not the feature. The
                          previous attempt made the whole heading look like a control and the
                          sidebar read like a form. */}
                      <svg
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        style={{
                          width: '13px', height: '13px', flexShrink: 0,
                          fill: 'none', stroke: 'currentColor', strokeWidth: 2.5,
                          strokeLinecap: 'round', strokeLinejoin: 'round',
                          opacity: 0.55,
                          transform: `rotate(${isOpen ? 90 : (isRTL ? 180 : 0)}deg)`,
                          transition: 'transform 0.18s ease',
                        }}
                      >
                        <path d="M9 18l6-6-6-6" />
                      </svg>
                    </div>
                  )}
                  {(isOpen || leftMenuCollapsed) &&
                    item.children.map(child => renderRow(child, !leftMenuCollapsed))}
                </div>
              );
            })}
          </div>

          {/* Help Section - Below Menu Items */}
          {!leftMenuCollapsed && (
            <div style={{ marginTop: 'auto', padding: '10px 12px 16px' }}>
              <div className="dt-helpcard">
                <img className="himg" src="/happy_papers.png" alt="" />
                <div className="hh">{t('hi')}{(authUser?.name || authUser?.email?.split('@')[0]) ? `, ${(authUser?.name || authUser?.email?.split('@')[0] || '').split(' ')[0]}` : ''}</div>
                <div className="hp">{t('needHelp')} {t('pleaseCheckDocs')}</div>
                <button
                  className="dt-btn sm"
                  onClick={() => setActiveMenuItem('documentation')}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px' }}
                >
                  <svg viewBox="0 0 24 24" style={{ width: 15, height: 15, stroke: 'currentColor', fill: 'none', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' }}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></svg>
                  {t('documentation')}
                </button>
              </div>
            </div>
          )}
        </nav>

        {/* PERSISTENT VIEWERS - Always mounted, positioned after menu */}
        <div style={{
          position: 'absolute',
          width: `calc(100% - ${leftMenuCollapsed ? '60px' : '268px'})`,
          height: '100%', // The body follows the header and any View As banner.
          top: 0,
          left: currentLanguage === 'he' ? '0' : (leftMenuCollapsed ? '60px' : '268px'),
          right: currentLanguage === 'he' ? (leftMenuCollapsed ? '60px' : '268px') : '0',
          zIndex: activeMenuItem === 'viewer' ? 100 : 1, // Keep accessible but lower than active content
          pointerEvents: (activeMenuItem === 'viewer' && (show3DViewer || showWorkingE57Viewer)) ? 'auto' : 'none', // Only enable when actually using persistent viewers - CRITICAL FIX
          transition: 'left 0.3s ease, right 0.3s ease, width 0.3s ease' // Smooth transitions when menu collapses/expands
        }}>
          {/* Persistent 3D Viewer */}
          {hasRendered3DViewer && (
            <div
              data-panorama-ifc-overlay={isIFCPanorama3DOverlayVisible ? 'true' : undefined}
              style={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              top: 0,
              left: 0,
              overflow: 'hidden',
              display: 'block', // Always rendered once initialized
              visibility: isIFC3DViewerVisible ? 'visible' : 'hidden',
              pointerEvents: isIFC3DViewerVisible ? 'auto' : 'none',
              opacity: isIFCPanorama3DOverlayVisible ? panorama3DOverlayOpacity : 1,
              zIndex: isIFCPanorama3DOverlayVisible ? 200 : (show3DViewer ? 10 : 1),
              transition: 'opacity 0.2s ease'
            }}
            onPointerDownCapture={(event) => {
              // Portaled panels sit ON TOP of the overlay; their input is theirs, not the
              // panorama's. See isEventFromViewerControls.
              if (isEventFromViewerControls(event)) return;
              if (routePanoramaOverlayPointer(event.clientX, event.clientY)) {
                stopOverlayPointerEvent(event);
                return;
              }
              if (beginPanoramaOverlayDrag(event)) {
                stopOverlayPointerEvent(event);
              }
            }}
            onPointerMoveCapture={(event) => {
              if (isEventFromViewerControls(event)) return;
              movePanoramaOverlayDrag(event);
            }}
            onPointerUpCapture={(event) => {
              if (isEventFromViewerControls(event)) return;
              endPanoramaOverlayDrag(event);
            }}
            onPointerCancelCapture={endPanoramaOverlayDrag}
            onPointerLeaveCapture={(event) => {
              if (panoramaOverlayDragRef.current?.pointerId === event.pointerId) {
                endPanoramaOverlayDrag(event);
              }
            }}
            onWheelCapture={(event) => {
              // Scrolling the panel's property list must not zoom the panorama.
              if (isEventFromViewerControls(event)) return;
              handlePanoramaOverlayWheel(event);
            }}
            onClickCapture={(event) => {
              // THE close-button bug: this ran before the panel's own onClick and stopped it.
              if (isEventFromViewerControls(event)) return;
              if (shouldSuppressRoutedPanoramaClick(event.clientX, event.clientY)) {
                stopOverlayPointerEvent(event);
              }
            }}
            >
              <ViewerErrorBoundary><Suspense fallback={<ViewerLoadingFallback />}>
                <WebIfcViewer
                      key={selectedAsset?.id || 'no-asset'}
                      asset={selectedAsset}
                      files={files3D}
                      currentLanguage={currentLanguage}
                      focusedPOI={focusedPOI}
                      highlightedPOIGroup={highlightedPOIGroup}
                      targetPosition={(isPanoramaPage || webIfcPanoramaSyncKey !== null) ? targetPosition : null}
                      targetPositionSpace={currentPanorama3DAnchor && modelBounds ? 'world' : 'legacy-ifc'}
                      targetRotation={(isPanoramaPage || webIfcPanoramaSyncKey !== null) ? targetRotation : null}
                      targetFov={(isPanoramaPage || webIfcPanoramaSyncKey !== null) ? (webIfcPanoramaSyncState?.fov ?? null) : null}
                      targetSyncKey={webIfcPanoramaSyncKey}
                      onTargetPoseApplied={handleIfcTargetPoseApplied}
                      panoramaOverlayLock={isIFCPanorama3DOverlayVisible}
                      onWorldCameraPositionChange={handleIfcCameraWorldPositionChange}
                      activeMenuItem={activeMenuItem}
                      syncType={(isPanoramaPage || webIfcPanoramaSyncKey !== null) ? (webIfcPanoramaSyncState?.syncType || null) : null}
                  multiModelMode={true}
                  onLoadedModelsChange={handleLoadedModelsChange}
                  requestedModelFileName={requestedModelFileName}
                  onRequestedModelLoaded={handleRequestedModelLoaded}
                  onModelBoundsChange={(bounds) => {
                    setModelBounds(bounds);
                  }}
                  onCameraPositionChange={(position) => {
                    // Calculate adjusted coordinates: X=X, Y=-Z, Z=Y
                    const adjustedPosition = {
                      x: position.x,
                      y: -position.z, // Y = -Z (inverted)
                      z: position.y   // Z = Y (swapped)
                    };

                    // Calculate adjustedFixedPosition using dynamic model bounds
                    let adjustedFixedPosition;
                    if (modelBounds) {
                      // Use dynamic model bounds (same formula as WebIfcViewer)
                      adjustedFixedPosition = {
                        x: adjustedPosition.x - (-modelBounds.min.x),  // Dynamic X offset
                        y: adjustedPosition.y + modelBounds.max.y,     // Dynamic Y offset
                        z: adjustedPosition.z                          // Z unchanged
                      };
                    } else {
                      // Fallback to old hardcoded values if bounds not available yet
                      adjustedFixedPosition = {
                        x: adjustedPosition.x - 3.15817, // Fallback X offset
                        y: adjustedPosition.y + 2.974,   // Fallback Y offset
                        z: adjustedPosition.z             // Z unchanged
                      };
                    }

                    // The viewer only calls this when the pose actually changed, so a plain
                    // state set is enough (the old _forceUpdate timestamp existed to force
                    // re-renders on the viewer's 60Hz unconditional callback — removed).
                    setCurrent3DCameraState({
                      position,
                      adjustedPosition,
                      adjustedFixedPosition
                    });
                  }}
                  showLayerPanel={isLayerPaneOpen}
                  onLayerPanelToggle={handleLayerPanelToggle}
                  showPanoramaLayer={showPanoramaLayer}
                  onPanoramaLayerToggle={(visible) => setShowPanoramaLayer(visible)}
                  onSpaceBoundaryExtracted={(boundary) => {
                    if (boundary) {
                      logger.debug('[App.tsx] Received space boundary from WebIfcViewer:', boundary.length, 'points');
                    }
                    setActiveSpaceBoundary(boundary);
                  }}
                  onSearchPanelChange={({ isOpen, height }) => {
                    setIfcSearchPanelOpen(isOpen);
                    setIfcSearchPanelHeight(height);
                  }}
                  // Element tickets from the IFC viewer land in the same global modal as the
                  // RVT viewer's (Epic B) — one creation surface for every origin.
                  onOpenServiceCall={setServiceCallDraft}
                  onViewPOITickets={handleViewPOITickets}
                  // …and so does "Show in system diagram": the Element Info panel offers the
                  // same actions in both 3D viewers, so the IFC one needs the same navigation.
                  onShowPOIInDiagram={handleShowPOIInDiagram}
                  // Removed automatic POI clearing - POI will stay highlighted until user interacts
                />
              </Suspense></ViewerErrorBoundary>

              {/* Minimap overlay for 3D viewer - only show if 2D image data is available */}
              {/* Removed old Minimap component in favor of GEOTIFFViewer minimap - keeping for reference */}
            </div>
          )}

          {/* Autodesk Viewer for RVT files (direct viewing without IFC conversion) */}
          {hasRenderedAutodeskViewer && showAutodeskViewer && autodeskViewerConfig && (
            <div
              data-panorama-autodesk-overlay="true"
              style={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              top: 0,
              left: 0,
              overflow: 'hidden',
              display: 'block',
              visibility: (activeMenuItem === 'viewer' && showAutodeskViewer && (currentViewMode === '3D' || currentViewMode === '2D' || currentViewMode === 'Panorama')) ? 'visible' : 'hidden',
              // Markers must be clickable in the marker layer too, not only under the overlay —
              // the capture handlers below route drags to the panorama, so this does not steal
              // the camera.
              pointerEvents: (activeMenuItem === 'viewer' && showAutodeskViewer && (currentViewMode === '3D' || currentViewMode === '2D' || isAutodeskPanorama3DOverlayVisible || isAutodeskPanoramaMarkerLayer)) ? 'auto' : 'none',
              opacity: (activeMenuItem === 'viewer' && currentViewMode === 'Panorama') ? ((isAutodeskPanorama3DOverlayVisible || isAutodeskPanoramaMarkerLayer) ? 1 : 0) : 1,
              // Markers mode (overlay off, or the slider at "markers"): NO z-index here, so this
              // container is not a stacking context and the 3D canvas — which carries the SCREEN
              // blend itself (useAutodeskPanoramaMarkers, applyHiddenModelLook) — blends against
              // the photo, which sits below it at z-index -1 (data-panorama-photo-layer). The blend
              // used to live on this whole container, so every panel and button inside it was
              // screen-blended into the photo too: the Planning/Browser pane vanished and the tool
              // rail went pale at "markers" (Idan 2026-10-01). On the canvas alone it needs every
              // isolating layer between canvas and photo gone — the 2026-08-25 black rectangle was
              // the canvas blending inside this then-isolated container.
              //
              // Overlay ON (a %) is layered the same way (Idan 2026-10-02: "with overlay i cant
              // [scroll the planning pane]"). At z-index 200 this container was a stacking context,
              // so everything the 3D viewer draws on top — the bottom-left tools, the Search table,
              // the Planning and Browser panes — sat UNDER the panorama's input shield (250): the
              // wheel zoomed the photo instead of scrolling them, their fields took no typing, their
              // dropdowns and sliders did nothing. Not a stacking context, the 3D picture still
              // stays under the shield (Autodesk's own layer, z-index 1) and the photo under it (-1),
              // while those panels (z-index ≥ 999) rise above the shield.
              zIndex: (activeMenuItem === 'viewer' && currentViewMode === 'Panorama')
                ? ((isAutodeskPanoramaMarkerLayer || isPanoramaMarkersOnly || isAutodeskPanorama3DOverlayVisible) ? 'auto' : 1)
                : (showAutodeskViewer ? 20 : 1),
              transition: 'opacity 0.2s ease'
            }}
            onPointerDownCapture={(event) => {
              // Portaled panels sit ON TOP of the overlay; their input is theirs, not the
              // panorama's. See isEventFromViewerControls.
              if (isEventFromViewerControls(event)) return;
              if (routePanoramaOverlayPointer(event.clientX, event.clientY)) {
                stopOverlayPointerEvent(event);
                return;
              }
              if (beginPanoramaOverlayDrag(event)) {
                stopOverlayPointerEvent(event);
              }
            }}
            onPointerMoveCapture={(event) => {
              if (isEventFromViewerControls(event)) return;
              movePanoramaOverlayDrag(event);
            }}
            onPointerUpCapture={(event) => {
              if (isEventFromViewerControls(event)) return;
              endPanoramaOverlayDrag(event);
            }}
            onPointerCancelCapture={endPanoramaOverlayDrag}
            onPointerLeaveCapture={(event) => {
              if (panoramaOverlayDragRef.current?.pointerId === event.pointerId) {
                endPanoramaOverlayDrag(event);
              }
            }}
            onWheelCapture={(event) => {
              // Scrolling the panel's property list must not zoom the panorama.
              if (isEventFromViewerControls(event)) return;
              handlePanoramaOverlayWheel(event);
            }}
            onClickCapture={(event) => {
              // THE close-button bug: this ran before the panel's own onClick and stopped it.
              if (isEventFromViewerControls(event)) return;
              if (shouldSuppressRoutedPanoramaClick(event.clientX, event.clientY)) {
                stopOverlayPointerEvent(event);
              }
            }}
            >
              <Suspense fallback={<ViewerLoadingFallback />}>
                <ViewerErrorBoundary>
                <AutodeskViewer
                  ref={autodeskViewerRef}
                  onRequestViewMode={handleViewModeChange}
                  onStartCustomRoomDrawing={handleStartCustomRoomDrawing}
                  onStartCustomStoreyDrawing={handleStartCustomStoreyDrawing}
                  onEditCustomLocation={handleEditCustomRoom}
                  onEditCustomStoreyLocation={handleEditCustomStorey}
                  onOpenDiagrams={() => setActiveMenuItem('diagrams')}
                  onShowPOIInDiagram={handleShowPOIInDiagram}
                  onViewPOITickets={handleViewPOITickets}
                  urn={autodeskViewerConfig.urn}
                  connectionId={autodeskViewerConfig.connectionId}
                  assetId={autodeskViewerConfig.assetId}
                  onOpenServiceCall={setServiceCallDraft}
                  viewables={autodeskViewerConfig.viewables}
                  modelFileName={autodeskViewerConfig.fileName}
                  defaultLevelName={autodeskViewerConfig.defaultLevelName}
                  defaultLevelElevation={autodeskViewerConfig.defaultLevelElevation}
                  initialViewableGuid={autodeskViewerConfig.initialViewableGuid}
                  accRegion={autodeskViewerConfig.accRegion}
                  roomSourceViewableGuid={autodeskViewerConfig.roomSourceViewableGuid}
                  additionalModels={effectiveAdditionalRVTModels}
                  onLoadedModelsChange={handleLoadedModelsChange}
                  showLayerPanel={isLayerPaneOpen}
                  onLayerPanelToggle={handleLayerPanelToggle}
                  focusedPOI={focusedRVTPOI}
                  onFocusedPOIHandled={handleFocusedPOIHandled}
                  highlightedPOIGroup={highlightedPOIGroup}
                  pois={loadedPOIs}
                  showAssetIcons2D={showAssetIcons2D}
                  viewMode={currentViewMode}
                  isVisible={activeMenuItem === 'viewer' && showAutodeskViewer && (currentViewMode === '3D' || currentViewMode === '2D' || currentViewMode === 'Panorama')}
                  assetCustomData={selectedAsset?.custom_data || null}
                  assetCustomDataAssetId={selectedAsset?.id || null}
                  currentPanoramaState={currentPanoramaState}
                  onPanoramaNavigation={handlePanoramaNavigation}
                  onRegisterPanoramaAssetFinder={registerPanoramaAssetFinder}
                  panoramaBrowserOpen={isLayerPaneOpen}
                  onClosestPanoramaNavigationTargetChange={handleClosestAutodeskPanoramaTargetChange}
                  onVisiblePanoramaIndicesChange={setVisiblePanoramaIndices}
                  onVisiblePanoramaKeysChange={setVisiblePanoramaKeys}
                  onCurrentPanoramaStoreyChange={setCurrentPanoramaStorey}
                  onAvailableStoreysChange={setAvailableStoreys}
                  onPanoramaFloorNavigationStateChange={setPanoramaFloorNavigationState}
                  showPanoramaLayer={showPanoramaLayer}
                  onPanoramaLayerToggle={(visible) => setShowPanoramaLayer(visible)}
                  enabledAssetSystems={enabledAssetSystems}
                  onAssetSystemsChange={toggleAssetSystem}
                  onAllAssetSystemsToggle={toggleAllAssetSystems}
                  isAssetsLayerOpen={isAssetsLayerOpen}
                  onAssetsLayerOpenToggle={() => setIsAssetsLayerOpen((open) => !open)}
                  onAssetSystemLayersChange={setAssetSystemLayers}
                  onElementSelect={(dbId, props) => {
                    logger.debug('[App.tsx] Autodesk element selected:', dbId, props.name);
                  }}
                  onViewerReady={(viewer) => {
                    logger.debug('[App.tsx] Autodesk Viewer ready');
                    const go = viewer?.model?.getData()?.globalOffset;
                    setModelGlobalOffset({ x: go?.x || 0, y: go?.y || 0, z: go?.z || 0 });
                  }}
                  onInteractionReady={handleAutodeskInteractionReady}
                  onModelsLoadProgress={(settled, total) => {
                    updateViewerBlockProgress('3D', (settled / total) * 100);
                    setAutodeskModelsProgress((settled / total) * 100);
                  }}
                  onPOIsExtracted={handlePOIsExtracted}
                  onNavigateToViewer={handlePOINavigation}
                  onHighlightGroup={handleHighlightGroup}
                  onClearHighlightedGroup={handleClearHighlightedGroup}
                  onSelectionChange={setViewerSelectedDbIds}
                  viewerSelectedDbIds={viewerSelectedDbIds}
                  customRoomDrawingMode={customRoomDrawingMode}
                  onCustomRoomDrawingComplete={handleCustomRoomDrawingComplete}
                  customStoreyDrawingMode={customStoreyDrawingMode}
                  onCustomStoreyDrawingComplete={handleCustomStoreyDrawingComplete}
                  editingCustomLocation={editingCustomLocation}
                  preSelectedBottomStoreyId={preSelectedBottomStoreyId}
                  onClearNavigatedLocation={handleClearNavigatedLocation}
                  onSceneInsetsChange={setAutodeskSceneInsets}
                  viewerOpacity={isAutodeskPanorama3DOverlayVisible ? panorama3DOverlayOpacity : 1}
                  markersOnly={isAutodeskPanoramaMarkerLayer || isPanoramaMarkersOnly}
                  hideViewerChrome={currentViewMode === 'Panorama' && !isAutodeskPanorama3DOverlayVisible}
                  hideTopControlBar={isAutodeskPanorama3DOverlayVisible}
                  onPhotoClickToolChange={setIsAutodeskPhotoClickToolActive}
                  onRegisterPhotoPressClaim={registerPhotoPressClaim}
                />
                </ViewerErrorBoundary>
              </Suspense>
            </div>
          )}

          {isPanoramaOverlayInputRoutingEnabled && (
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 250,
                pointerEvents: 'auto',
                // A tool takes the photo's clicks (measuring, placing): the photo is where you click.
                cursor: isAutodeskPhotoClickToolActive ? 'crosshair' : 'grab',
                touchAction: 'none',
                userSelect: 'none',
                WebkitUserSelect: 'none'
              }}
              onPointerDownCapture={handlePanoramaOverlayShieldPointerDown}
              onPointerMoveCapture={handlePanoramaOverlayShieldPointerMove}
              onPointerUpCapture={handlePanoramaOverlayShieldPointerEnd}
              onPointerCancelCapture={(event) => handlePanoramaOverlayShieldPointerEnd(event, { replayClick: false })}
              onPointerLeaveCapture={(event) => {
                if (panoramaOverlayDragRef.current?.pointerId === event.pointerId) {
                  handlePanoramaOverlayShieldPointerEnd(event, { replayClick: false });
                }
              }}
              onMouseDownCapture={stopOverlayPointerEvent}
              onMouseMoveCapture={stopOverlayPointerEvent}
              onMouseUpCapture={stopOverlayPointerEvent}
              onClickCapture={handlePanoramaOverlayShieldClick}
              onDoubleClickCapture={handlePanoramaOverlayShieldDoubleClick}
              onContextMenuCapture={stopOverlayPointerEvent}
              onWheelCapture={handlePanoramaOverlayShieldWheel}
              onTouchStartCapture={stopOverlayPointerEvent}
              onTouchMoveCapture={stopOverlayPointerEvent}
              onTouchEndCapture={stopOverlayPointerEvent}
              onDragStartCapture={stopOverlayPointerEvent}
            />
          )}

          {/* Persistent E57 Panorama Viewer */}
          {(() => {
            const panoramaFiles = filesPanorama.length > 0 ? filesPanorama :
              (selectedAsset?.files?.e57 ? [{ name: 'asset.e57', assetFilePath: 'panoramas_extracted' } as File] : []);
            const hasE57Files = panoramaFiles.some(f => {
              const name = f.name.toLowerCase();
              return name.endsWith('.e57') || name.endsWith('.e57p');
            });
            const shouldRender =
              hasE57Files &&
              (
                hasRenderedE57Viewer ||
                currentViewMode === 'Panorama' ||
                targetPanoramaSetup !== undefined ||
                panoramaNavigationRequest !== null
              );
            const isVisible = activeMenuItem === 'viewer' && currentViewMode === 'Panorama';


            return shouldRender;
          })() && (
            <div data-panorama-photo-viewport="true" style={{
              // NO z-index here: this container also holds Panorama's own buttons (top-left bar,
              // Browser pane, bottom-left menu). Only the PHOTO goes under the 3D layer in markers
              // mode — [data-panorama-photo-layer] below. Putting this whole container at -1
              // (97f12f6) trapped every one of those buttons under the 3D layer: visible through
              // the blend, but every click hit the 3D layer (Idan 2026-10-01: "top left buttons just
              // dont work in panorama").
              // The photo and the 3D layer drawn over it (the model, or only its markers and
              // measurements with the overlay closed) share one frame: when the 3D viewer's Search
              // panel takes the bottom, both shrink to above it.
              width: (isAutodeskPanorama3DOverlayVisible || isAutodeskPanoramaMarkerLayer) ? `calc(100% - ${autodeskSceneInsets.left + autodeskSceneInsets.right}px)` : '100%',
              height: (isAutodeskPanorama3DOverlayVisible || isAutodeskPanoramaMarkerLayer) ? `calc(100% - ${autodeskSceneInsets.bottom}px)` : '100%',
              position: 'absolute',
              top: 0,
              left: (isAutodeskPanorama3DOverlayVisible || isAutodeskPanoramaMarkerLayer) ? autodeskSceneInsets.left : 0,
              overflow: 'hidden',
              display: 'block', // Always rendered once initialized
              visibility: (activeMenuItem === 'viewer' && currentViewMode === 'Panorama') ? 'visible' : 'hidden'
            }}>
              {/* The photo alone — under the 3D layer whenever that layer is shown (markers, or the
                  overlay at a %), in the same stacking context, so the 3D canvas's screen blend
                  reaches it in markers mode (see the Autodesk container above). */}
              <div data-panorama-photo-layer="true" style={{
                position: 'absolute',
                inset: 0,
                zIndex: (activeMenuItem === 'viewer' && currentViewMode === 'Panorama' && (isAutodeskPanoramaMarkerLayer || isPanoramaMarkersOnly || isAutodeskPanorama3DOverlayVisible)) ? -1 : 'auto'
              }}>
              {(() => {
                // Get E57 files from panorama mode specifically, not current view mode
                const panoramaFiles = filesPanorama.length > 0 ? filesPanorama :
                  (selectedAsset?.files?.e57 ? [{ name: 'asset.e57', assetFilePath: 'panoramas_extracted' } as File] : []);

                const hasE57File = panoramaFiles.some(f => {
                  const name = f.name.toLowerCase();
                  return name.endsWith('.e57') || name.endsWith('.e57p');
                });


                return hasE57File && selectedAsset;
              })() && (
                <Suspense fallback={<ViewerLoadingFallback />}>
                  <ViewerErrorBoundary>
                  <WorkingE57PanoramaViewer
                    ref={panoramaViewerRef}
                    key={`persistent-e57-viewer-${selectedAsset?.id || 'no-asset'}-${panoramaViewerMountKey}`}
                    file={(() => {
                      const panoramaFiles = filesPanorama.length > 0 ? filesPanorama :
                        (selectedAsset?.files?.e57 ? [{ name: 'asset.e57', assetFilePath: 'panoramas_extracted' } as File] : []);
                      return panoramaFiles.find(f => {
                        const name = f.name.toLowerCase();
                        return name.endsWith('.e57') || name.endsWith('.e57p');
                      })!;
                    })()}
                    assetId={selectedAsset?.id}
                    assetCustomData={selectedAsset?.custom_data || null}
                    initialSetupIndex={targetPanoramaSetup}
                    navigationRequest={panoramaNavigationRequest}
                    onPanoramaStateChange={handlePanoramaStateChange}
                    visiblePanoramaIndices={visiblePanoramaIndices}
                    visiblePanoramaKeys={visiblePanoramaKeys}
                    currentPanoramaStorey={currentPanoramaStorey}
                    availableStoreys={availableStoreys}
                    modelGlobalOffset={modelGlobalOffset}
                    // overlayMode hides the panorama's OWN nav discs. They must also stand down
                    // when the Autodesk marker layer is showing, or both sets draw at once —
                    // one surface per gesture.
                    overlayMode={isPanorama3DOverlayVisible || isAutodeskPanoramaMarkerLayer}
                    onPOIClick={(poi) => handlePOINavigation(poi as POI, { preservePanoramaCamera: true })}
                    onError={(error) => {
                      setViewerError(error);
                      setShowWorkingE57Viewer(false);
                      finishViewerBlock('Panorama');
                    }}
                    onProgress={(progress) => {
                      setLoadingProgress(progress);
                      // The photo only marks itself ready; the gate effect lifts the screen once
                      // the 3D model behind it is in too. Its bar stops tracking the photo then.
                      if (panoramaPhotoReadyFor !== selectedAsset?.id) updateViewerBlockProgress('Panorama', progress);
                      if (progress >= 100) {
                        setPanoramaPhotoReadyFor(selectedAsset?.id ?? null);
                      }
                    }}
                    onReady={() => setPanoramaPhotoReadyFor(selectedAsset?.id ?? null)}
                  />
                  </ViewerErrorBoundary>
                </Suspense>
              )}
              </div>

              {/* Layer controls for panorama viewer */}
              {activeMenuItem === 'viewer' && currentViewMode === 'Panorama' && (
                <>
                  {/* Layer button — on the Browser pane's side (it flips for Hebrew, like the pane and
                      the bottom-left menus), and never under the 3D / 2D / Panorama switch on the
                      other side: the row reserves room for it, so the opacity bar wraps below instead
                      (Idan 2026-10-01: the opacity number was hidden under the switch). */}
                  <div style={{
                    position: 'absolute',
                    top: '20px',
                    ...browserPaneSide(currentLanguage === 'he', panoramaTopBarInset),
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '10px',
                    maxWidth: `calc(100% - ${panoramaTopBarInset} - ${VIEW_MODE_SWITCH_RESERVE_PX}px)`,
                    zIndex: 10050,
                    pointerEvents: 'auto',
                    transition: 'left 0.3s ease, right 0.3s ease'
                  }}>
                    {/* DT viewer chrome — same .dt-vwbar / .dt-vwticon language as the 3D viewer's
                        top-left toolbar (panorama was still on the old pill buttons).
                        Regression hooks preserved: the suite matches these by title/aria-label
                        ("Show 3D overlay" is a hard requirement), so titles are unchanged and the
                        browser toggle keeps aria-label="Browser" now that it is icon-only. */}
                    <div className="dt-vwbar">
                      <button
                        onClick={() => setIsLayerPaneOpen(!isLayerPaneOpen)}
                        className={`dt-vwticon${isLayerPaneOpen ? ' on' : ''}`}
                        aria-label="Browser"
                        title={isLayerPaneOpen ? "Close browser" : "Show browser"}
                      >
                        <svg viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18"/></svg>
                      </button>
                      <div className="dt-vwdiv" />
                      <button
                        onClick={handlePanorama3DOverlayToggle}
                        aria-pressed={isPanorama3DOverlayVisible}
                        className={`dt-vwticon${isPanorama3DOverlayVisible ? ' on' : ''}`}
                        title={isPanorama3DOverlayVisible ? 'Hide 3D overlay' : 'Show 3D overlay'}
                      >
                        <svg viewBox="0 0 24 24"><path d="M12 3l9 5v8l-9 5-9-5V8z"/><path d="M3 8l9 5 9-5M12 13v10"/></svg>
                      </button>
                      <div className="dt-vwdiv" />
                      <button
                        type="button"
                        onPointerDown={stopPanoramaOverlayControlEvent}
                        onPointerMove={stopPanoramaOverlayControlEvent}
                        onPointerUp={stopPanoramaOverlayControlEvent}
                        onWheel={stopPanoramaOverlayControlEvent}
                        onClick={handlePanoramaFloorDown}
                        disabled={!panoramaFloorNavigationState.canGoDown}
                        className="dt-btn sm"
                        style={{ opacity: panoramaFloorNavigationState.canGoDown ? 1 : 0.5, cursor: panoramaFloorNavigationState.canGoDown ? 'pointer' : 'not-allowed' }}
                        title="Floor down"
                      >
                        Floor -
                      </button>
                      <button
                        type="button"
                        onPointerDown={stopPanoramaOverlayControlEvent}
                        onPointerMove={stopPanoramaOverlayControlEvent}
                        onPointerUp={stopPanoramaOverlayControlEvent}
                        onWheel={stopPanoramaOverlayControlEvent}
                        onClick={handlePanoramaFloorUp}
                        disabled={!panoramaFloorNavigationState.canGoUp}
                        className="dt-btn sm"
                        style={{ opacity: panoramaFloorNavigationState.canGoUp ? 1 : 0.5, cursor: panoramaFloorNavigationState.canGoUp ? 'pointer' : 'not-allowed' }}
                        title="Floor up"
                      >
                        Floor +
                      </button>
                    </div>
                    {isPanorama3DOverlayVisible && (
                      <div
                        className="dt-vwbar dt-vwopacity"
                        onPointerDown={stopPanoramaOverlayControlEvent}
                        onPointerMove={stopPanoramaOverlayControlEvent}
                        onPointerUp={stopPanoramaOverlayControlEvent}
                        onClick={stopPanoramaOverlayControlEvent}
                        onWheel={stopPanoramaOverlayControlEvent}
                        title={isPanoramaMarkersOnly ? 'Markers only — model hidden' : '3D overlay opacity'}
                      >
                        <input
                          type="range"
                          className="dt-vwrange"
                          min={0}
                          max={100}
                          step={5}
                          value={panorama3DOverlayOpacityPercent}
                          onChange={handlePanorama3DOverlayOpacityChange}
                          aria-label="3D overlay opacity"
                        />
                        <span className="dt-vwopacity__value">
                          {isPanoramaMarkersOnly ? 'markers' : `${panorama3DOverlayOpacityPercent}%`}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Layer pane */}
                  {!isPanorama3DOverlayVisible && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '0',
                        ...browserPaneSide(currentLanguage === 'he', isLayerPaneOpen ? '0' : BROWSER_PANE_HIDDEN_OFFSET),
                        width: BROWSER_PANE_WIDTH,
                        height: '100%',
                        background: colors.surface,
                        borderRight: currentLanguage === 'he' ? 'none' : `1px solid ${colors.border}`,
                        borderLeft: currentLanguage === 'he' ? `1px solid ${colors.border}` : 'none',
                        zIndex: 250,
                        transition: 'left 0.3s ease, right 0.3s ease',
                        overflow: 'auto'
                      }}
                    >
                      <div style={{ padding: '20px' }}>
                        <h3 style={{
                          color: colors.textPrimary,
                          marginBottom: '20px',
                          fontSize: '18px',
                          fontWeight: 'bold'
                        }}>
                          Browser
                        </h3>

                      {/* No "2D Map" base-layer row here (Idan 2026-10-02): it was always ticked and could
                          not be changed — a control that does nothing. The 2D view's pane keeps its own. */}

                      {/* Layers — the SHARED component, the same one the RVT viewer pane renders.
                          Before this, the panorama pane had its own hand-rolled Panoramas toggle
                          and no asset controls at all, so flags switched on from the viewer pane
                          could not be switched off here. */}
                      <LayersPanel
                        panoramaCount={selectedAsset?.files?.e57 ? (currentPanoramaState?.totalSetups || 1) : 0}
                        showPanoramaLayer={showPanoramaLayer}
                        onTogglePanoramas={setShowPanoramaLayer}
                        assetSystemLayers={assetSystemLayers}
                        enabledAssetSystems={enabledAssetSystems}
                        onToggleAssetSystem={toggleAssetSystem}
                        onToggleAllAssetSystems={toggleAllAssetSystems}
                        isAssetsOpen={isAssetsLayerOpen}
                        onToggleAssetsOpen={() => setIsAssetsLayerOpen((open) => !open)}
                        isRTL={currentLanguage === 'he'}
                        showHeading={false}
                      />

                      {/* Unified Browser pane — the SAME component the 3D viewers render.
                          Panorama capabilities: the go-to action shows the asset from the
                          nearest panorama that can see it, facing it (RVT; else the nearest
                          scan station — see handleBrowserJumpToNearestStation), tickets,
                          diagrams and the API-backed location/system management. Room
                          highlight / group highlight / room drawing are omitted on purpose:
                          they need a loaded model, and the pane self-gates on its callbacks. */}
                      <POIHierarchyBrowser
                        locationsHierarchy={browserLocationsHierarchy}
                        systemsHierarchy={browserSystemsHierarchy}
                        hasData={browserPOIs.length > 0}
                        emptyMessage={<>No assets for this site yet.</>}
                        focusTarget="panorama"
                        onFocusPOI={panoramaStationLocations.length > 0 ? handleBrowserJumpToNearestStation : undefined}
                        onCreateTicketForPOI={handleBrowserCreateTicket}
                        onViewTicketsForPOI={handleViewPOITickets}
                        onShowPOIInDiagram={handleShowPOIInDiagram}
                        onOpenDiagrams={() => setActiveMenuItem('diagrams')}
                        assetId={selectedAsset?.id}
                        onDataChanged={handleBrowserDataChanged}
                      />
                    </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Main 2D GEOTIFF Viewer - Independent instance for 2D mode (not for RVT - RVT uses built-in minimap) */}
          {hasRenderedGEOTIFFViewer && activeMenuItem === 'viewer' && currentViewMode === '2D' && !hasRVTFile && (
            <div
              style={{
                position: 'absolute',
                top: '0',
                left: '0',
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                zIndex: 200
              }}
            >
              <div
                id="geotiff-viewer-2d-container"
                style={{
                  position: 'absolute',
                  top: '-50%',
                  left: '-50%',
                  width: '200%',
                  height: '200%',
                  pointerEvents: 'auto',
                  display: 'block',
                  overflow: 'visible'
                }}
              >
              {selectedAsset && selectedAsset.files.geotiff && (
                <Suspense fallback={<ViewerLoadingFallback />}>
                  <ViewerErrorBoundary>
                  <GEOTIFFViewer
                    key={`2d-${selectedAsset?.id || 'no-asset'}`}
                    asset={selectedAsset}
                    onImageUpdate={setMinimapImageData}
                    onPanoramaNavigation={handlePanoramaNavigation}
                    isMinimapMode={false}
                    currentPanoramaState={currentPanoramaState}
                    centerOnCurrentPanorama={true}
                    current3DCameraState={current3DCameraState}
                    centerOn3DCamera={true}
                    showPanoramaLayer={showPanoramaLayer}
                    onMouseCoordinatesChange={setMouseCoordinates}
                    onPOIClick={handlePOINavigation}
                    spaceBoundary={activeSpaceBoundary ? { points: activeSpaceBoundary, color: '#48CAE4' } : null}
                    onClearSpaceBoundary={() => setActiveSpaceBoundary(null)}
                    locateTarget={map2DLocateTarget}
                    onReady={() => finishViewerBlock('2D')}
                  />
                  </ViewerErrorBoundary>
                </Suspense>
              )}
              </div>

              {/* Layer controls for 2D viewer - positioned at container level */}
              {selectedAsset && selectedAsset.files.geotiff && (
                <>
                  {/* Layer pane toggle button */}
                  <div style={{
                    position: 'absolute',
                    top: '20px',
                    left: isLayerPaneOpen ? BROWSER_PANE_CONTROL_INSET : '20px',
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'center',
                    zIndex: 300,
                    transition: 'left 0.3s ease'
                  }}>
                    <button
                      onClick={() => setIsLayerPaneOpen(!isLayerPaneOpen)}
                      style={{
                        background: isLayerPaneOpen ? 'var(--dt-accent)' : 'var(--dt-surface)',
                        color: isLayerPaneOpen ? 'var(--dt-accent-ink)' : 'var(--dt-text)',
                        border: isLayerPaneOpen ? '1px solid var(--dt-accent)' : '1px solid var(--dt-border-strong)',
                        borderRadius: '10px',
                        padding: '9px 14px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 700,
                        fontFamily: 'inherit',
                        boxShadow: 'var(--dt-sh-sm)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        position: 'relative',
                        zIndex: 500,
                        pointerEvents: 'auto'
                      }}
                      title={isLayerPaneOpen ? "Close browser" : "Show browser"}
                    >
                      {isLayerPaneOpen ? '◀' : '▶'} Browser
                    </button>

                    {/* Mouse coordinates display - compact horizontal layout */}
                    {mouseCoordinates && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        background: 'rgba(0, 0, 0, 0.85)',
                        color: '#00FF00',
                        padding: '6px 10px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        border: '1px solid rgba(0, 255, 0, 0.3)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
                        pointerEvents: 'none',
                        zIndex: 50
                      }}>
                        <div style={{ fontWeight: 'bold', color: '#FFFFFF', fontSize: '11px' }}>
                          🌍
                        </div>
                        {mouseCoordinates.hasGeoCoords ? (
                          <>
                            <div style={{ color: '#00FFFF', fontWeight: 'bold', fontSize: '11px' }}>
                              X: {mouseCoordinates.geoX?.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                            </div>
                            <div style={{ color: '#00FFFF', fontWeight: 'bold', fontSize: '11px' }}>
                              Y: {mouseCoordinates.geoY?.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                            </div>
                          </>
                        ) : (
                          <>
                            <div style={{ fontSize: '11px' }}>({mouseCoordinates.x}, {mouseCoordinates.y})</div>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Collapsible layer pane */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '0',
                      ...browserPaneSide(currentLanguage === 'he', isLayerPaneOpen ? '0' : BROWSER_PANE_HIDDEN_OFFSET),
                      width: BROWSER_PANE_WIDTH,
                      height: '100%',
                      background: colors.surface,
                      borderRight: currentLanguage === 'he' ? 'none' : `1px solid ${colors.border}`,
                      borderLeft: currentLanguage === 'he' ? `1px solid ${colors.border}` : 'none',
                      overflowY: 'auto',
                      padding: '20px',
                      boxShadow: currentLanguage === 'he' ? '-2px 0 8px rgba(0,0,0,0.1)' : '2px 0 8px rgba(0,0,0,0.1)',
                      zIndex: 250,
                      transition: 'left 0.3s ease, right 0.3s ease',
                      pointerEvents: 'auto'
                    }}
                  >
                    <h3 style={{
                      color: colors.textPrimary,
                      marginBottom: '20px',
                      fontSize: '18px',
                      fontWeight: 'bold'
                    }}>
                      Browser
                    </h3>

                    {/* Base layers */}
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px',
                        borderRadius: '4px',
                        background: colors.backgroundSecondary
                      }}>
                        <input
                          type="checkbox"
                          checked={true}
                          disabled={true}
                          style={{ margin: 0 }}
                        />
                        <span style={{
                          color: colors.textPrimary,
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <span>📐</span>
                          2D Map
                        </span>
                      </div>
                    </div>

                    {/* Panorama layer - only show if site has panoramas */}
                    {selectedAsset?.files?.e57 && (
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px',
                          borderRadius: '4px',
                          background: showPanoramaLayer ? colors.primaryLight : colors.backgroundSecondary,
                          cursor: 'pointer'
                        }}
                        onClick={() => setShowPanoramaLayer(!showPanoramaLayer)}
                        >
                          <input
                            type="checkbox"
                            checked={showPanoramaLayer}
                            onChange={(e) => setShowPanoramaLayer(e.target.checked)}
                            style={{ margin: 0 }}
                          />
                          <span style={{
                            color: colors.textPrimary,
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <span>📍</span>
                            Panoramas
                          </span>
                        </div>
                      </div>
                    )}

                    {/* POI Layer Controls */}
                    <div style={{ marginBottom: '12px' }}>
                      <POILayerControls
                        compact={true}
                      />
                    </div>

                    {/* Unified Browser pane — the SAME component the 3D viewers render.
                        2D capabilities: the go-to action centres the map on the asset (this
                        viewer is prop/event driven — no imperative handle), plus tickets,
                        diagrams and the API-backed location/system management. Room and group
                        highlighting need a model, so they are not offered here. */}
                    <POIHierarchyBrowser
                      locationsHierarchy={browserLocationsHierarchy}
                      systemsHierarchy={browserSystemsHierarchy}
                      hasData={browserPOIs.length > 0}
                      emptyMessage={<>No assets for this site yet.</>}
                      focusTarget="map2d"
                      onFocusPOI={handleBrowserLocateOnMap}
                      onCreateTicketForPOI={handleBrowserCreateTicket}
                      onViewTicketsForPOI={handleViewPOITickets}
                      onShowPOIInDiagram={handleShowPOIInDiagram}
                      onOpenDiagrams={() => setActiveMenuItem('diagrams')}
                      assetId={selectedAsset?.id}
                      onDataChanged={handleBrowserDataChanged}
                    />
                  </div>

                  {/* Bottom-Left Compact Menus - matching 3D viewer layout */}
                  <div style={{
                    position: 'absolute',
                    ...browserPaneSide(currentLanguage === 'he', isLayerPaneOpen ? BROWSER_PANE_CONTROL_INSET : '16px'),
                    bottom: '16px',
                    display: 'flex',
                    gap: '8px',
                    zIndex: 1000,
                    transition: 'left 0.3s ease, right 0.3s ease',
                    pointerEvents: 'auto'
                  }}>
                    {/* Navigation Controls Menu - LEFT */}
                    <div style={{ position: 'relative' }}>
                      {/* Navigation Controls Popup - 3x2 grid like 3D viewer fly mode */}
                      {show2DNavigationMenu && (
                        <div style={{
                          position: 'absolute',
                          bottom: '60px',
                          left: 0,
                          backgroundColor: 'transparent',
                          padding: '10px',
                          animation: 'slideUpFadeIn 0.2s ease-out'
                        }}>
                          {/* 3x2 Grid: Row 1: Rotate Left, Forward, Rotate Right | Row 2: Strafe Left, Backward, Strafe Right */}
                          {/* These dispatch keyboard events to control the 3D camera while button is held */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 50px)', gridTemplateRows: 'repeat(2, 50px)', gap: '8px' }}>
                            {/* Row 1: Rotate Left (Q), Forward (W), Rotate Right (E) */}
                            <button
                              onMouseDown={() => window.dispatchEvent(new KeyboardEvent('keydown', { code: 'KeyQ', key: 'q', bubbles: true }))}
                              onMouseUp={() => window.dispatchEvent(new KeyboardEvent('keyup', { code: 'KeyQ', key: 'q', bubbles: true }))}
                              onMouseLeave={() => window.dispatchEvent(new KeyboardEvent('keyup', { code: 'KeyQ', key: 'q', bubbles: true }))}
                              onTouchStart={() => window.dispatchEvent(new KeyboardEvent('keydown', { code: 'KeyQ', key: 'q', bubbles: true }))}
                              onTouchEnd={() => window.dispatchEvent(new KeyboardEvent('keyup', { code: 'KeyQ', key: 'q', bubbles: true }))}
                              style={{ width: '50px', height: '50px', borderRadius: '6px', border: 'none', backgroundColor: 'var(--primary-color)', color: 'white', cursor: 'pointer', fontSize: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-small)', userSelect: 'none' }}
                              title="Rotate Left (Q)"
                            >
                              ↺
                            </button>
                            <button
                              onMouseDown={() => window.dispatchEvent(new KeyboardEvent('keydown', { code: 'KeyW', key: 'w', bubbles: true }))}
                              onMouseUp={() => window.dispatchEvent(new KeyboardEvent('keyup', { code: 'KeyW', key: 'w', bubbles: true }))}
                              onMouseLeave={() => window.dispatchEvent(new KeyboardEvent('keyup', { code: 'KeyW', key: 'w', bubbles: true }))}
                              onTouchStart={() => window.dispatchEvent(new KeyboardEvent('keydown', { code: 'KeyW', key: 'w', bubbles: true }))}
                              onTouchEnd={() => window.dispatchEvent(new KeyboardEvent('keyup', { code: 'KeyW', key: 'w', bubbles: true }))}
                              style={{ width: '50px', height: '50px', borderRadius: '6px', border: 'none', backgroundColor: 'var(--primary-color)', color: 'white', cursor: 'pointer', fontSize: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-small)', userSelect: 'none' }}
                              title="Forward (W)"
                            >
                              ⬆️
                            </button>
                            <button
                              onMouseDown={() => window.dispatchEvent(new KeyboardEvent('keydown', { code: 'KeyE', key: 'e', bubbles: true }))}
                              onMouseUp={() => window.dispatchEvent(new KeyboardEvent('keyup', { code: 'KeyE', key: 'e', bubbles: true }))}
                              onMouseLeave={() => window.dispatchEvent(new KeyboardEvent('keyup', { code: 'KeyE', key: 'e', bubbles: true }))}
                              onTouchStart={() => window.dispatchEvent(new KeyboardEvent('keydown', { code: 'KeyE', key: 'e', bubbles: true }))}
                              onTouchEnd={() => window.dispatchEvent(new KeyboardEvent('keyup', { code: 'KeyE', key: 'e', bubbles: true }))}
                              style={{ width: '50px', height: '50px', borderRadius: '6px', border: 'none', backgroundColor: 'var(--primary-color)', color: 'white', cursor: 'pointer', fontSize: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-small)', userSelect: 'none' }}
                              title="Rotate Right (E)"
                            >
                              ↻
                            </button>
                            {/* Row 2: Strafe Left (A), Backward (S), Strafe Right (D) */}
                            <button
                              onMouseDown={() => window.dispatchEvent(new KeyboardEvent('keydown', { code: 'KeyA', key: 'a', bubbles: true }))}
                              onMouseUp={() => window.dispatchEvent(new KeyboardEvent('keyup', { code: 'KeyA', key: 'a', bubbles: true }))}
                              onMouseLeave={() => window.dispatchEvent(new KeyboardEvent('keyup', { code: 'KeyA', key: 'a', bubbles: true }))}
                              onTouchStart={() => window.dispatchEvent(new KeyboardEvent('keydown', { code: 'KeyA', key: 'a', bubbles: true }))}
                              onTouchEnd={() => window.dispatchEvent(new KeyboardEvent('keyup', { code: 'KeyA', key: 'a', bubbles: true }))}
                              style={{ width: '50px', height: '50px', borderRadius: '6px', border: 'none', backgroundColor: 'var(--primary-color)', color: 'white', cursor: 'pointer', fontSize: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-small)', userSelect: 'none' }}
                              title="Strafe Left (A)"
                            >
                              ⬅️
                            </button>
                            <button
                              onMouseDown={() => window.dispatchEvent(new KeyboardEvent('keydown', { code: 'KeyS', key: 's', bubbles: true }))}
                              onMouseUp={() => window.dispatchEvent(new KeyboardEvent('keyup', { code: 'KeyS', key: 's', bubbles: true }))}
                              onMouseLeave={() => window.dispatchEvent(new KeyboardEvent('keyup', { code: 'KeyS', key: 's', bubbles: true }))}
                              onTouchStart={() => window.dispatchEvent(new KeyboardEvent('keydown', { code: 'KeyS', key: 's', bubbles: true }))}
                              onTouchEnd={() => window.dispatchEvent(new KeyboardEvent('keyup', { code: 'KeyS', key: 's', bubbles: true }))}
                              style={{ width: '50px', height: '50px', borderRadius: '6px', border: 'none', backgroundColor: 'var(--primary-color)', color: 'white', cursor: 'pointer', fontSize: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-small)', userSelect: 'none' }}
                              title="Backward (S)"
                            >
                              ⬇️
                            </button>
                            <button
                              onMouseDown={() => window.dispatchEvent(new KeyboardEvent('keydown', { code: 'KeyD', key: 'd', bubbles: true }))}
                              onMouseUp={() => window.dispatchEvent(new KeyboardEvent('keyup', { code: 'KeyD', key: 'd', bubbles: true }))}
                              onMouseLeave={() => window.dispatchEvent(new KeyboardEvent('keyup', { code: 'KeyD', key: 'd', bubbles: true }))}
                              onTouchStart={() => window.dispatchEvent(new KeyboardEvent('keydown', { code: 'KeyD', key: 'd', bubbles: true }))}
                              onTouchEnd={() => window.dispatchEvent(new KeyboardEvent('keyup', { code: 'KeyD', key: 'd', bubbles: true }))}
                              style={{ width: '50px', height: '50px', borderRadius: '6px', border: 'none', backgroundColor: 'var(--primary-color)', color: 'white', cursor: 'pointer', fontSize: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-small)', userSelect: 'none' }}
                              title="Strafe Right (D)"
                            >
                              ➡️
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Navigation Controls Toggle Button */}
                      <button
                        onClick={() => {
                          setShow2DNavigationMenu(!show2DNavigationMenu);
                          setShow2DMeasurementMenu(false);
                          setShow2DHelpPanel(false);
                        }}
                        style={{
                          width: '60px',
                          height: '60px',
                          borderRadius: '50%',
                          border: 'none',
                          backgroundColor: show2DNavigationMenu ? 'var(--primary-color-dark)' : 'var(--primary-color)',
                          color: 'white',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '24px',
                          boxShadow: 'var(--shadow-medium)',
                          transition: 'all 0.2s ease',
                          transform: 'scale(1)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.1)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = 'var(--shadow-medium)';
                        }}
                        title="Navigation Controls"
                      >
                        🎮
                      </button>
                    </div>

                    {/* Measurement Tools Menu */}
                    <div style={{ position: 'relative' }}>
                      {/* Measurement Tools Popup */}
                      {show2DMeasurementMenu && (
                        <div style={{
                          position: 'absolute',
                          bottom: '60px',
                          left: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px',
                          backgroundColor: 'transparent',
                          padding: '8px',
                          animation: 'slideUpFadeIn 0.2s ease-out'
                        }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActive2DMeasurementTool(active2DMeasurementTool === 'distance' ? null : 'distance');
                              const event = new CustomEvent('geotiff-toggle-measurement', { detail: { tool: 'distance', active: active2DMeasurementTool !== 'distance' } });
                              window.dispatchEvent(event);
                            }}
                            style={{ width: '40px', height: '40px', borderRadius: '4px', border: 'none', backgroundColor: active2DMeasurementTool === 'distance' ? 'var(--primary-color-dark)' : 'var(--primary-color)', color: 'white', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-small)' }}
                            title="Distance"
                          >
                            📏
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActive2DMeasurementTool(active2DMeasurementTool === 'area' ? null : 'area');
                              const event = new CustomEvent('geotiff-toggle-measurement', { detail: { tool: 'area', active: active2DMeasurementTool !== 'area' } });
                              window.dispatchEvent(event);
                            }}
                            style={{ width: '40px', height: '40px', borderRadius: '4px', border: 'none', backgroundColor: active2DMeasurementTool === 'area' ? 'var(--primary-color-dark)' : 'var(--primary-color)', color: 'white', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-small)' }}
                            title="Area"
                          >
                            ⬜
                          </button>
                          <div style={{ height: '1px', backgroundColor: 'rgba(255, 255, 255, 0.3)', margin: '2px 0' }} />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActive2DMeasurementTool(null);
                              const event = new CustomEvent('geotiff-clear-measurements');
                              window.dispatchEvent(event);
                            }}
                            style={{ width: '40px', height: '40px', borderRadius: '4px', border: 'none', backgroundColor: 'var(--primary-color)', color: 'white', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-small)' }}
                            title="Clear All"
                          >
                            🗑️
                          </button>
                        </div>
                      )}

                      {/* Measurement Tools Toggle Button */}
                      <button
                        onClick={() => {
                          if (show2DMeasurementMenu && active2DMeasurementTool) {
                            setActive2DMeasurementTool(null);
                            const event = new CustomEvent('geotiff-toggle-measurement', { detail: { tool: null, active: false } });
                            window.dispatchEvent(event);
                          }
                          setShow2DMeasurementMenu(!show2DMeasurementMenu);
                          setShow2DNavigationMenu(false);
                          setShow2DHelpPanel(false);
                        }}
                        style={{
                          width: '60px',
                          height: '60px',
                          borderRadius: '50%',
                          border: 'none',
                          backgroundColor: show2DMeasurementMenu ? 'var(--primary-color-dark)' : 'var(--primary-color)',
                          color: 'white',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '24px',
                          boxShadow: 'var(--shadow-medium)',
                          transition: 'all 0.2s ease',
                          transform: 'scale(1)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.1)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = 'var(--shadow-medium)';
                        }}
                        title="Measurement Tools"
                      >
                        📐
                      </button>
                    </div>

                    {/* Help Panel Toggle Button */}
                    <button
                      onClick={() => {
                        setShow2DHelpPanel(!show2DHelpPanel);
                        setShow2DNavigationMenu(false);
                        setShow2DMeasurementMenu(false);
                        if (active2DMeasurementTool) {
                          setActive2DMeasurementTool(null);
                          const event = new CustomEvent('geotiff-toggle-measurement', { detail: { tool: null, active: false } });
                          window.dispatchEvent(event);
                        }
                      }}
                      style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        border: 'none',
                        backgroundColor: show2DHelpPanel ? 'var(--primary-color-dark)' : 'var(--primary-color)',
                        color: 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px',
                        boxShadow: 'var(--shadow-medium)',
                        transition: 'all 0.2s ease',
                        transform: 'scale(1)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.1)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = 'var(--shadow-medium)';
                      }}
                      title="Movement Controls Help"
                    >
                      ❓
                    </button>

                    {/* Clear Space Indicator Button - only show when space indicator is active */}
                    {activeSpaceBoundary && activeSpaceBoundary.length >= 3 && (
                      <button
                        onClick={() => {
                          // Clear 2D space boundary
                          setActiveSpaceBoundary(null);
                          // Also clear 3D viewer space indicator
                          window.dispatchEvent(new CustomEvent('webifcviewer-clear-highlights'));
                        }}
                        style={{
                          width: '60px',
                          height: '60px',
                          borderRadius: '50%',
                          border: 'none',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '24px',
                          boxShadow: 'var(--shadow-medium)',
                          transition: 'all 0.2s ease',
                          transform: 'scale(1)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.1)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = 'var(--shadow-medium)';
                        }}
                        title="Clear Space Indicator"
                      >
                        {/* Cyan cube with red X - EXACTLY like 3D viewer */}
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                          {/* Cube in cyan */}
                          <path d="M16 4L26 10V22L16 28L6 22V10L16 4Z" fill="#00BCD4" stroke="#00838F" strokeWidth="1.5"/>
                          <path d="M16 4L26 10L16 16L6 10L16 4Z" fill="#4DD0E1"/>
                          <path d="M16 16V28L6 22V10L16 16Z" fill="#00ACC1"/>
                          <path d="M16 16V28L26 22V10L16 16Z" fill="#0097A7"/>
                          {/* Red X */}
                          <path d="M10 10L22 22M22 10L10 22" stroke="#FF1744" strokeWidth="3" strokeLinecap="round"/>
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* Active Measurement Indicator */}
                  {active2DMeasurementTool && (
                    <div style={{
                      position: 'absolute',
                      bottom: '16px',
                      ...browserPaneSide(
                        currentLanguage === 'he',
                        // 200px clears the horizontal 2D FAB row that sits at the control inset.
                        isLayerPaneOpen ? `calc(${BROWSER_PANE_CONTROL_INSET} + 200px)` : '216px',
                      ),
                      background: 'rgba(0, 0, 0, 0.85)',
                      color: 'white',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      maxWidth: '350px',
                      zIndex: 100,
                      transition: 'left 0.3s ease, right 0.3s ease',
                      border: '1px solid var(--primary-color)',
                      boxShadow: 'var(--shadow-medium)',
                      animation: 'slideUpFadeIn 0.2s ease-out',
                      pointerEvents: 'auto'
                    }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '13px' }}>
                        {active2DMeasurementTool === 'distance' ? 'Distance Measurement' : 'Area Measurement'}
                      </div>
                      <div style={{ marginBottom: '6px' }}>
                        {active2DMeasurementTool === 'distance' ? (
                          <><strong>Click:</strong> Place start and end points on the map</>
                        ) : (
                          <><strong>Click:</strong> Place polygon vertices, click near first point to close</>
                        )}
                      </div>
                      <div style={{ fontSize: '11px', color: '#aaa', marginTop: '8px', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '8px' }}>
                        {active2DMeasurementTool === 'distance'
                          ? 'Measurements are calculated using geo-coordinates when available.'
                          : 'Area is calculated in m² or hectares using geo-coordinates when available.'}
                      </div>
                    </div>
                  )}

                  {/* Help Panel */}
                  {show2DHelpPanel && (
                    <div style={{
                      position: 'absolute',
                      bottom: '90px',
                      left: currentLanguage === 'he' ? 'auto' : '16px',
                      right: currentLanguage === 'he' ? '16px' : 'auto',
                      background: 'rgba(0, 0, 0, 0.85)',
                      color: 'white',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      maxWidth: '280px',
                      zIndex: 300,
                      border: '1px solid var(--primary-color)',
                      boxShadow: 'var(--shadow-medium)',
                      animation: 'slideUpFadeIn 0.2s ease-out',
                      pointerEvents: 'auto'
                    }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '14px' }}>
                        🗺️ 2D Map Controls
                      </div>

                      <div style={{ marginBottom: '8px' }}>
                        <strong>Pan:</strong> Click and drag, or use <kbd style={{ background: '#444', padding: '2px 6px', borderRadius: '3px', fontSize: '11px' }}>W A S D</kbd> / Arrow keys
                      </div>

                      <div style={{ marginBottom: '8px' }}>
                        <strong>Rotate:</strong> <kbd style={{ background: '#444', padding: '2px 6px', borderRadius: '3px', fontSize: '11px' }}>Q</kbd> / <kbd style={{ background: '#444', padding: '2px 6px', borderRadius: '3px', fontSize: '11px' }}>E</kbd> keys
                      </div>

                      <div style={{ marginBottom: '8px' }}>
                        <strong>Zoom:</strong> Mouse wheel to zoom in/out
                      </div>

                      <div style={{ marginBottom: '8px' }}>
                        <strong>Panorama markers:</strong> Click cyan dots to navigate to panorama view
                      </div>

                      <div style={{ marginBottom: '8px' }}>
                        <strong>Asset markers:</strong> Click colored dots to view asset details
                      </div>

                      <div style={{ fontSize: '11px', color: '#aaa', marginTop: '10px', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '8px' }}>
                        Use the layer panel on the left to toggle visibility of panoramas and assets.
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Minimap GEOTIFF Viewer - Independent instance for panorama mode (not for RVT - RVT has built-in 2D minimap) */}
          {hasRenderedGEOTIFFViewer && activeMenuItem === 'viewer' && currentViewMode === 'Panorama' && selectedAsset?.files?.e57 && !hasRVTFile && (
            <>
              {/* Collapsed state - 2D Map button */}
              {minimapCollapsed && (
                <button
                  onClick={() => setMinimapCollapsed(false)}
                  style={{
                    position: 'fixed',
                    bottom: '20px',
                    right: currentLanguage === 'he' ? 'auto' : '20px',
                    left: currentLanguage === 'he' ? '20px' : 'auto',
                    zIndex: 9999,
                    padding: '12px 20px',
                    background: 'rgba(0, 0, 0, 0.9)',
                    border: '2px solid rgba(72, 202, 228, 0.5)',
                    borderRadius: '8px',
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(72, 202, 228, 0.2)';
                    e.currentTarget.style.borderColor = 'rgba(72, 202, 228, 0.8)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.9)';
                    e.currentTarget.style.borderColor = 'rgba(72, 202, 228, 0.5)';
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <line x1="3" y1="9" x2="21" y2="9"/>
                    <line x1="9" y1="21" x2="9" y2="9"/>
                  </svg>
                  {t('map2D')}
                </button>
              )}
              {/* Expanded state - Full minimap */}
              {!minimapCollapsed && (
                <div
                  id="geotiff-viewer-minimap-container"
                  style={{
                    position: 'fixed',
                    bottom: '20px',
                    right: currentLanguage === 'he' ? 'auto' : '20px',
                    left: currentLanguage === 'he' ? '20px' : 'auto',
                    width: '500px',
                    height: '200px', // Default collapsed height
                    zIndex: 9999,
                    pointerEvents: 'auto',
                    display: 'block',
                    overflow: 'hidden',
                    background: 'rgba(0, 0, 0, 0.9)',
                    border: '2px solid rgba(72, 202, 228, 0.5)',
                    borderRadius: '8px',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
                    transition: 'all 0.3s ease-in-out'
                  }}
                  onMouseEnter={(e) => {
                    // Expand on hover - restore original working 750x400 size
                    e.currentTarget.style.width = '750px'; // Original working expanded width
                    e.currentTarget.style.height = '400px'; // Original working expanded height
                    // Keep inner container size consistent to preserve coordinate system
                    const innerDiv = e.currentTarget.querySelector('div:not([data-close-button])') as HTMLElement;
                    if (innerDiv) {
                      // Maintain 500x600 content for coordinate system consistency
                      // Center content in expanded view: expanded center (375,200) - content center (250,300) = (125,-100)
                      innerDiv.style.top = '-100px'; // Position 500×600 content center vertically for expanded view
                      innerDiv.style.left = '125px'; // Position 500×600 content center horizontally for expanded view
                      innerDiv.style.width = '500px'; // Keep consistent with collapsed state
                      innerDiv.style.height = '600px'; // Keep consistent with collapsed state
                      innerDiv.style.overflow = 'visible'; // Allow rotation overflow, parent clips appropriately
                    }
                  }}
                  onMouseLeave={(e) => {
                    // Return to default - CRITICAL: restore original positioning
                    e.currentTarget.style.width = '500px';
                    e.currentTarget.style.height = '200px';
                    // Restore original collapsed positioning
                    const innerDiv = e.currentTarget.querySelector('div:not([data-close-button])') as HTMLElement;
                    if (innerDiv) {
                      innerDiv.style.top = '-200px'; // Move the 600px container down to center in 200px view
                      innerDiv.style.left = '0'; // Restore original left position
                      innerDiv.style.bottom = 'auto';
                      innerDiv.style.width = '500px'; // Return to collapsed size
                      innerDiv.style.height = '600px'; // Keep height for rotation space
                    }
                  }}
                >
                  {/* Close button */}
                  <button
                    data-close-button="true"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMinimapCollapsed(true);
                    }}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: currentLanguage === 'he' ? 'auto' : '8px',
                      left: currentLanguage === 'he' ? '8px' : 'auto',
                      zIndex: 10000,
                      width: '28px',
                      height: '28px',
                      padding: 0,
                      background: 'rgba(0, 0, 0, 0.7)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: '4px',
                      color: '#fff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 100, 100, 0.8)';
                      e.currentTarget.style.borderColor = 'rgba(255, 100, 100, 1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                    }}
                    title={t('closeMinimap')}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                  <div
                    style={{
                      position: 'absolute',
                      top: '-200px', // Move the 600px content down to center it in the 200px visible area
                      left: '0',
                      width: '500px',
                      height: '600px', // Larger height to accommodate rotated canvas diagonal
                      pointerEvents: 'auto'
                    }}
                  >
                    {selectedAsset && selectedAsset.files.geotiff && (
                      <Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#48CAE4' }}>Loading...</div>}>
                  <ViewerErrorBoundary>
                  <GEOTIFFViewer
                    key={`minimap-${selectedAsset?.id || 'no-asset'}`}
                    asset={selectedAsset}
                    onImageUpdate={() => {}} // Minimap doesn't update main minimap state
                    onPanoramaNavigation={handlePanoramaNavigation}
                          isMinimapMode={true}
                          resetViewOnLoad={true}
                    currentPanoramaState={currentPanoramaState}
                    centerOnCurrentPanorama={true}
                    showPanoramaLayer={showPanoramaLayer}
                    onPanorama3DAnchorChange={setCurrentPanorama3DAnchor}
                    onPOIClick={handlePOINavigation}
                    spaceBoundary={activeSpaceBoundary ? { points: activeSpaceBoundary, color: '#48CAE4' } : null}
                  />
                  </ViewerErrorBoundary>
                      </Suspense>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

	          {/* Minimap GEOTIFF Viewer - Independent instance for 3D mode (not for RVT - RVT has built-in 2D minimap) */}
	          {hasRenderedGEOTIFFViewer && activeMenuItem === 'viewer' && currentViewMode === '3D' && selectedAsset?.files?.geotiff && !hasRVTFile && (
	            <>
              {/* Collapsed state - 2D Viewer button */}
	              {minimapCollapsed && (
	                <button
	                  onClick={() => setMinimapCollapsed(false)}
	                  style={{
	                    position: 'fixed',
	                    bottom: ifcSearchPanelOpen ? `calc(${ifcSearchPanelHeight} + 20px)` : '20px',
	                    right: currentLanguage === 'he' ? 'auto' : '20px',
	                    left: currentLanguage === 'he' ? '20px' : 'auto',
                    zIndex: 9999,
                    padding: '12px 20px',
                    background: 'rgba(0, 0, 0, 0.9)',
                    border: '2px solid rgba(72, 202, 228, 0.5)',
                    borderRadius: '8px',
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(72, 202, 228, 0.2)';
                    e.currentTarget.style.borderColor = 'rgba(72, 202, 228, 0.8)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.9)';
                    e.currentTarget.style.borderColor = 'rgba(72, 202, 228, 0.5)';
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <line x1="3" y1="9" x2="21" y2="9"/>
                    <line x1="9" y1="21" x2="9" y2="9"/>
                  </svg>
                  {t('map2D')}
                </button>
              )}
              {/* Expanded state - Full minimap */}
              {!minimapCollapsed && (
                <div
                  id="geotiff-viewer-3d-minimap-container"
	                  style={{
	                    position: 'fixed',
	                    bottom: ifcSearchPanelOpen ? `calc(${ifcSearchPanelHeight} + 20px)` : '20px',
	                    right: currentLanguage === 'he' ? 'auto' : '20px',
	                    left: currentLanguage === 'he' ? '20px' : 'auto',
                    width: '500px',
                    height: '200px', // Default collapsed height
                    zIndex: 9999,
                    pointerEvents: 'auto',
                    display: 'block',
                    overflow: 'hidden', // Critical: Hide overflow from 750x600px canvas in 500x200px container
                    background: 'rgba(0, 0, 0, 0.9)', // Match panorama minimap background
                    border: '2px solid rgba(72, 202, 228, 0.5)', // Match panorama minimap border
                    borderRadius: '8px',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
                    transition: 'all 0.3s ease-in-out'
                  }}
                  onMouseEnter={(e) => {
                    // Expand on hover - keep 750x400 visual experience
                    e.currentTarget.style.width = '750px'; // Original expanded width
                    e.currentTarget.style.height = '400px'; // Original expanded height
                    // Expand inner container space for full rotation without clipping
                    const innerDiv = e.currentTarget.querySelector('div:not([data-close-button])') as HTMLElement;
                    if (innerDiv) {
                      innerDiv.style.transform = 'scale(1)'; // Remove scaling
                      innerDiv.style.top = '-200px'; // Center 800px content in 400px window
                      innerDiv.style.left = '-25px'; // Center 800px content in 750px window
                      innerDiv.style.width = '800px'; // Large enough for 1443px rotated diagonal
                      innerDiv.style.height = '800px'; // Square prevents any rotation clipping
                    }
                  }}
                  onMouseLeave={(e) => {
                    // Return to minimap mode
                    e.currentTarget.style.width = '500px';
                    e.currentTarget.style.height = '200px';
                    const innerDiv = e.currentTarget.querySelector('div:not([data-close-button])') as HTMLElement;
                    if (innerDiv) {
                      innerDiv.style.transform = 'scale(1)'; // Remove scaling to prevent double-scaling with GEOTIFFViewer's internal scale
                      innerDiv.style.top = '-150px'; // Reset to original collapsed position
                      innerDiv.style.left = '0'; // Reset to original collapsed position
                      innerDiv.style.width = '500px'; // Match container
                      innerDiv.style.height = '500px'; // Provide space for rotation
                    }
                  }}
                >
                  {/* Close button */}
                  <button
                    data-close-button="true"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMinimapCollapsed(true);
                    }}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: currentLanguage === 'he' ? 'auto' : '8px',
                      left: currentLanguage === 'he' ? '8px' : 'auto',
                      zIndex: 10000,
                      width: '28px',
                      height: '28px',
                      padding: 0,
                      background: 'rgba(0, 0, 0, 0.7)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: '4px',
                      color: '#fff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 100, 100, 0.8)';
                      e.currentTarget.style.borderColor = 'rgba(255, 100, 100, 1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                    }}
                    title={t('closeMinimap')}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                  <div
                    style={{
                      position: 'absolute',
                      top: '-150px', // Center the 500px content in 200px visible area
                      left: '0',
                      width: '500px', // Match container size
                      height: '500px', // Provide space for rotated canvas diagonal
                      transform: 'scale(1)', // Remove scaling - let GEOTIFFViewer handle its own scaling
                      transformOrigin: 'center center', // Scale from center
                      pointerEvents: 'auto'
                    }}
                  >
                    {selectedAsset && selectedAsset.files.geotiff && (
                      <Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#48CAE4' }}>Loading...</div>}>
                        <ViewerErrorBoundary>
                        <GEOTIFFViewer
                          key={`minimap-3d-${selectedAsset?.id || 'no-asset'}`}
                          asset={selectedAsset}
                          onImageUpdate={() => {}} // Minimap doesn't update main minimap state
                          onPanoramaNavigation={handlePanoramaNavigation} // Enable panorama navigation from 3D minimap
                          isMinimapMode={true}
                          resetViewOnLoad={true}
                          currentPanoramaState={null}
                          closestPanoramaTarget={closestIfcPanoramaTarget}
                          onClosestPanoramaTargetChange={handleIfcMinimapClosestPanoramaTargetChange}
                          centerOnCurrentPanorama={false}
                          current3DCameraState={current3DCameraState}
                          centerOn3DCamera={true}
                          showPanoramaLayer={showPanoramaLayer}
                          onPOIClick={handlePOINavigation}
                          spaceBoundary={activeSpaceBoundary ? { points: activeSpaceBoundary, color: '#48CAE4' } : null}
                        />
                        </ViewerErrorBoundary>
                      </Suspense>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Persistent DWG Viewer - COMMENTED OUT - Using GEOTIFF instead
          {hasRenderedDWGViewer && (
            <div style={{
              position: 'absolute',
              top: 0,
              width: '100%',
              height: '100%',
              left: 0,
              display: 'block',
              visibility: (activeMenuItem === 'viewer' && showDWGViewer && currentViewMode === '2D') ? 'visible' : 'hidden'
            }}>
              {selectedAsset && selectedAsset.files.dwg && (
                <DWGViewer
                  assetId={selectedAsset.id}
                  fileId={selectedAsset.files.dwg.id}
                  fileName={selectedAsset.files.dwg.fileName}
                />
              )}
            </div>
          )}
          */}
          {activeMenuItem === 'viewer' && viewerBlockState && (
            <div
              data-viewer-loading-block="true"
              role="status"
              aria-live="polite"
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 50000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `${colors.background}E6`,
                backdropFilter: 'blur(3px)',
                pointerEvents: 'auto',
                userSelect: 'none'
              }}
              onPointerDown={(event) => event.stopPropagation()}
              onPointerMove={(event) => event.stopPropagation()}
              onPointerUp={(event) => event.stopPropagation()}
              onWheel={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
              onContextMenu={(event) => event.preventDefault()}
            >
              <div style={{
                width: 'min(360px, calc(100vw - 48px))',
                padding: '24px',
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                background: colors.surface,
                color: colors.textPrimary,
                boxShadow: `0 12px 30px ${colors.shadowStrong}`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
                textAlign: 'center'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  width: '100%',
                  paddingBottom: '4px'
                }}>
                  <img
                    src="https://mabat3d.com/_next/static/media/logo.ed3d26f6.svg"
                    alt="Mabat3D Logo"
                    style={{
                      height: '30px',
                      width: 'auto',
                      filter: colors.textPrimary === '#F8F9FA' ? 'brightness(0) invert(1)' : 'none'
                    }}
                  />
                  <span style={{
                    fontSize: '16px',
                    fontWeight: 700,
                    color: colors.textPrimary,
                    lineHeight: 1
                  }}>
                    Mabat3D
                  </span>
                </div>
                <div style={{
                  width: '42px',
                  height: '42px',
                  border: `3px solid ${colors.border}`,
                  borderTopColor: colors.primary,
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <div style={{
                  fontSize: '15px',
                  fontWeight: 700,
                  color: colors.textPrimary,
                  lineHeight: 1.35
                }}>
                  {viewerBlockState.message}
                </div>
                {viewerBlockState.progress !== null && (
                  <div style={{ width: '100%' }}>
                    <div style={{
                      height: '6px',
                      borderRadius: '999px',
                      background: colors.backgroundTertiary,
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${viewerBlockState.progress}%`,
                        background: colors.primary,
                        transition: 'width 0.2s ease'
                      }} />
                    </div>
                    <div style={{
                      marginTop: '6px',
                      fontSize: '12px',
                      color: colors.textSecondary
                    }}>
                      {Math.round(viewerBlockState.progress)}%
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Main Content Area */}
        <div style={{
          flex: 1,
          position: 'relative',
          background: colors.backgroundTertiary,
          display: 'flex',
          flexDirection: 'column',
          order: 2
        }}>
          {/* Sync task notification banner */}
          {pendingSyncTasks.length > 0 && !syncPipelineRunning && (
            <div style={{
              padding: '10px 16px',
              background: colors.primary + '15',
              borderBottom: `2px solid ${colors.primary}`,
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              zIndex: 100,
              flexShrink: 0
            }}>
              <span style={{ fontSize: '16px' }}>🔄</span>
              <div style={{ flex: 1 }}>
                <strong style={{ color: colors.textPrimary, fontSize: '13px' }}>
                  New version synced from ACC
                </strong>
                <span style={{ color: colors.textSecondary, fontSize: '12px', marginLeft: '8px' }}>
                  {pendingSyncTasks[0].sync_file_name} — {new Date(pendingSyncTasks[0].created_at).toLocaleString()}
                </span>
              </div>
              <button
                onClick={() => handleApplySyncTask(pendingSyncTasks[0].id)}
                style={{
                  padding: '5px 14px',
                  background: colors.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 600
                }}
              >
                Apply
              </button>
              <button
                onClick={() => handleDismissSyncTask(pendingSyncTasks[0].id)}
                style={{
                  padding: '5px 14px',
                  background: 'transparent',
                  color: colors.textSecondary,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Dismiss
              </button>
            </div>
          )}
          {/* Sync pipeline progress banner */}
          {syncPipelineRunning && (
            <div style={{
              padding: '10px 16px',
              background: colors.warning + '15',
              borderBottom: `2px solid ${colors.warning}`,
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              zIndex: 100,
              flexShrink: 0
            }}>
              <span style={{ fontSize: '16px' }}>⏳</span>
              <span style={{ flex: 1, color: colors.textPrimary, fontSize: '13px' }}>
                {syncPipelineStatus}
              </span>
              <button
                onClick={handleRunReassignment}
                style={{
                  padding: '5px 14px',
                  background: colors.success,
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 600
                }}
              >
                Run Reassignment
              </button>
              <button
                onClick={() => { setSyncPipelineRunning(false); setSyncPipelineStatus(''); }}
                style={{
                  padding: '5px 14px',
                  background: 'transparent',
                  color: colors.textSecondary,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Cancel
              </button>
            </div>
          )}
          {activeMenuItem === 'viewer' ? (
            <>
              {/* View Mode Selector - Only show if modes are available */}
              {(() => {
                const availableModes = getAvailableViewModes();
                return availableModes.length > 0 ? (
                  <div
                    className="view-mode-buttons"
                    style={{
                    position: 'absolute',
                    top: '16px',
                    right: currentLanguage === 'he' ? 'auto' : '16px',
                    left: currentLanguage === 'he' ? '16px' : 'auto',
                    display: 'inline-flex',
                    background: 'var(--dt-surface-2)',
                    borderRadius: '10px',
                    boxShadow: 'var(--dt-sh-md)',
                    padding: '3px',
                    border: '1px solid var(--dt-border)',
                    zIndex: 900
                  }}>
                    {['3D', '2D', 'Panorama'].filter(mode => availableModes.includes(mode as '3D' | '2D' | 'Panorama')).map(mode => {
                    const active = currentViewMode === mode;
                    return (
                  <button
                    key={mode}
                    disabled={!!viewerBlockState}
                    onClick={() => {
                      if (viewerBlockState) return;
                      void handleViewModeChange(mode as '3D' | '2D' | 'Panorama');
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '7px 13px',
                      margin: 0,
                      border: 'none',
                      borderRadius: '7px',
                      background: active ? 'var(--dt-surface)' : 'transparent',
                      color: active ? 'var(--dt-text)' : 'var(--dt-text-2)',
                      boxShadow: active ? 'var(--dt-sh-sm)' : 'none',
                      cursor: viewerBlockState ? 'not-allowed' : 'pointer',
                      opacity: viewerBlockState ? 0.55 : 1,
                      transition: 'all 0.15s ease',
                      fontSize: '13px',
                      fontWeight: 600,
                      fontFamily: 'inherit'
                    }}
                  >
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      {mode === '3D' ? (
                        <><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><path d="M3.27 6.96 12 12.01l8.73-5.05M12 22.08V12"/></>
                      ) : mode === '2D' ? (
                        <><path d="M3 6l6-3 6 3 6-3v15l-6 3-6-3-6 3z"/><path d="M9 3v15M15 6v15"/></>
                      ) : (
                        <><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></>
                      )}
                    </svg>
                      {mode === 'Panorama' ? t('panorama') : mode}
                    </button>
                    );
                    })}
                  </div>
                ) : null;
              })()}
              {/* Viewer Content */}
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                background: colors.surface,
                border: 'none',
                overflow: 'hidden',
                position: 'relative'
              }}>

                {/* Loading State */}
                {isLoading ? (
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '18px',
                    background: 'var(--dt-surface-2)',
                    textAlign: 'center',
                    padding: '40px'
                  }}>
                    <div style={{
                      width: '46px',
                      height: '46px',
                      border: '3px solid var(--dt-border)',
                      borderTopColor: 'var(--dt-accent)',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    <div>
                      <h2 style={{ color: 'var(--dt-text)', margin: '0 0 6px', fontSize: '18px', fontWeight: 700 }}>
                        Loading {currentViewMode} Model…
                      </h2>
                      <p style={{ color: 'var(--dt-text-3)', margin: 0, fontSize: '13px' }}>
                        Processing your BIM file, please wait…
                      </p>
                    </div>
                  </div>
                ) : getAvailableViewModes().length === 0 ? (
                  <NoViewerFilesComponent asset={selectedAsset} />
                ) : !usesAutodeskPanoramaFlow &&
                     selectedAsset?.files.e57?.metadata?.extractionSuccess &&
                     selectedAsset.files.e57.metadata.panoramaData &&
                     currentViewMode === 'Panorama' ? (() => {
                    const panoramaData = selectedAsset.files.e57.metadata.panoramaData;
                    const currentPanorama = panoramaData[currentPanoramaIndex];

                    return (
                      <div style={{ width: '100%', height: '100%', position: 'relative', background: '#000' }}>
                        {/* Panorama View Mode Toggle */}
                        <div style={{
                          position: 'absolute',
                          top: '20px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          background: 'rgba(0,0,0,0.8)',
                          padding: '10px',
                          borderRadius: '25px',
                          display: 'flex',
                          gap: '10px',
                          alignItems: 'center',
                          zIndex: 1000
                        }}>
                          <button
                            onClick={() => setPanoramaViewMode('flat')}
                            style={{
                              background: panoramaViewMode === 'flat' ? '#48CAE4' : 'rgba(255,255,255,0.2)',
                              color: 'white',
                              border: 'none',
                              padding: '8px 16px',
                              borderRadius: '15px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: panoramaViewMode === 'flat' ? 'bold' : 'normal'
                            }}
                          >
                            📷 Flat View
                          </button>
                          <button
                            onClick={() => setPanoramaViewMode('cube')}
                            style={{
                              background: panoramaViewMode === 'cube' ? '#48CAE4' : 'rgba(255,255,255,0.2)',
                              color: 'white',
                              border: 'none',
                              padding: '8px 16px',
                              borderRadius: '15px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: panoramaViewMode === 'cube' ? 'bold' : 'normal'
                            }}
                          >
                            🎲 Cube View
                          </button>
                        </div>

                        {/* Main Panorama Display */}
                        {panoramaViewMode === 'flat' ? (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            paddingTop: '80px',
                            paddingBottom: '120px',
                            boxSizing: 'border-box'
                          }}>
                            {currentPanorama?.imageUrl ? (
                              <img
                                key={currentPanoramaIndex}
                                src={currentPanorama.imageUrl}
                                alt={`Panorama ${currentPanoramaIndex + 1}`}
                                style={{
                                  maxWidth: '90%',
                                  maxHeight: '100%',
                                  objectFit: 'contain',
                                  border: '2px solid #48CAE4',
                                  borderRadius: '8px'
                                }}
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div style={{
                                color: '#ccc',
                                textAlign: 'center',
                                padding: '40px'
                              }}>
                                <div style={{ fontSize: '48px', marginBottom: '20px' }}>📷</div>
                                <div>Panorama image not available</div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            paddingTop: '80px',
                            paddingBottom: '120px',
                            boxSizing: 'border-box'
                          }}>
                            <div style={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: colors.backgroundSecondary,
                              color: colors.textPrimary
                            }}>
                              <div style={{ textAlign: 'center', padding: '40px' }}>
                                <div style={{ fontSize: '72px', marginBottom: '20px' }}>🎲</div>
                                <h2 style={{ marginBottom: '15px' }}>Cube Panorama Viewer</h2>
                                <p style={{ color: colors.textSecondary }}>
                                  3D cube panorama view coming soon!<br />
                                  Use flat view to browse panoramas.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Navigation Controls - Only show for flat view */}
                        {panoramaViewMode === 'flat' && (
                          <div style={{
                            position: 'absolute',
                            bottom: '20px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: 'rgba(0,0,0,0.8)',
                            padding: '15px 25px',
                            borderRadius: '25px',
                            display: 'flex',
                            gap: '15px',
                            alignItems: 'center',
                            zIndex: 1000
                          }}>
                            <button
                              onClick={() => setCurrentPanoramaIndex(Math.max(0, currentPanoramaIndex - 1))}
                              disabled={currentPanoramaIndex === 0}
                              style={{
                                background: currentPanoramaIndex === 0 ? 'rgba(255,255,255,0.2)' : '#48CAE4',
                                color: 'white',
                                border: 'none',
                                padding: '10px 15px',
                                borderRadius: '5px',
                                cursor: currentPanoramaIndex === 0 ? 'not-allowed' : 'pointer',
                                fontSize: '14px'
                              }}
                            >
                              ← Previous
                            </button>

                            <span style={{ color: 'white', fontSize: '14px', minWidth: '100px', textAlign: 'center' }}>
                              {currentPanoramaIndex + 1} / {panoramaData.length}
                            </span>

                            <button
                              onClick={() => setCurrentPanoramaIndex(Math.min(panoramaData.length - 1, currentPanoramaIndex + 1))}
                              disabled={currentPanoramaIndex === panoramaData.length - 1}
                              style={{
                                background: currentPanoramaIndex === panoramaData.length - 1 ? 'rgba(255,255,255,0.2)' : '#48CAE4',
                                color: 'white',
                                border: 'none',
                                padding: '10px 15px',
                                borderRadius: '5px',
                                cursor: currentPanoramaIndex === panoramaData.length - 1 ? 'not-allowed' : 'pointer',
                                fontSize: '14px'
                              }}
                            >
                              Next →
                            </button>
                          </div>
                        )}

                        {/* Position Info - Only show for flat view */}
                        {panoramaViewMode === 'flat' && currentPanorama?.position && (
                          <div style={{
                            position: 'absolute',
                            bottom: '20px',
                            right: '20px',
                            background: 'rgba(0,0,0,0.8)',
                            color: 'white',
                            padding: '10px 15px',
                            borderRadius: '5px',
                            fontSize: '12px',
                            zIndex: 1000,
                            maxWidth: '200px'
                          }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Position:</div>
                            <div>X: {currentPanorama.position.x?.toFixed(2) || 'N/A'}</div>
                            <div>Y: {currentPanorama.position.y?.toFixed(2) || 'N/A'}</div>
                            <div>Z: {currentPanorama.position.z?.toFixed(2) || 'N/A'}</div>
                          </div>
                        )}
                      </div>
                    );
                  })() : ((selectedAsset?.files?.dxf || selectedAsset?.files?.dwg) && currentViewMode === '2D') ? (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    background: colors.background,
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}>
                    {/* DWGViewer is already rendered as a persistent component above - removed duplicate */}
                    {selectedAsset.files?.dwg ? (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: colors.textSecondary
                      }}>
                        {/* DWG viewer is rendered in persistent layer */}
                      </div>
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: colors.textSecondary
                      }}>
                        DXF viewing not supported
                      </div>
                    )}
                  </div>
                ) : (show3DViewer && currentViewMode === '3D' && hasRendered3DViewer) ? null : (getCurrentViewFiles().length > 0 || (selectedAsset && selectedAsset.files?.geotiff && currentViewMode === '2D')) && !showGEOTIFFViewer && !show3DViewer && !showWorkingE57Viewer ? (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100vw',
                    height: '100vh',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    background: colors.backgroundColor,
                    zIndex: 1000
                  }}>
                    <img
                      src="https://mabat3d.com/_next/static/media/logo.ed3d26f6.svg"
                      alt="Mabat3D Logo"
                      style={{
                        height: '120px',
                        width: 'auto'
                      }}
                    />
                  </div>
                ) : null}
              </div>

            </>
          ) : activeMenuItem === 'sites' || activeMenuItem === 'onboarding' ? (
            <SitesPage
              onAssetSelected={handleAssetSelected}
              onAssetUpdated={handleAssetUpdated}
              onOpenAutodeskViewer={handleOpenAutodeskViewer}
              onAccLinksChanged={refreshAccLinks}
              onOpenSiteViewer={handleOpenOnboardingSiteViewer}
              translate={t}
              currentLanguage={currentLanguage}
            />
          ) : activeMenuItem === 'assets' ? (
            <AssetsSearchPage
              onViewTickets={handleViewPOITickets}
              onCreateTicket={handleBrowserCreateTicket}
              assetId={selectedAsset?.id}
              onNavigateToViewer={handlePOINavigation}
              loadedIfcFiles={loadedIfcFiles}
              loadingIfcFiles={loadingIfcFiles}
              onRequestLoadModel={handleRequestLoadModel}
              onHighlightGroup={handleHighlightGroup}
              viewerSelectedDbIds={viewerSelectedDbIds}
            />
          ) : activeMenuItem === 'locations' ? (
            <LocationsPage onOpenTickets={openScopeTickets} onCreateTicket={createScopeTicket} assetId={selectedAsset?.id} permissionLevel={selectedAsset?.permission_level} onNavigateToViewer={handlePOINavigation} loadedIfcFiles={loadedIfcFiles} loadingIfcFiles={loadingIfcFiles} focusedLocationId={focusedLocationId} onFocusHandled={() => setFocusedLocationId(null)} onRequestLoadModel={handleRequestLoadModel} onSwitchToViewer={() => enterViewerPage()} onStartCustomRoomDrawing={handleStartCustomRoomDrawing} onStartCustomStoreyDrawing={handleStartCustomStoreyDrawing} onEditCustomRoom={handleEditCustomRoom} onEditCustomStorey={handleEditCustomStorey} onRequestExtractPolygons={async () => {
              if (autodeskViewerRef.current) {
                return await autodeskViewerRef.current.extractAllRoomPolygons();
              }
              return new Map();
            }} />
          ) : activeMenuItem === 'systems' ? (
            <SystemsPage focusedSystemId={ticketSystemFocusId} onSystemFocusHandled={() => setTicketSystemFocusId(null)} onOpenTickets={openScopeTickets} onCreateTicket={createScopeTicket} assetId={selectedAsset?.id} onNavigateToViewer={handlePOINavigation} onHighlightGroup={handleHighlightGroup} loadedIfcFiles={loadedIfcFiles} loadingIfcFiles={loadingIfcFiles} onRequestLoadModel={handleRequestLoadModel} onOpenDiagrams={() => setActiveMenuItem('diagrams')} onSystemsAssigned={enqueueThumbnails} />
          ) : activeMenuItem === 'diagrams' ? (
            <ViewerErrorBoundary kind="page"><Suspense fallback={<div style={{ padding: 40 }}>Loading diagrams…</div>}>
              <DiagramsPage
                assetId={selectedAsset?.id}
                onNavigateToPOI={(poi) => handlePOINavigation(poi as POI)}
                onHighlightGroup={handleHighlightGroup}
                onOpenServiceCall={setServiceCallDraft}
                onRequestThumbnails={enqueueThumbnails}
                thumbnailsVersion={thumbnailsVersion}
                targetDiagramId={diagramTargetId}
                onTargetDiagramHandled={() => setDiagramTargetId(null)}
                focusPOIId={diagramFocusPOIId}
                onFocusPOIHandled={() => setDiagramFocusPOIId(null)}
              />
            </Suspense></ViewerErrorBoundary>
          ) : activeMenuItem === 'tickets' ? (
            <ViewerErrorBoundary kind="page"><Suspense fallback={<div className="dt-body"><div className="dt-statebox">{t('dt_loading')}</div></div>}>
              <ServiceCallsPage
                key={selectedAsset?.id}
                queueState={ticketQueues[selectedAsset?.id || ''] || DEFAULT_TICKET_QUEUE}
                onQueueChange={updateTicketQueue}
                onTicketChanged={() => setTicketRevision(value => value + 1)}
                onOpenScope={scope => {
                  if (scope.scope_type === 'location') {
                    setFocusedLocationId(scope.scope_id);
                    setActiveMenuItem('locations');
                  } else {
                    setTicketSystemFocusId(Number(scope.scope_id));
                    setActiveMenuItem('systems');
                  }
                }}
                assetId={selectedAsset?.id}
                onNavigateToPOI={(poi) => handlePOINavigation(poi as POI)}
                onOpenDiagram={(diagramId, poiId) => {
                  setDiagramTargetId(diagramId);
                  setDiagramFocusPOIId(poiId ? String(poiId) : null);
                  setActiveMenuItem('diagrams');
                }}
              />
            </Suspense></ViewerErrorBoundary>
          ) : activeMenuItem === 'dashboard' ? (
            <ViewerErrorBoundary kind="page"><Suspense fallback={<div style={{ padding: 40 }}>Loading dashboard…</div>}>
              <DashboardPage
                assetId={selectedAsset?.id}
                assetName={selectedAsset?.name}
                onOpenTickets={(callId) => { if (selectedAsset) void openTicket(selectedAsset.id, callId); }}
                onOpenDevices={() => setActiveMenuItem('devices')}
                onOpenDiagrams={() => setActiveMenuItem('diagrams')}
              />
            </Suspense></ViewerErrorBoundary>
          ) : activeMenuItem === 'devices' ? (
            <ViewerErrorBoundary kind="page"><Suspense fallback={<div style={{ padding: 40 }}>Loading devices…</div>}>
              <DevicesPage
                assetId={selectedAsset?.id}
                onNavigateToPOI={(poi) => handlePOINavigation(poi as POI)}
              />
            </Suspense></ViewerErrorBoundary>
          ) : activeMenuItem === 'integrations' ? (
            <ViewerErrorBoundary kind="page"><Suspense fallback={<div style={{ padding: 40 }}>Loading integrations…</div>}>
              <IntegrationsPage assetId={selectedAsset?.id} />
            </Suspense></ViewerErrorBoundary>
          ) : activeMenuItem === 'users' ? (
            <ViewerErrorBoundary kind="page"><Suspense fallback={<div style={{ padding: 24 }}>Loading…</div>}><UsersPage /></Suspense></ViewerErrorBoundary>
          ) : activeMenuItem === 'responsibility' ? (
            <ViewerErrorBoundary kind="page"><Suspense fallback={<div style={{ padding: 40 }}>Loading responsibility…</div>}>
              {/* canManage is a UI affordance only — the server gate is
                  requireSiteAdmin({ siteIdParam: 'assetId' }) in routes/responsibility.js. */}
              <ResponsibilityPage assetId={selectedAsset?.id} canManage={administersSite(selectedAsset?.id || '')} />
            </Suspense></ViewerErrorBoundary>
          ) : activeMenuItem === 'coverage' ? (
            <ViewerErrorBoundary kind="page"><Suspense fallback={<div style={{ padding: 40 }}>Loading coverage…</div>}>
              <CoverageDashboard assetId={selectedAsset?.id} assetName={selectedAsset?.name} />
            </Suspense></ViewerErrorBoundary>
          ) : activeMenuItem === 'tests' ? (
            <ViewerErrorBoundary kind="page"><Suspense fallback={<div style={{ padding: 24 }}>Loading…</div>}><TestRunsPage currentLanguage={currentLanguage} /></Suspense></ViewerErrorBoundary>
          ) : activeMenuItem === 'usage' ? (
            <ViewerErrorBoundary kind="page"><Suspense fallback={<div style={{ padding: 24 }}>Loading…</div>}><UsageDashboard /></Suspense></ViewerErrorBoundary>
          ) : activeMenuItem === 'settings' ? (
            <SimpleSettingsPanel
              isVisible={true}
            />
          ) : activeMenuItem === 'documentation' ? (
            <ViewerErrorBoundary kind="page"><Suspense fallback={<div style={{ padding: 24 }}>Loading…</div>}><DocumentationPage /></Suspense></ViewerErrorBoundary>
          ) : (
            <div style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <h2 style={{ color: colors.textPrimary, marginBottom: '15px' }}>🚧 Under Construction</h2>
                <p style={{ color: colors.textSecondary, marginBottom: '20px' }}>
                  The {activeMenuItem} module is coming soon!
                </p>
                <p style={{ color: colors.textSecondary, marginBottom: '20px' }}>
                  For now, please use the Viewer to explore BIM models.
                </p>
                <button
                  onClick={() => enterViewerPage()}
                  style={{
                    padding: '10px 20px',
                    background: colors.primary,
                    color: colors.textInverse,
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }}
                >
                  Go to Viewer
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Global service-call creation modal (Epic B) — reachable from every origin
          surface (viewer element panel, diagram impact rows, manual page). */}
      {serviceCallDraft && selectedAsset?.id && (
        <ViewerErrorBoundary kind="silent"><Suspense fallback={null}>
          <CreateServiceCallModal
            assetId={selectedAsset.id}
            draft={serviceCallDraft}
            // The dialog calls onCreated and THEN onClose, so the outcome is known by the
            // time it closes: tell whoever opened it (see ServiceCallDraft.onDismissed).
            onClose={() => {
              const created = ticketCreatedRef.current;
              ticketCreatedRef.current = false;
              serviceCallDraft.onDismissed?.({ created });
              setServiceCallDraft(null);
            }}
            onCreated={call => {
              ticketCreatedRef.current = true;
              setTicketRevision(value => value + 1);
              void openTicket(selectedAsset.id, call.id);
            }}
          />
        </Suspense></ViewerErrorBoundary>
      )}

      {/* "Tickets for this asset" — the read side of the same relation, reachable from the
          Browser pane's asset rows and the viewers' Element Info panel. */}
      {poiTicketsTarget && selectedAsset?.id && (
        <ViewerErrorBoundary kind="silent"><Suspense fallback={null}>
          <PoiTicketsModal
            assetId={selectedAsset.id}
            poiId={poiTicketsTarget.id}
            poiTitle={poiTicketsTarget.title}
            onClose={() => setPoiTicketsTarget(null)}
            onOpenTickets={(callId) => {
              updateTicketQueue({ ...DEFAULT_TICKET_QUEUE, poiId: String(poiTicketsTarget.id), poiName: poiTicketsTarget.title, selectedId: callId || null });
              setActiveMenuItem('tickets');
            }}
            onCreateTicket={() => setServiceCallDraft({
              origin: '3d_element',
              title: `${poiTicketsTarget.title} — service call`,
              context: { poiId: String(poiTicketsTarget.id), elementName: poiTicketsTarget.title },
            })}
          />
        </Suspense></ViewerErrorBoundary>
      )}

    </div>
  );
};

export default App;
